import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { api, setToken } from './api';
import { 
  Building2, 
  Users, 
  Target, 
  TrendingUp, 
  CheckCircle, 
  ArrowRight, 
  Mail, 
  Phone,
  Calendar,
  MessageSquare,
  FileText,
  BarChart3,
  Shield,
  Clock,
  Award,
  Briefcase,
  DollarSign,
  Settings,
  LogOut,
  Bell,
  Search,
  Plus,
  Filter,
  Eye,
  Edit,
  Send,
  Paperclip,
  ChevronRight,
  ChevronLeft,
  Globe,
  Database,
  UserPlus,
  Layout,
  Activity,
  Save,
  X,
  ChevronDown,
  ChevronUp,
  Download,
  Upload,
  Trash2,
  Copy,
  ExternalLink,
  CreditCard,
  Archive,
  RefreshCw,
  AlertCircle,
  CheckSquare,
  Square,
  Star,
  MapPin,
  Link,
  Folder,
  Image,
  Video,
  Music,
  File,
  PieChart,
  LineChart,
  TrendingDown,
  Gauge,
  Lock,
  Unlock,
  Key,
  Wifi,
  WifiOff,
  Monitor,
  Smartphone,
  Tablet,
  Menu,
  PlayCircle,
  Bot,
  Brain,
  Lightbulb,
  Zap,
  Cpu,
  Network,
  Reply,
  Check,
  CheckCircle
} from 'lucide-react';

const Beyond26System = () => {
  // Core State Management
  const [activeTab, setActiveTab] = useState('home');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [loginPortalType, setLoginPortalType] = useState('client'); // 'client' or 'employee'
  const [loginForm, setLoginForm] = useState({ 
    email: '', 
    password: '', 
    role: 'client' 
  });
  const [dashboardView, setDashboardView] = useState('overview');
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Contact Form State
  const [contactForm, setContactForm] = useState({ 
    name: '', 
    firm: '', 
    email: '', 
    phone: '', 
    comments: '' 
  });

  // AI Assistant State
  const [aiQuery, setAiQuery] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiHistory, setAiHistory] = useState([]);

  // Interactive Widget States
  const [editingProject, setEditingProject] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editingContract, setEditingContract] = useState(null);
  const [showHistory, setShowHistory] = useState({});
  const [emailClient, setEmailClient] = useState('outlook'); // 'outlook' or 'gmail'

  // Team photo upload state
  const [teamPhotos, setTeamPhotos] = useState({
    edgar: null,
    alex: null
  });
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [expandedService, setExpandedService] = useState(null);

  // Calendar scheduling state
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [selectedAdvisors, setSelectedAdvisors] = useState([]); // Array: can include 'edgar' and/or 'alex'
  const [selectedTimes, setSelectedTimes] = useState([]); // Array to store up to 3 date/time selections
  const [schedulingForm, setSchedulingForm] = useState({
    name: '',
    email: '',
    phone: '',
    notes: ''
  });

  // Contact page view state
  const [contactView, setContactView] = useState('message'); // 'message' or 'schedule'
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  // Refs for section scrolling
  const contactFormRef = React.useRef(null);
  const scheduleFormRef = React.useRef(null);

  // Success message handler
  const handleSuccessMessage = () => {
    setShowSuccessMessage(true);
    setTimeout(() => {
      setShowSuccessMessage(false);
    }, 5000); // Fade out after 5 seconds
  };

  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  
  // File Management States
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Enhanced AI States
  const [aiPrompt, setAiPrompt] = useState(`You are an advanced AI investment advisor for Beyond26 with complete access to all portal data including projects, contracts, messages, calendar events, and tasks. You can:

1. ANALYZE all data in the portals to provide context-aware responses
2. CREATE documents, contracts, and reports based on portal data
3. GENERATE investment recommendations using current project information
4. ACCESS client portfolios, due diligence reports, and performance data
5. PROVIDE strategic advice based on current workflow and deadlines

When creating documents, use the following format:
- For contracts: Include scope, terms, deliverables, and payment structure
- For reports: Include executive summary, analysis, recommendations, and next steps
- For presentations: Include agenda, key points, supporting data, and conclusions

Always reference specific data from the portals when applicable and maintain professional investment industry standards.`);
  const [showAiPromptEditor, setShowAiPromptEditor] = useState(false);
  const [documentCreationMode, setDocumentCreationMode] = useState(false);
  const [documentType, setDocumentType] = useState('contract');
  const [documentInstructions, setDocumentInstructions] = useState('');

  // Advanced Features States
  const [realTimeUpdates, setRealTimeUpdates] = useState(false); // DISABLED to prevent re-renders
  const [darkMode, setDarkMode] = useState(false);
  const [analyticsData, setAnalyticsData] = useState({});
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [marketData, setMarketData] = useState({});
  const [marketDataLoading, setMarketDataLoading] = useState(false);
  const [advancedNotifications, setAdvancedNotifications] = useState([]);
  const [systemHealth, setSystemHealth] = useState({
    api: 'healthy',
    database: 'healthy',
    email: 'healthy',
    ai: 'healthy',
    lastUpdate: new Date().toISOString()
  });

  // Interactive Features
  const [hoverEffects, setHoverEffects] = useState(true);
  const [soundEffects, setSoundEffects] = useState(false);
  const [animations, setAnimations] = useState(true);
  const [autoSave, setAutoSave] = useState(true);
  const [collaborativeMode, setCollaborativeMode] = useState(false);

  // Portfolio Management States
  const [portfolios, setPortfolios] = useState([
    {
      id: 1,
      name: 'ABC Capital Portfolio',
      totalValue: 85000000,
      allocation: {
        privateEquity: 35,
        realEstate: 25,
        publicEquity: 20,
        credit: 15,
        alternatives: 5
      },
      performance: {
        ytd: 8.2,
        oneYear: 12.4,
        threeYear: 9.8,
        fiveYear: 11.2
      },
      riskMetrics: {
        volatility: 12.3,
        sharpe: 1.4,
        maxDrawdown: -8.2,
        beta: 0.85
      },
      lastUpdate: new Date().toISOString()
    }
  ]);

  // CRM States
  const [crmContacts, setCrmContacts] = useState([
    {
      id: 1,
      name: 'John Stevens',
      company: 'ABC Capital Partners',
      role: 'Managing Partner',
      email: 'jstevens@abccapital.com',
      phone: '(212) 555-0100',
      type: 'GP',
      status: 'active',
      lastInteraction: '2025-01-10',
      tags: ['private-equity', 'fund-manager', 'active-client'],
      notes: 'Primary contact for fund marketing engagement'
    },
    {
      id: 2,
      name: 'Sarah Johnson',
      company: 'XYZ Family Office',
      role: 'Chief Investment Officer',
      email: 'sjohnson@xyzfamily.com',
      phone: '(415) 555-0200',
      type: 'LP',
      status: 'active',
      lastInteraction: '2025-01-09',
      tags: ['family-office', 'lp', 'active-client'],
      notes: 'Leading due diligence engagement on PE manager'
    },
    {
      id: 3,
      name: 'Michael Chen',
      company: 'DEF Pension Fund',
      role: 'Investment Director',
      email: 'mchen@defpension.com',
      phone: '(310) 555-0300',
      type: 'LP',
      status: 'active',
      lastInteraction: '2025-01-08',
      tags: ['pension', 'institutional', 'past-client'],
      notes: 'Completed portfolio optimization project'
    }
  ]);
  const [crmInteractions, setCrmInteractions] = useState([]);
  const [crmOpportunities, setCrmOpportunities] = useState([]);
  const [crmActivities, setCrmActivities] = useState([]);

  // Real-time Collaboration States
  const [onlineUsers, setOnlineUsers] = useState(['Edgar Smith', 'Alex Smith']);
  const [recentActivity, setRecentActivity] = useState([]);
  const [systemAlerts, setSystemAlerts] = useState([]);

  // Modal States
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);

  // Data States
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);

  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);

  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);

  const [contracts, setContracts] = useState([]);
  const [contractsLoading, setContractsLoading] = useState(false);

  const [teamMembers, setTeamMembers] = useState([
    { 
      id: 1, 
      name: 'Edgar Smith', 
      email: 'esmith@beyond26advisors.com', 
      role: 'admin', 
      status: 'active', 
      title: 'Founder & CIO',
      expertise: ['Private Equity', 'Venture Capital', 'Strategic Advisory'],
      phone: '(310) 922-4812'
    },
    { 
      id: 2, 
      name: 'Alex Smith', 
      email: 'asmith@beyond26advisors.com', 
      role: 'employee', 
      status: 'active', 
      title: 'Partner',
      expertise: ['Real Estate', 'Infrastructure', 'Due Diligence'],
      phone: '(562) 252-5434'
    },
    { 
      id: 3, 
      name: 'John Stevens', 
      email: 'jstevens@abccapital.com', 
      role: 'client', 
      status: 'active', 
      company: 'ABC Capital Partners',
      type: 'GP'
    },
    { 
      id: 4, 
      name: 'Sarah Wilson', 
      email: 'swilson@xyzfamily.com', 
      role: 'client', 
      status: 'active', 
      company: 'XYZ Family Office',
      type: 'LP'
    }
  ]);

  // Utility Functions - Optimized to reduce re-renders
  const addNotification = useCallback((message, type = 'info') => {
    const notification = {
      id: Date.now(),
      message,
      type,
      timestamp: new Date().toLocaleTimeString()
    };
    setNotifications(prev => [notification, ...prev.slice(0, 2)]); // Reduced to 2 notifications max
    // DISABLED auto-remove to prevent constant re-renders
    // setTimeout(() => {
    //   setNotifications(prev => prev.filter(n => n.id !== notification.id));
    // }, 5000);
  }, []);

  // Authentication Functions
  const handleLogin = async () => {
    if (loginForm.email && loginForm.password) {
      try {
        // Update login form role based on portal type
        const updatedForm = { ...loginForm, role: loginPortalType === 'employee' ? 'employee' : 'client' };
        
        const response = await api.login(updatedForm.email, updatedForm.password);
        
        if (response.token && response.user) {
          // Allow access based on portal type (simplified for demo)
          const userRole = response.user.role;
          
          // Admin can access both portals, others need matching roles
          if (userRole === 'admin') {
            // Admin can access any portal
          } else if (loginPortalType === 'employee' && userRole !== 'employee') {
            addNotification('Access denied. Employee credentials required.', 'error');
            return;
          } else if (loginPortalType === 'client' && userRole !== 'client') {
            addNotification('Access denied. Client credentials required.', 'error');
            return;
          }
          
          setToken(response.token);
          setCurrentUser(response.user);
          setUserRole(response.user.role);
          setIsLoggedIn(true);
          setShowLogin(false);
          setDashboardView('overview');
          
          // Reset form
          setLoginForm({ email: '', password: '', role: loginPortalType === 'employee' ? 'employee' : 'client' });
          
          const portalName = loginPortalType === 'employee' ? 'Employee Portal' : 'Client Portal';
          addNotification(`Welcome to Beyond26 ${portalName}, ${response.user.name || response.user.email}`, 'success');
        } else {
          addNotification('Login failed - invalid response', 'error');
        }
      } catch (error) {
        console.error('Login error:', error);
        addNotification('Login failed - please check your credentials', 'error');
      }
    } else {
      addNotification('Please enter both email and password', 'error');
    }
  };

  // Open specific portal login
  const openPortalLogin = useCallback((portalType) => {
    setLoginPortalType(portalType);
    setLoginForm({ 
      email: '', 
      password: '', 
      role: portalType === 'employee' ? 'employee' : 'client' 
    });
    setShowLogin(true);
  }, []);

  // Optimized input handlers to prevent constant re-renders
  const handleEmailChange = useCallback((e) => {
    const value = e.target.value;
    setLoginForm(prev => ({ ...prev, email: value }));
  }, []);

  const handlePasswordChange = useCallback((e) => {
    const value = e.target.value;
    setLoginForm(prev => ({ ...prev, password: value }));
  }, []);

  // Optimized contact form handlers - simplified to prevent re-renders
  const handleContactNameChange = useCallback((e) => {
    setContactForm(prev => ({ ...prev, name: e.target.value }));
  }, []);

  const handleContactFirmChange = useCallback((e) => {
    setContactForm(prev => ({ ...prev, firm: e.target.value }));
  }, []);

  const handleContactEmailChange = useCallback((e) => {
    setContactForm(prev => ({ ...prev, email: e.target.value }));
  }, []);

  const handleContactPhoneChange = useCallback((e) => {
    setContactForm(prev => ({ ...prev, phone: e.target.value }));
  }, []);

  const handleContactCommentsChange = useCallback((e) => {
    setContactForm(prev => ({ ...prev, comments: e.target.value }));
  }, []);

  const handleLogout = useCallback(() => {
    setToken(null);
    setIsLoggedIn(false);
    setUserRole(null);
    setCurrentUser(null);
    setActiveTab('home');
    setDashboardView('overview');
    addNotification('Successfully logged out', 'success');
  }, []);

  // AI Assistant Functions
  const handleAiQuery = useCallback(async () => {
    if (!aiQuery.trim()) {
      addNotification('Please enter a query', 'warning');
      return;
    }
    
    setAiLoading(true);
    addNotification('Processing your query...', 'info');
    
    try {
      const response = await api.queryAI(aiQuery);
    
      const newEntry = {
        id: Date.now(),
        query: aiQuery,
        response: response.response,
        timestamp: new Date().toISOString()
      };
    
      setAiHistory(prev => [newEntry, ...prev]);
      setAiResponse(response.response);
      setAiQuery('');
      addNotification('AI analysis complete', 'success');
    } catch (error) {
      console.error('AI query error:', error);
      setAiResponse('Sorry, I encountered an error processing your request. Please try again or contact support if the issue persists.');
      addNotification('AI query failed - please try again', 'error');
    } finally {
      setAiLoading(false);
    }
  }, [aiQuery]);

  // Email Integration Functions
  const sendEmailViaOutlook = (to, subject, body) => {
    const outlookUrl = `https://outlook.office.com/mail/deeplink/compose?to=${encodeURIComponent(to)}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(outlookUrl, '_blank');
  };

  const sendEmailViaGmail = (to, subject, body) => {
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(to)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(gmailUrl, '_blank');
  };

  const sendEmail = (to, subject, body) => {
    if (emailClient === 'outlook') {
      sendEmailViaOutlook(to, subject, body);
    } else if (emailClient === 'gmail') {
      sendEmailViaGmail(to, subject, body);
    } else {
      // Fallback to mailto
      const mailtoUrl = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.location.href = mailtoUrl;
    }
  };

  // File Management Functions
  const handleFileUpload = async (files) => {
    setIsUploading(true);
    setUploadProgress(0);
    
    const newFiles = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Simulate upload progress
      for (let progress = 0; progress <= 100; progress += 10) {
        setUploadProgress(progress);
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      const uploadedFile = {
        id: Date.now() + i,
        name: file.name,
        size: file.size,
        type: file.type,
        uploadedBy: currentUser?.name,
        uploadedAt: new Date().toISOString(),
        url: URL.createObjectURL(file), // Create blob URL for download
        tags: [],
        project: null,
        description: ''
      };
      
      newFiles.push(uploadedFile);
    }
    
    setUploadedFiles(prev => [...newFiles, ...prev]);
    localStorage.setItem('beyond26_files', JSON.stringify([...newFiles, ...uploadedFiles]));
    setIsUploading(false);
    setUploadProgress(0);
    addNotification(`${files.length} file(s) uploaded successfully`, 'success');
  };

  const handleFileDownload = (file) => {
    const link = document.createElement('a');
    link.href = file.url;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    addNotification(`${file.name} downloaded`, 'success');
  };

  const deleteFile = (fileId) => {
    setUploadedFiles(prev => {
      const updated = prev.filter(f => f.id !== fileId);
      localStorage.setItem('beyond26_files', JSON.stringify(updated));
      return updated;
    });
    addNotification('File deleted successfully', 'success');
  };

  // Enhanced AI Functions with Portal Data Access
  const getPortalContext = () => {
    return {
      projects: projects,
      messages: messages,
      calendarEvents: calendarEvents,
      contracts: contracts,
      uploadedFiles: uploadedFiles,
      currentUser: currentUser,
      userRole: userRole
    };
  };

  const generateAIDocument = async (type, instructions) => {
    setAiLoading(true);
    
    const context = getPortalContext();
    const contextPrompt = `
    ${aiPrompt}
    
    Current Portal Data Context:
    - Projects: ${JSON.stringify(context.projects, null, 2)}
    - Messages: ${JSON.stringify(context.messages, null, 2)}
    - Calendar Events: ${JSON.stringify(context.calendarEvents, null, 2)}
    - Contracts: ${JSON.stringify(context.contracts, null, 2)}
    - Files: ${JSON.stringify(context.uploadedFiles, null, 2)}
    - Current User: ${JSON.stringify(context.currentUser, null, 2)}
    
    Task: Create a ${type} based on the following instructions: ${instructions}
    
    Please generate a professional ${type} that incorporates relevant data from the portal context above.
    `;

    // Simulate AI processing with enhanced context awareness
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    let generatedDocument = '';
    
    if (type === 'contract') {
      generatedDocument = `PROFESSIONAL SERVICES AGREEMENT

This Professional Services Agreement ("Agreement") is entered into on ${new Date().toLocaleDateString()} between Beyond26 Investment Advisors LLC ("Beyond26") and ${context.projects[0]?.client || 'Client Name'} ("Client").

SCOPE OF SERVICES:
Based on current project data, Beyond26 will provide:
${context.projects.map(p => `- ${p.title}: ${(p as any).description || 'Strategic advisory services'}`).join('\n')}

DELIVERABLES:
${context.projects.flatMap(p => p.tasks || []).map(t => `- ${t.title}`).join('\n')}

TERMS:
- Project Duration: ${(context.projects[0] as any)?.startDate || new Date().toISOString().split('T')[0]} to ${(context.projects[0] as any)?.dueDate || context.projects[0]?.deadline}
- Budget: $${(context.projects[0] as any)?.budget?.toLocaleString() || '75,000'}
- Payment Schedule: 50% upfront, 30% at milestone completion, 20% upon final delivery

ADDITIONAL CONTEXT:
${instructions}

This agreement incorporates all current project requirements and deliverables as outlined in the Beyond26 portal system.`;
    } else if (type === 'report') {
      generatedDocument = `INVESTMENT ANALYSIS REPORT

Executive Summary:
This report provides a comprehensive analysis based on current portfolio data and ongoing projects managed through the Beyond26 portal system.

Current Projects Analysis:
${context.projects.map(p => `
Project: ${p.title}
Client: ${p.client}
Status: ${p.status}
Progress: ${p.progress}%
Budget: $${(p as any).budget?.toLocaleString() || 'N/A'}
`).join('\n')}

Upcoming Obligations:
${context.calendarEvents.map(e => `- ${e.title} on ${e.date} at ${e.time}`).join('\n')}

Recommendations:
Based on the analysis of current data:
1. Continue monitoring project progress for on-time delivery
2. Address any upcoming calendar conflicts
3. Ensure all contractual obligations are met
4. ${instructions}

Next Steps:
- Review all pending tasks and deliverables
- Coordinate upcoming client meetings
- Prepare quarterly performance reports

This report was generated using real-time data from the Beyond26 management portal.`;
    } else if (type === 'presentation') {
      generatedDocument = `BEYOND26 CLIENT PRESENTATION

Agenda:
1. Project Status Update
2. Current Portfolio Performance
3. Strategic Recommendations
4. Next Steps and Timeline

Current Project Status:
${context.projects.map(p => `
• ${p.title} - ${p.progress}% Complete
  Client: ${p.client}
  Status: ${p.status}
  Team: ${p.team?.join(', ') || 'N/A'}
`).join('\n')}

Key Metrics:
- Active Projects: ${context.projects.filter(p => p.status === 'Active').length}
- Completed Projects: ${context.projects.filter(p => p.status === 'Completed').length}
- Total Budget Managed: $${context.projects.reduce((sum, p) => sum + ((p as any).budget || 0), 0).toLocaleString()}

Strategic Focus:
${instructions}

Upcoming Milestones:
${context.calendarEvents.slice(0, 3).map(e => `- ${e.title}: ${e.date}`).join('\n')}

This presentation incorporates live data from our project management system.`;
    }

    // Save generated document as a file
    const documentFile = {
      id: Date.now(),
      name: `${type}_${new Date().toISOString().split('T')[0]}.txt`,
      content: generatedDocument,
      type: 'text/plain',
      size: generatedDocument.length,
      uploadedBy: currentUser?.name,
      uploadedAt: new Date().toISOString(),
      url: `data:text/plain;charset=utf-8,${encodeURIComponent(generatedDocument)}`,
      tags: ['ai-generated', type],
      project: context.projects[0]?.title || null,
      description: `AI-generated ${type} based on portal data`
    };

    setUploadedFiles(prev => {
      const updated = [documentFile, ...prev];
      localStorage.setItem('beyond26_files', JSON.stringify(updated));
      return updated;
    });

    setAiResponse(generatedDocument);
    setAiHistory(prev => {
      const entry = {
        id: Date.now(),
        query: `Generate ${type}: ${instructions}`,
        response: generatedDocument,
        timestamp: new Date().toISOString(),
        type: 'document_generation',
        documentId: documentFile.id
      };
      const updated = [entry, ...prev];
      localStorage.setItem('beyond26_ai_history', JSON.stringify(updated.slice(0, 100)));
      return updated;
    });

    setAiLoading(false);
    setDocumentCreationMode(false);
    setDocumentInstructions('');
    addNotification(`${type.charAt(0).toUpperCase() + type.slice(1)} generated and saved to files`, 'success');
  };

  // Data Persistence Functions
  const saveToStorage = (key, data) => {
    localStorage.setItem(`beyond26_${key}`, JSON.stringify(data));
  };

  const loadFromStorage = (key, defaultValue = []) => {
    try {
      const stored = localStorage.getItem(`beyond26_${key}`);
      return stored ? JSON.parse(stored) : defaultValue;
    } catch (error) {
      console.error('Error loading from storage:', error);
      return defaultValue;
    }
  };

  // Photo upload functionality
  const handlePhotoUpload = async (member, file) => {
    if (!file) return;
    
    setIsUploadingPhoto(true);
    try {
      // Simulate upload process (in production, this would upload to cloud storage)
      const reader = new FileReader();
      reader.onload = (e) => {
        const photoData = {
          url: e.target.result,
          name: file.name,
          uploadedAt: new Date().toISOString()
        };
        
        setTeamPhotos(prev => ({
          ...prev,
          [member]: photoData
        }));
        
        // Save to localStorage
        saveToStorage('teamPhotos', {
          ...teamPhotos,
          [member]: photoData
        });
        
        addNotification(`Photo uploaded successfully for ${member === 'edgar' ? 'Edgar' : 'Alex'}`, 'success');
        setIsUploadingPhoto(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Photo upload error:', error);
      addNotification('Error uploading photo', 'error');
      setIsUploadingPhoto(false);
    }
  };

  const removePhoto = (member) => {
    setTeamPhotos(prev => ({
      ...prev,
      [member]: null
    }));
    
    saveToStorage('teamPhotos', {
      ...teamPhotos,
      [member]: null
    });
    
    addNotification(`Photo removed for ${member === 'edgar' ? 'Edgar' : 'Alex'}`, 'success');
  };

  // Real-time Data Updates
  // Load data from backend when user logs in
  const loadAllDataFromBackend = async () => {
    if (!isLoggedIn) return;

    try {
      addNotification('Loading data from server...', 'info');

      // Load projects with individual error handling
      setProjectsLoading(true);
      try {
        const projectsResponse = await api.getProjects();
        setProjects(projectsResponse.projects || []);
      } catch (e) {
        console.error('Failed to load projects:', e);
        addNotification('Failed to load projects', 'warning');
      } finally {
        setProjectsLoading(false);
      }

      // Load messages with individual error handling
      setMessagesLoading(true);
      try {
        const messagesResponse = await api.getMessages();
        setMessages(messagesResponse.messages || []);
      } catch (e) {
        console.error('Failed to load messages:', e);
        addNotification('Failed to load messages', 'warning');
      } finally {
        setMessagesLoading(false);
      }

      // Load calendar events with individual error handling
      setCalendarLoading(true);
      try {
        const calendarResponse = await api.getCalendar();
        setCalendarEvents(calendarResponse.events || []);
      } catch (e) {
        console.error('Failed to load calendar events:', e);
        addNotification('Failed to load calendar', 'warning');
      } finally {
        setCalendarLoading(false);
      }

      // Load contracts with individual error handling
      setContractsLoading(true);
      try {
        const contractsResponse = await api.getContracts();
        setContracts(contractsResponse.contracts || []);
      } catch (e) {
        console.error('Failed to load contracts:', e);
        addNotification('Failed to load contracts', 'warning');
      } finally {
        setContractsLoading(false);
      }

      // Load tasks with individual error handling
      setTasksLoading(true);
      try {
        const tasksResponse = await api.getTasks();
        setTasks(tasksResponse.tasks || []);
      } catch (e) {
        console.error('Failed to load tasks:', e);
        addNotification('Failed to load tasks', 'warning');
      } finally {
        setTasksLoading(false);
      }

      // Load documents with individual error handling
      setDocumentsLoading(true);
      try {
        const documentsResponse = await api.getDocuments();
        setClientDocuments(documentsResponse.documents || []);
      } catch (e) {
        console.error('Failed to load documents:', e);
        addNotification('Failed to load documents', 'warning');
      } finally {
        setDocumentsLoading(false);
      }

      // Load AI history with individual error handling
      try {
        const aiHistoryResponse = await api.getAIHistory();
        setAiHistory(aiHistoryResponse.history || []);
      } catch (e) {
        console.error('Failed to load AI history:', e);
        addNotification('Failed to load AI history', 'warning');
      }

      addNotification('Data loaded successfully', 'success');

    } catch (error) {
      console.error('Critical error loading data from backend:', error);
      addNotification('Failed to load data from server. Using offline mode.', 'error');
    }
  };

  // Auto-login on page load if token exists
  useEffect(() => {
    const checkAuthOnLoad = async () => {
      const savedToken = localStorage.getItem('b26_token');
      if (savedToken) {
        try {
          setToken(savedToken);
          const response = await api.me();
          if (response.user) {
            setCurrentUser(response.user);
            setUserRole(response.user.role);
            setIsLoggedIn(true);
            addNotification(`Welcome back, ${response.user.name || response.user.email}`, 'success');
            loadAllDataFromBackend();
          }
        } catch (error) {
          console.error('Auto-login failed:', error);
          setToken(null);
          addNotification('Session expired. Please log in again.', 'warning');
        }
      }
    };
    
    checkAuthOnLoad();
  }, []);

  // Keyboard shortcuts for power users
  useEffect(() => {
    const handleKeydown = (e) => {
      // Only handle shortcuts when not in input fields
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case 'p':
            e.preventDefault();
            if (isLoggedIn && (userRole === 'admin' || userRole === 'employee')) {
              setShowProjectModal(true);
              addNotification('New project modal opened (Ctrl+P)', 'info');
            }
            break;
          case 'm':
            e.preventDefault();
            if (isLoggedIn) {
              setShowComposeModal(true);
              addNotification('Compose message modal opened (Ctrl+M)', 'info');
            }
            break;
          case '1':
            e.preventDefault();
            if (isLoggedIn) {
              setDashboardView('overview');
              addNotification('Switched to Overview (Ctrl+1)', 'info');
            }
            break;
          case '2':
            e.preventDefault();
            if (isLoggedIn) {
              setDashboardView('projects');
              addNotification('Switched to Projects (Ctrl+2)', 'info');
            }
            break;
          case '3':
            e.preventDefault();
            if (isLoggedIn) {
              setDashboardView('messages');
              addNotification('Switched to Messages (Ctrl+3)', 'info');
            }
            break;
          case '4':
            e.preventDefault();
            if (isLoggedIn) {
              setDashboardView('ai-assistant');
              addNotification('Switched to AI Assistant (Ctrl+4)', 'info');
            }
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [isLoggedIn, userRole]);

  useEffect(() => {
    if (isLoggedIn) {
      loadAllDataFromBackend();
    }
    
    // Keep some localStorage for non-critical data
    setUploadedFiles(loadFromStorage('files', []));
    setCrmContacts(loadFromStorage('crm_contacts', []));
    setPortfolios(loadFromStorage('portfolios', portfolios));
    const loadedPhotos = loadFromStorage('teamPhotos', {} as any);
    if (loadedPhotos && typeof loadedPhotos === 'object' && !Array.isArray(loadedPhotos)) {
      setTeamPhotos(loadedPhotos as any);
    }

    // DISABLED: Real-time market data updates were causing constant re-renders
    // TODO: Re-implement with proper optimization if needed
    /* 
    if (realTimeUpdates) {
      const interval = setInterval(() => {
        // Market data updates disabled to prevent re-rendering issues
      }, 30000); // Reduced frequency if re-enabled
      return () => clearInterval(interval);
    }
    */
  }, [realTimeUpdates, isLoggedIn]);

  // Backend CRUD Operations
  const createProject = async (projectData) => {
    try {
      const response = await api.createProject(projectData);
      setProjects(prev => [response.project, ...prev]);
      addNotification('Project created successfully', 'success');
      return response.project;
    } catch (error) {
      console.error('Create project error:', error);
      addNotification('Failed to create project', 'error');
      throw error;
    }
  };

  const updateProject = async (id, projectData) => {
    try {
      const response = await api.updateProject(id, projectData);
      setProjects(prev => prev.map(p => p.id === id ? { ...p, ...response.project } : p));
      addNotification('Project updated successfully', 'success');
      return response.project;
    } catch (error) {
      console.error('Update project error:', error);
      addNotification('Failed to update project', 'error');
      throw error;
    }
  };

  const deleteProject = async (id) => {
    try {
      await api.deleteProject(id);
      setProjects(prev => prev.filter(p => p.id !== id));
      addNotification('Project deleted successfully', 'success');
    } catch (error) {
      console.error('Delete project error:', error);
      addNotification('Failed to delete project', 'error');
      throw error;
    }
  };

  const createContract = async (contractData) => {
    try {
      const response = await api.createContract(contractData);
      setContracts(prev => [response.contract, ...prev]);
      addNotification('Contract created successfully', 'success');
      return response.contract;
    } catch (error) {
      console.error('Create contract error:', error);
      addNotification('Failed to create contract', 'error');
      throw error;
    }
  };

  const updateContract = async (id, contractData) => {
    try {
      const response = await api.updateContract(id, contractData);
      setContracts(prev => prev.map(c => c.id === id ? { ...c, ...response.contract } : c));
      addNotification('Contract updated successfully', 'success');
      return response.contract;
    } catch (error) {
      console.error('Update contract error:', error);
      addNotification('Failed to update contract', 'error');
      throw error;
    }
  };

  const deleteContract = async (id) => {
    try {
      await api.deleteContract(id);
      setContracts(prev => prev.filter(c => c.id !== id));
      addNotification('Contract deleted successfully', 'success');
    } catch (error) {
      console.error('Delete contract error:', error);
      addNotification('Failed to delete contract', 'error');
      throw error;
    }
  };

  const sendMessage = async (messageData) => {
    try {
      const response = await api.sendMessage(messageData);
      setMessages(prev => [response.message, ...prev]);
      addNotification('Message sent successfully', 'success');
      return response.message;
    } catch (error) {
      console.error('Send message error:', error);
      addNotification('Failed to send message', 'error');
      throw error;
    }
  };

  const markMessageAsRead = async (id) => {
    try {
      await api.markMessageRead(id);
      setMessages(prev => prev.map(m => m.id === id ? { ...m, read: true } : m));
    } catch (error) {
      console.error('Mark message read error:', error);
      addNotification('Failed to mark message as read', 'error');
    }
  };

  const handleReplyToMessage = (message) => {
    setMessageForm({
      recipient: message.senderEmail || message.senderName,
      subject: message.subject.startsWith('Re:') ? message.subject : `Re: ${message.subject}`,
      body: `\n\n--- Original Message ---\nFrom: ${message.senderName}\nDate: ${message.timestamp}\n${message.body}`
    });
    setShowComposeModal(true);
    markMessageAsRead(message.id);
  };

  const createCalendarEvent = async (eventData) => {
    try {
      const response = await api.createEvent(eventData);
      setCalendarEvents(prev => [response.event, ...prev]);
      addNotification('Calendar event created successfully', 'success');
      return response.event;
    } catch (error) {
      console.error('Create event error:', error);
      addNotification('Failed to create event', 'error');
      throw error;
    }
  };

  // Activity Tracking
  const trackActivity = (action, details) => {
    const activity = {
      id: Date.now(),
      user: currentUser?.name,
      action,
      details,
      timestamp: new Date().toISOString()
    };
    
    setRecentActivity(prev => [activity, ...prev.slice(0, 49)]);
    saveToStorage('recent_activity', recentActivity);
  };

  // Advanced Analytics Functions
  const calculatePortfolioMetrics = (portfolio: any) => {
    const totalValue = portfolio.totalValue;
    const allocation = portfolio.allocation;
    const performance = portfolio.performance;
    
    return {
      diversificationScore: Object.values(allocation).reduce((acc: number, val: any) => acc + (Number(val) * Number(val)), 0),
      riskAdjustedReturn: performance.ytd / portfolio.riskMetrics.volatility,
      efficiencyRatio: performance.ytd / Math.abs(portfolio.riskMetrics.maxDrawdown),
      benchmarkOutperformance: performance.ytd - 7.5 // Assuming 7.5% benchmark
    };
  };

  // AI-Powered Recommendations
  const generateAIRecommendations = () => {
    const recommendations = [];
    
    portfolios.forEach(portfolio => {
      const metrics = calculatePortfolioMetrics(portfolio);
      
      if (Number(metrics.diversificationScore) > 0.3) {
        recommendations.push({
          type: 'portfolio_optimization',
          portfolio: portfolio.name,
          message: 'Consider rebalancing for better diversification',
          priority: 'medium'
        });
      }
      
      if (portfolio.riskMetrics.volatility > 15) {
        recommendations.push({
          type: 'risk_management',
          portfolio: portfolio.name,
          message: 'High volatility detected - review risk exposure',
          priority: 'high'
        });
      }
    });

    return recommendations;
  };

  // Market Analysis Functions
  const getMarketSentiment = () => {
    const vixLevel = marketData.vix.value;
    if (vixLevel < 15) return { sentiment: 'bullish', color: 'green' };
    if (vixLevel < 25) return { sentiment: 'neutral', color: 'yellow' };
    return { sentiment: 'bearish', color: 'red' };
  };

  // Enhanced Notification System
  const addAdvancedNotification = (message, type = 'info', data = {}) => {
    const notification = {
      id: Date.now(),
      message,
      type,
      data,
      timestamp: new Date().toISOString(),
      read: false,
      actions: []
    };
    
    if (type === 'portfolio_alert') {
      notification.actions = ['View Portfolio', 'Dismiss', 'Schedule Review'];
    }
    
    setAdvancedNotifications(prev => [notification, ...prev.slice(0, 99)]);
    
    if (soundEffects && type === 'alert') {
      // Sound notification would go here
      console.log('🔔 Alert sound');
    }
  };

  // Intelligent Workflow Automation
  const automationRules = [
    {
      id: 'project_overdue',
      name: 'Project Overdue Alert',
      trigger: 'daily_check',
      condition: (project: any) => new Date(project.deadline) < new Date() && project.status !== 'Completed',
      action: (project: any) => {
        addAdvancedNotification(
          `Project "${project.title}" is overdue. Deadline was ${project.deadline}`,
          'alert',
          { projectId: project.id, type: 'overdue' }
        );
        trackActivity('automated_alert', `Overdue notification sent for ${project.title}`);
      }
    },
    {
      id: 'portfolio_rebalance',
      name: 'Portfolio Rebalance Suggestion',
      trigger: 'market_volatility',
      condition: (portfolio: any) => portfolio.riskMetrics && portfolio.riskMetrics.volatility > 20,
      action: (portfolio: any) => {
        addAdvancedNotification(
          `Portfolio "${portfolio.name}" shows high volatility. Consider rebalancing.`,
          'portfolio_alert',
          { portfolioId: portfolio.id, type: 'rebalance' }
        );
      }
    },
    {
      id: 'client_follow_up',
      name: 'Client Follow-up Reminder',
      trigger: 'weekly_check',
      condition: (contact: any) => {
        const lastInteraction = new Date(contact.lastInteraction || 0);
        const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        return lastInteraction < weekAgo && contact.status === 'active';
      },
      action: (contact: any) => {
        addAdvancedNotification(
          `Follow up with ${contact.name} - no interaction in over a week`,
          'reminder',
          { contactId: contact.id, type: 'follow_up' }
        );
      }
    }
  ];

  // AI-Powered Workflow Optimization
  const optimizeWorkflow = () => {
    const insights = {
      taskPriority: (tasks || [])
        .filter(t => t.status === 'pending')
        .sort((a, b) => {
          const priorityWeight = { high: 3, medium: 2, low: 1 };
          const dueDateWeight = new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
          return (priorityWeight[b.priority] - priorityWeight[a.priority]) * 1000 + dueDateWeight;
        }),
      
      resourceAllocation: projects
        .filter(p => p.status === 'Active')
        .map(p => ({
          project: p.title,
          teamSize: p.team.length,
          progress: p.progress,
          efficiency: (p.progress && p.team.length) ? p.progress / p.team.length : 0,
          recommendation: (p.progress && p.progress < 50 && p.team.length < 3) ? 'Add resources' : 'On track'
        })),
      
      riskAssessment: portfolios.map(p => {
        const metrics = calculatePortfolioMetrics(p);
        const diversificationScore = Number(metrics.diversificationScore || 0);
        return {
          portfolio: p.name,
          riskScore: diversificationScore,
          recommendation: diversificationScore > 0.3 ? 'Rebalance needed' : 'Well diversified'
        };
      })
    };

    return insights;
  };

  // Smart Calendar Scheduling
  const suggestMeetingTimes = (attendees: string[], duration: number = 60) => {
    const businessHours = { start: 9, end: 17 };
    const suggestions = [];
    const today = new Date();
    
    for (let day = 0; day < 7; day++) {
      const date = new Date(today);
      date.setDate(date.getDate() + day);
      
      for (let hour = businessHours.start; hour <= businessHours.end - duration / 60; hour++) {
        const timeSlot = {
          date: date.toISOString().split('T')[0],
          time: `${hour}:00`,
          available: true,
          confidence: Math.random() * 100
        };
        
        // Check against existing calendar events
        const conflicts = calendarEvents.filter(event => 
          event.date === timeSlot.date && 
          Math.abs(parseInt(event.time.split(':')[0]) - hour) < duration / 60
        );
        
        if (conflicts.length === 0) {
          suggestions.push(timeSlot);
        }
      }
    }
    
    return suggestions.sort((a, b) => b.confidence - a.confidence).slice(0, 5);
  };

  // Automated Report Generation
  const generateAutomatedReports = () => {
    const reports = {
      weekly: {
        title: 'Weekly Performance Summary',
        generated: new Date().toISOString(),
        data: {
          projectsCompleted: projects.filter(p => p.status === 'Completed').length,
          newClients: crmContacts.filter(c => {
            const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
            return new Date(c.createdAt || 0) > weekAgo;
          }).length,
          portfolioPerformance: portfolios.reduce((avg, p) => avg + p.performance.ytd, 0) / portfolios.length,
          marketSentiment: getMarketSentiment().sentiment
        }
      },
      
      monthly: {
        title: 'Monthly Business Review',
        generated: new Date().toISOString(),
        data: {
          revenue: analyticsData.totalAUM * 0.015, // Assuming 1.5% fee
          clientSatisfaction: 94.2, // Simulated score
          teamProductivity: projects.reduce((sum, p) => sum + p.progress, 0) / projects.length,
          marketOpportunities: generateAIRecommendations().length
        }
      }
    };

    return reports;
  };

  // Predictive Analytics
  const predictiveAnalytics = {
    clientChurn: (client: any) => {
      const riskFactors = [
        client.lastInteraction && new Date(client.lastInteraction) < new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
        client.portfolioPerformance < 0,
        client.communicationFrequency < 2
      ].filter(Boolean).length;
      
      return { risk: riskFactors * 33.3, recommendation: riskFactors > 1 ? 'Schedule immediate call' : 'Monitor closely' };
    },
    
    marketTrends: () => {
      const trend = {
        direction: (marketData.sp500.change && marketData.sp500.change > 0) ? 'bullish' : 'bearish',
        strength: (marketData.sp500.change && Math.abs(marketData.sp500.change) > 1) ? 'strong' : 'weak',
        volatility: (marketData.vix.value && marketData.vix.value > 20) ? 'high' : 'low',
        recommendation: (marketData.vix.value && marketData.vix.value > 20) ? 'Defensive positioning' : 'Growth opportunities'
      };
      
      return trend;
    },
    
    portfolioOptimization: (portfolio: any) => {
      const metrics = calculatePortfolioMetrics(portfolio);
      return {
        currentScore: 100 - (Number(metrics.diversificationScore || 0) * 100),
        potentialImprovement: (Number(metrics.diversificationScore || 0) > 0.3) ? 15 : 5,
        suggestedActions: [
          'Reduce concentration risk',
          'Increase alternative allocations',
          'Review correlation matrix'
        ]
      };
    }
  };

  // Automated Task Prioritization
  const prioritizeTasks = () => {
    return (tasks || [])
      .filter(t => t.status === 'pending')
      .map(task => ({
        ...task,
        aiScore: calculateTaskPriority(task),
        urgency: getTaskUrgency(task),
        impact: getTaskImpact(task)
      }))
      .sort((a, b) => b.aiScore - a.aiScore);
  };

  const calculateTaskPriority = (task: any) => {
    let score = 0;
    
    // Priority weight
    const priorityWeights = { high: 40, medium: 25, low: 10 };
    score += priorityWeights[task.priority] || 10;
    
    // Due date urgency
    const daysUntilDue = task.dueDate ? Math.ceil((new Date(task.dueDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : 30;
          score += Math.max(0, 30 - daysUntilDue);
    
    // Project association
    const associatedProject = projects.find(p => p.title.includes(task.project || ''));
    if (associatedProject && typeof associatedProject.progress === 'number') {
      score += associatedProject.progress < 50 ? 20 : 10;
    }
    
    return score;
  };

  const getTaskUrgency = (task: any) => {
    const daysUntilDue = task.dueDate ? Math.ceil((new Date(task.dueDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : 30;
    if (daysUntilDue <= 1) return 'critical';
    if (daysUntilDue <= 3) return 'high';
    if (daysUntilDue <= 7) return 'medium';
    return 'low';
  };

  const getTaskImpact = (task: any) => {
    const impactKeywords = ['client', 'revenue', 'critical', 'urgent', 'deadline'];
    const matchCount = impactKeywords.filter(keyword => 
      task.title.toLowerCase().includes(keyword) || 
      task.description.toLowerCase().includes(keyword)
    ).length;
    
    if (matchCount >= 3) return 'high';
    if (matchCount >= 2) return 'medium';
    return 'low';
  };

  // Enhanced Contact Form Functions
  const handleContactSubmit = async () => {
    if (contactForm.name && contactForm.email && contactForm.comments) {
      try {
        // Submit to backend API
        await api.submitContact(contactForm);
        
        // Also send email
      const subject = `New Inquiry from ${contactForm.name} - ${contactForm.firm || 'Individual'}`;
      const body = `Name: ${contactForm.name}\nFirm: ${contactForm.firm}\nPhone: ${contactForm.phone}\nEmail: ${contactForm.email}\n\nComments:\n${contactForm.comments}\n\n---\nSent via Beyond26 Contact Form\nTime: ${new Date().toLocaleString()}`;
      const recipients = 'esmith@beyond26advisors.com,asmith@beyond26advisors.com';
      sendEmail(recipients, subject, body);
      
      setContactForm({ name: '', firm: '', email: '', phone: '', comments: '' });
        addNotification('Message sent successfully! We\'ll be in touch soon.', 'success');
      } catch (error) {
        console.error('Contact submission error:', error);
        addNotification('Failed to send message. Please try again or contact us directly.', 'error');
      }
    } else {
      addNotification('Please fill in all required fields (Name, Email, Comments)', 'error');
    }
  };

  // Navigation Component
  const Navigation = () => (
    <nav className="bg-gradient-to-b from-white via-white to-gray-50 backdrop-blur-sm shadow-2xl border-b border-gray-300 sticky top-0 z-50">
      <div className="max-w-[95%] mx-auto px-4 sm:px-8">
        <div className="flex justify-between items-center py-4 sm:py-6">
          <div className="flex items-center">
            <h1
              className="text-5xl sm:text-6xl md:text-7xl font-display font-bold text-beyond-black cursor-pointer hover:text-gray-700 transition-all duration-500 tracking-[0.15em] hover:scale-[1.02]"
              onClick={() => { setActiveTab('home'); setMobileMenuOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              style={{ fontFamily: 'Georgia, serif', textShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
            >
              Beyond<span className="italic font-thin tracking-wider">26</span>
            </h1>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-12">
            <button
              onClick={() => { setActiveTab('home'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              className={`font-light text-lg tracking-[0.12em] transition-all duration-500 px-8 py-2.5 ${
                activeTab === 'home' ? 'text-beyond-black bg-gradient-to-r from-gray-200 to-gray-100 shadow-inner' : 'text-gray-600 hover:text-beyond-black hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-50'
              }`}
              style={{ fontFamily: 'Georgia, serif' }}
            >
              Home
            </button>
            <button
              onClick={() => { setActiveTab('about'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              className={`font-light text-lg tracking-[0.12em] transition-all duration-500 px-8 py-2.5 ${
                activeTab === 'about' ? 'text-beyond-black bg-gradient-to-r from-gray-200 to-gray-100 shadow-inner' : 'text-gray-600 hover:text-beyond-black hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-50'
              }`}
              style={{ fontFamily: 'Georgia, serif' }}
            >
              About
            </button>
            <button
              onClick={() => { setActiveTab('contact'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              className={`font-light text-lg tracking-[0.12em] transition-all duration-500 px-8 py-2.5 ${
                activeTab === 'contact' ? 'text-beyond-black bg-gradient-to-r from-gray-200 to-gray-100 shadow-inner' : 'text-gray-600 hover:text-beyond-black hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-50'
              }`}
              style={{ fontFamily: 'Georgia, serif' }}
            >
              Contact
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-gray-600 hover:text-beyond-black transition-colors"
            aria-label="Toggle menu"
          >
            <Menu size={28} />
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200">
            <div className="flex flex-col space-y-2">
              <button
                onClick={() => { setActiveTab('home'); setMobileMenuOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className={`font-light text-lg tracking-[0.12em] transition-all duration-300 px-4 py-3 text-left ${
                  activeTab === 'home' ? 'text-beyond-black bg-gradient-to-r from-gray-200 to-gray-100' : 'text-gray-600 hover:text-beyond-black hover:bg-gray-50'
                }`}
                style={{ fontFamily: 'Georgia, serif' }}
              >
                Home
              </button>
              <button
                onClick={() => { setActiveTab('about'); setMobileMenuOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className={`font-light text-lg tracking-[0.12em] transition-all duration-300 px-4 py-3 text-left ${
                  activeTab === 'about' ? 'text-beyond-black bg-gradient-to-r from-gray-200 to-gray-100' : 'text-gray-600 hover:text-beyond-black hover:bg-gray-50'
                }`}
                style={{ fontFamily: 'Georgia, serif' }}
              >
                About
              </button>
              <button
                onClick={() => { setActiveTab('contact'); setMobileMenuOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className={`font-light text-lg tracking-[0.12em] transition-all duration-300 px-4 py-3 text-left ${
                  activeTab === 'contact' ? 'text-beyond-black bg-gradient-to-r from-gray-200 to-gray-100' : 'text-gray-600 hover:text-beyond-black hover:bg-gray-50'
                }`}
                style={{ fontFamily: 'Georgia, serif' }}
              >
                Contact
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );

  // Homepage Component
  const HomePage = React.memo(() => (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Full Width */}
      <section className="pt-32 pb-40 bg-gradient-to-b from-black via-gray-900 to-black text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-gray-800/20 via-black to-black"></div>
          <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-40 h-40 bg-white/[0.03] rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-20 right-10 w-64 h-64 bg-white/[0.02] rounded-full blur-3xl animate-float delay-1000"></div>
          <div className="absolute top-1/2 left-1/3 w-32 h-32 bg-white/[0.015] rounded-full blur-2xl animate-float delay-500"></div>
          </div>
        <div className="max-w-[90%] mx-auto px-4 sm:px-8 relative z-10">
          <div className="text-center py-24">
            <h1 className="text-4xl sm:text-6xl md:text-8xl lg:text-9xl font-display font-bold mb-8 animate-fade-in tracking-[0.03em] sm:tracking-[0.1em] md:tracking-[0.2em]" style={{ fontFamily: 'Georgia, serif', textShadow: '0 4px 20px rgba(255,255,255,0.1)' }}>
              Beyond<span className="italic font-thin">26</span>
              </h1>
            <div className="w-32 h-[2px] bg-gradient-to-r from-transparent via-gray-400 to-transparent mx-auto mb-8 animate-fade-in delay-200 shadow-lg"></div>
            <p className="text-3xl md:text-4xl font-light mb-10 max-w-5xl mx-auto animate-slide-up delay-400 tracking-[0.15em]" style={{ fontFamily: 'Georgia, serif' }}>
                Strategic Investment Advisory Firm
              </p>
            <p className="text-xl font-light mb-16 max-w-4xl mx-auto text-gray-300 animate-slide-up delay-600 tracking-[0.08em]" style={{ fontFamily: 'Georgia, serif' }}>
              Institutional expertise. Bespoke strategies. Superior outcomes.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center animate-slide-up delay-800 max-w-2xl mx-auto">
                <button
                  onClick={() => { setActiveTab('about'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="group bg-gradient-to-br from-gray-100 via-white to-gray-200 text-beyond-black px-12 py-4 font-medium text-lg tracking-[0.1em] transition-all duration-500 transform hover:scale-[1.05] shadow-[0_4px_20px_rgba(255,255,255,0.3)] hover:shadow-[0_8px_30px_rgba(255,255,255,0.5)] border border-gray-300"
                style={{ fontFamily: 'Georgia, serif' }}
                >
                  <span className="group-hover:tracking-[0.15em] transition-all duration-500">Learn More</span>
                </button>
                <button
                  onClick={() => { setActiveTab('contact'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  className="group border-2 border-white/50 text-white px-12 py-4 font-medium text-lg tracking-[0.1em] transition-all duration-500 transform hover:scale-[1.05] hover:bg-white/10 hover:border-white shadow-lg"
                  style={{ fontFamily: 'Georgia, serif' }}
                >
                  <span className="group-hover:tracking-[0.15em] transition-all duration-500">Get in Touch</span>
                </button>
              </div>
            </div>
          </div>
        </section>

      {/* Service Categories - Enhanced Full Width Design */}
      <section className="py-32 bg-gradient-to-b from-gray-50 via-white to-gray-100 relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-white via-transparent to-gray-100/30"></div>
        <div className="max-w-[88%] mx-auto px-8 relative z-10">
          <div className="text-center mb-20">
            <h2 className="text-6xl md:text-7xl font-display font-bold mb-8 text-beyond-black tracking-[0.12em]" style={{ fontFamily: 'Georgia, serif', textShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
              Our <span className="italic font-thin">Expertise</span>
                </h2>
            <div className="w-32 h-[2px] bg-gradient-to-r from-transparent via-black to-transparent mx-auto mb-10 shadow-sm"></div>
            <p className="text-2xl md:text-3xl text-gray-600 max-w-5xl mx-auto font-light tracking-[0.08em]" style={{ fontFamily: 'Georgia, serif' }}>
              Comprehensive advisory services across all asset classes
                </p>
              </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-16 lg:gap-20">
            {/* Private Markets */}
            <div className="text-center group animate-fade-in hover:transform hover:scale-[1.06] transition-all duration-700">
              <div className="w-40 h-40 bg-gradient-to-br from-black via-gray-900 to-gray-800 flex items-center justify-center mx-auto mb-12 group-hover:shadow-[0_8px_30px_rgba(0,0,0,0.4)] transition-all duration-700 relative overflow-hidden border border-gray-700">
                <div className="absolute inset-0 bg-gradient-to-br from-white/[0.08] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                <div className="absolute top-3 right-3 w-12 h-12 bg-white/[0.04] rounded-full blur-2xl animate-pulse"></div>
                <div className="absolute bottom-3 left-3 w-10 h-10 bg-white/[0.03] rounded-full blur-xl animate-pulse delay-300"></div>
                <FileText className="text-white relative z-10" size={56} />
              </div>
              <h3 className="text-4xl font-display font-bold mb-8 text-beyond-black group-hover:text-gray-800 transition-colors duration-500 tracking-[0.08em]" style={{ fontFamily: 'Georgia, serif', textShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
                Private Markets
                  </h3>
              <p className="text-gray-600 font-light leading-relaxed text-xl tracking-wide" style={{ fontFamily: 'Georgia, serif' }}>
                Buyout, Venture Capital, Growth Equity, Private Credit, Crypto, and Special Situations
              </p>
                    </div>

            {/* Real Assets */}
            <div className="text-center group animate-fade-in hover:transform hover:scale-[1.06] transition-all duration-700">
              <div className="w-40 h-40 bg-gradient-to-br from-black via-gray-900 to-gray-800 flex items-center justify-center mx-auto mb-12 group-hover:shadow-[0_8px_30px_rgba(0,0,0,0.4)] transition-all duration-700 relative overflow-hidden border border-gray-700">
                <div className="absolute inset-0 bg-gradient-to-br from-white/[0.08] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                <div className="absolute top-3 right-3 w-12 h-12 bg-white/[0.04] rounded-full blur-2xl animate-pulse delay-500"></div>
                <div className="absolute bottom-3 left-3 w-10 h-10 bg-white/[0.03] rounded-full blur-xl animate-pulse delay-700"></div>
                <TrendingUp className="text-white relative z-10" size={56} />
                    </div>
              <h3 className="text-4xl font-display font-bold mb-8 text-beyond-black group-hover:text-gray-800 transition-colors duration-500 tracking-[0.08em]" style={{ fontFamily: 'Georgia, serif', textShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
                Real Assets
              </h3>
              <p className="text-gray-600 font-light leading-relaxed text-xl tracking-wide" style={{ fontFamily: 'Georgia, serif' }}>
                Real Estate, Infrastructure, Natural Resources, and Uncorrelated Strategies
              </p>
                    </div>

            {/* Public Markets */}
            <div className="text-center group animate-fade-in hover:transform hover:scale-[1.06] transition-all duration-700">
              <div className="w-40 h-40 bg-gradient-to-br from-black via-gray-900 to-gray-800 flex items-center justify-center mx-auto mb-12 group-hover:shadow-[0_8px_30px_rgba(0,0,0,0.4)] transition-all duration-700 relative overflow-hidden border border-gray-700">
                <div className="absolute inset-0 bg-gradient-to-br from-white/[0.08] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                <div className="absolute top-3 right-3 w-12 h-12 bg-white/[0.04] rounded-full blur-2xl animate-pulse delay-1000"></div>
                <div className="absolute bottom-3 left-3 w-10 h-10 bg-white/[0.03] rounded-full blur-xl animate-pulse delay-200"></div>
                <BarChart3 className="text-white relative z-10" size={56} />
                    </div>
              <h3 className="text-4xl font-display font-bold mb-8 text-beyond-black group-hover:text-gray-800 transition-colors duration-500 tracking-[0.08em]" style={{ fontFamily: 'Georgia, serif', textShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
                Public Markets
              </h3>
              <p className="text-gray-600 font-light leading-relaxed text-xl tracking-wide" style={{ fontFamily: 'Georgia, serif' }}>
                Global Equities, Fixed Income, Hedge Funds, Crypto, and Liquid Alternatives
              </p>
                </div>
                    </div>
                    </div>
              </section>

        {/* Smooth Transition Section */}
        <section className="h-32 bg-gradient-fade-light"></section>

            {/* GP Services Section - Interactive and Expandable */}
      <section className="py-32 bg-gradient-to-b from-gray-100 via-gray-50 to-white">
        <div className="max-w-[88%] mx-auto px-8">
          <div className="text-center mb-24">
            <h2 className="text-6xl md:text-7xl font-display font-bold mb-8 text-beyond-black tracking-[0.12em]" style={{ fontFamily: 'Georgia, serif', textShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
              For <em className="font-display italic font-thin">General Partners</em>
            </h2>
            <div className="w-32 h-[2px] bg-gradient-to-r from-transparent via-black to-transparent mx-auto mb-10 shadow-sm"></div>
            <p className="text-2xl md:text-3xl text-gray-600 max-w-6xl mx-auto font-light leading-relaxed tracking-[0.06em]" style={{ fontFamily: 'Georgia, serif' }}>
              Strategic review of GP marketing strategies from the lens of experienced institutional allocators.
            </p>
            <button
              onClick={() => setExpandedService(expandedService === 'gp-main' ? null : 'gp-main')}
              className="mt-12 inline-flex items-center px-12 py-4 bg-gradient-to-br from-black via-gray-900 to-gray-800 text-white font-medium text-lg tracking-[0.08em] hover:from-gray-900 hover:via-black hover:to-gray-900 transition-all duration-500 shadow-[0_4px_16px_rgba(0,0,0,0.3)] hover:shadow-[0_6px_24px_rgba(0,0,0,0.5)] border border-gray-700 transform hover:scale-[1.02]"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              {expandedService === 'gp-main' ? 'Show Less' : 'View All Services'}
              <ChevronDown className={`ml-3 w-6 h-6 transition-transform duration-500 ${
                expandedService === 'gp-main' ? 'rotate-180' : ''
              }`} />
            </button>
                    </div>

          {/* Uniform Grid Layout - All 5 Services */}
          {expandedService === 'gp-main' && (
            <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-10 animate-fade-in transition-all duration-700">
            {[
              {
                icon: TrendingUp,
                title: 'Fundraising Strategy',
                desc: 'Investor targeting and capital formation strategies',
                items: ['Investor Targeting', 'Process Design', 'Pitch Materials', 'Diligence Support', 'Mock Sessions']
              },
              {
                icon: MessageSquare,
                title: 'Marketing & Communications',
                desc: 'Fund positioning and investor communications',
                items: ['Marketing Materials', 'Brand Strategy', 'Investor Updates', 'Digital Strategy', 'Event Planning']
              },
              {
                icon: Shield,
                title: 'Operational Support',
                desc: 'Infrastructure and process optimization',
                items: ['Process Review', 'Compliance Support', 'Technology Advisory', 'Best Practices']
              },
              {
                icon: BarChart3,
                title: 'Reporting & Analytics',
                desc: 'Performance reporting and data visualization',
                items: ['Performance Reports', 'Dashboard Design', 'Investor Materials', 'Analytics Support']
              },
              {
                icon: Users,
                title: 'Strategic Advisory',
                desc: 'Strategic guidance and market intelligence',
                items: ['Strategy Consulting', 'Market Intelligence', 'Team Advisory', 'Network Access']
              }
            ].map((service, index) => (
              <div key={index} className="bg-white border border-gray-200 shadow-lg hover:shadow-2xl p-10 group transition-all duration-700 flex flex-col h-full min-h-[420px] hover:scale-[1.02]">
                <div className="flex flex-col items-center mb-8 text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-black via-gray-900 to-gray-800 flex items-center justify-center mb-6 group-hover:scale-110 transition-all duration-700 relative overflow-hidden border border-gray-700">
                    <div className="absolute inset-0 bg-gradient-to-br from-white/[0.06] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                    <service.icon className="text-white relative z-10" size={32} />
                  </div>
                  <h3 className="text-2xl font-bold text-beyond-black mb-4 tracking-wide" style={{ fontFamily: 'Georgia, serif' }}>{service.title}</h3>
                </div>
                <p className="text-gray-600 font-light mb-8 leading-relaxed text-center text-lg" style={{ fontFamily: 'Georgia, serif' }}>{service.desc}</p>
                <div className="space-y-4 flex-1 flex flex-col items-center">
                  {service.items.map((item, idx) => (
                    <div key={idx} className="flex items-center">
                      <CheckCircle className="text-green-600 mr-3 flex-shrink-0" size={18} />
                      <span className="text-gray-700 font-light text-base" style={{ fontFamily: 'Georgia, serif' }}>{item}</span>
              </div>
                  ))}
            </div>
              </div>
            ))}
            </div>
          )}
          </div>
        </section>

        {/* Smooth Transition Section */}
        <section className="h-32 bg-gradient-fade-dark"></section>

            {/* For Limited Partners - Interactive and Expandable */}
        <section className="py-32 bg-gradient-to-b from-white via-gray-50 to-gray-100">
        <div className="max-w-[88%] mx-auto px-8">
          <div className="text-center mb-24">
            <h2 className="text-6xl md:text-7xl font-display font-bold mb-8 text-beyond-black tracking-[0.12em]" style={{ fontFamily: 'Georgia, serif', textShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
              For <em className="font-display italic font-thin">Limited Partners</em>
              </h2>
            <div className="w-32 h-[2px] bg-gradient-to-r from-transparent via-black to-transparent mx-auto mb-10 shadow-sm"></div>
            <p className="text-2xl md:text-3xl text-gray-600 max-w-6xl mx-auto font-light leading-relaxed tracking-[0.06em]" style={{ fontFamily: 'Georgia, serif' }}>
              Strategic advisory services for institutional investors
            </p>
            <button
              onClick={() => setExpandedService(expandedService === 'lp-main' ? null : 'lp-main')}
              className="mt-12 inline-flex items-center px-12 py-4 bg-gradient-to-br from-black via-gray-900 to-gray-800 text-white font-medium text-lg tracking-[0.08em] hover:from-gray-900 hover:via-black hover:to-gray-900 transition-all duration-500 shadow-[0_4px_16px_rgba(0,0,0,0.3)] hover:shadow-[0_6px_24px_rgba(0,0,0,0.5)] border border-gray-700 transform hover:scale-[1.02]"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              {expandedService === 'lp-main' ? 'Show Less' : 'View All Services'}
              <ChevronDown className={`ml-3 w-6 h-6 transition-transform duration-500 ${
                expandedService === 'lp-main' ? 'rotate-180' : ''
              }`} />
            </button>
          </div>

          {expandedService === 'lp-main' && (
            <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-10 animate-fade-in transition-all duration-700">
            {[
              {
                icon: Target,
                title: 'Asset Allocation',
                desc: 'Portfolio construction and strategic allocation',
                items: ['Asset Allocation', 'Portfolio Construction', 'Rebalancing', 'Risk Profile Analysis', 'Performance Tracking']
              },
              {
                icon: Search,
                title: 'Manager Research',
                desc: 'Due diligence and manager selection',
                items: ['Manager Due Diligence', 'Performance Analysis', 'Operational Review', 'Co-Investment Opportunities', 'Network Intelligence']
              },
              {
                icon: Shield,
                title: 'Risk Management',
                desc: 'Risk assessment and portfolio protection',
                items: ['Risk Assessment', 'Stress Testing', 'Scenario Analysis', 'Risk Budgeting', 'Portfolio Protection']
              },
              {
                icon: Award,
                title: 'Performance Analysis',
                desc: 'Attribution and benchmark analysis',
                items: ['Performance Attribution', 'Benchmark Analysis', 'Fee Analysis', 'Return Analytics', 'Peer Review']
              },
              {
                icon: Globe,
                title: 'Market Intelligence',
                desc: 'Research and competitive insights',
                items: ['Market Research', 'Trends Analysis', 'Economic Insights', 'Sector Research', 'Strategic Intelligence']
              }
            ].map((service, index) => (
              <div key={index} className="bg-white border border-gray-200 shadow-lg hover:shadow-2xl p-10 group transition-all duration-700 flex flex-col h-full min-h-[420px] hover:scale-[1.02]">
                <div className="flex flex-col items-center mb-8 text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-black via-gray-900 to-gray-800 flex items-center justify-center mb-6 group-hover:scale-110 transition-all duration-700 relative overflow-hidden border border-gray-700">
                    <div className="absolute inset-0 bg-gradient-to-br from-white/[0.06] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                    <service.icon className="text-white relative z-10" size={32} />
                  </div>
                  <h3 className="text-2xl font-bold text-beyond-black mb-4 tracking-wide" style={{ fontFamily: 'Georgia, serif' }}>{service.title}</h3>
                </div>
                <p className="text-gray-600 font-light mb-8 leading-relaxed text-center text-lg" style={{ fontFamily: 'Georgia, serif' }}>{service.desc}</p>
                <div className="space-y-4 flex-1 flex flex-col items-center">
                  {service.items.map((item, idx) => (
                    <div key={idx} className="flex items-center">
                      <CheckCircle className="text-blue-600 mr-3 flex-shrink-0" size={18} />
                      <span className="text-gray-700 font-light text-base" style={{ fontFamily: 'Georgia, serif' }}>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            </div>
          )}
          </div>
        </section>
      </div>
    ));

  // About Page Component - Enhanced with Team Focus
  const AboutPage = React.memo(() => (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="pt-20 pb-32 bg-gradient-dark text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-radial from-beyond-charcoal/50 via-beyond-black to-beyond-black"></div>
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-32 h-32 bg-white/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-48 h-48 bg-white/3 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>
        <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12 xl:px-20">
          <div className="max-w-6xl mx-auto text-center">
            <h1 className="text-6xl md:text-8xl font-display font-bold mb-8 tracking-wide">
              About <em className="italic font-thin">Beyond26</em>
              </h1>
            <div className="w-32 h-0.5 bg-gradient-to-r from-transparent via-white to-transparent mx-auto mb-12"></div>
            <p className="text-2xl md:text-3xl font-light max-w-5xl mx-auto leading-relaxed">
              Bridging the gap between institutional investors and asset managers through unparalleled expertise and actionable insights
              </p>
            </div>
        </div>
      </section>

      {/* Company Overview Section */}
      <section className="py-24 bg-gradient-subtle">
        <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20">
          <div className="max-w-6xl mx-auto">
            <div className="prose prose-lg max-w-none">
              <p className="text-xl text-beyond-gray-700 font-light leading-relaxed mb-12 text-center">
                Beyond26 provides strategic investment advisory services to institutional investors and asset managers.
                With decades of experience managing billions across all asset classes, we deliver sophisticated analysis
                and actionable recommendations.
              </p>

              <div className="grid md:grid-cols-2 gap-16 mt-20">
                <div className="bg-white p-10 rounded-3xl shadow-elegant">
                  <h3 className="text-3xl font-display font-bold mb-8 text-beyond-black">Our Approach</h3>
                  <p className="text-beyond-gray-700 font-light leading-relaxed text-lg">
                    We combine quantitative rigor with qualitative insights to deliver comprehensive investment solutions.
                    Our team's deep industry experience enables us to identify opportunities and risks that others might miss.
                  </p>
                </div>

                <div className="bg-white p-10 rounded-3xl shadow-elegant">
                  <h3 className="text-3xl font-display font-bold mb-8 text-beyond-black">Our Commitment</h3>
                  <p className="text-beyond-gray-700 font-light leading-relaxed text-lg">
                    We are committed to delivering excellence in every engagement. Our clients rely on us for independent,
                    objective advice that helps them achieve their investment objectives while managing risk appropriately.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Experience & Expertise Section */}
      <section className="py-32 bg-white">
        <div className="max-w-[88%] mx-auto px-8">
          <div className="text-center mb-20">
            <h2 className="text-6xl md:text-7xl font-bold mb-8 text-beyond-black tracking-[0.12em]" style={{ fontFamily: 'Georgia, serif', textShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
              Our <span className="italic font-thin">Expertise</span>
            </h2>
            <div className="w-32 h-[2px] bg-gradient-to-r from-transparent via-black to-transparent mx-auto shadow-sm mb-16"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-12 md:gap-16 max-w-6xl mx-auto">
            <div className="text-center">
              <h3 className="text-3xl md:text-4xl font-light mb-6 text-beyond-black" style={{ fontFamily: 'Georgia, serif' }}>
                Fundraising Strategy
              </h3>
              <p className="text-gray-700 font-light leading-relaxed text-lg md:text-xl" style={{ fontFamily: 'Georgia, serif', lineHeight: '1.8' }}>
                Targeted fundraising strategies that resonate with institutional investors through optimized diligence processes and narrative.
              </p>
            </div>

            <div className="text-center">
              <h3 className="text-3xl md:text-4xl font-light mb-6 text-beyond-black" style={{ fontFamily: 'Georgia, serif' }}>
                Manager Due Diligence
              </h3>
              <p className="text-gray-700 font-light leading-relaxed text-lg md:text-xl" style={{ fontFamily: 'Georgia, serif', lineHeight: '1.8' }}>
                Rigorous assessment of risks and opportunities with experience from allocating billions as institutional investors.
              </p>
            </div>

            <div className="text-center">
              <h3 className="text-3xl md:text-4xl font-light mb-6 text-beyond-black" style={{ fontFamily: 'Georgia, serif' }}>
                Asset Allocation
              </h3>
              <p className="text-gray-700 font-light leading-relaxed text-lg md:text-xl" style={{ fontFamily: 'Georgia, serif', lineHeight: '1.8' }}>
                Portfolio solutions optimized for risk tolerance, liabilities and investment horizons across public and private markets.
              </p>
            </div>

            <div className="text-center">
              <h3 className="text-3xl md:text-4xl font-light mb-6 text-beyond-black" style={{ fontFamily: 'Georgia, serif' }}>
                Operational Efficiency
              </h3>
              <p className="text-gray-700 font-light leading-relaxed text-lg md:text-xl" style={{ fontFamily: 'Georgia, serif', lineHeight: '1.8' }}>
                Streamlined processes, enhanced reporting and infrastructure optimization for institutional investment operations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-32 bg-gradient-to-b from-gray-50 via-white to-gray-50 relative overflow-hidden">
        <div className="max-w-[88%] mx-auto px-8 relative z-10">
          <div className="text-center mb-20">
            <h2 className="text-6xl md:text-7xl font-bold mb-8 text-beyond-black tracking-[0.12em]" style={{ fontFamily: 'Georgia, serif', textShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
              Our <span className="italic font-thin">Team</span>
            </h2>
            <div className="w-32 h-[2px] bg-gradient-to-r from-transparent via-black to-transparent mx-auto shadow-sm"></div>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 max-w-7xl mx-auto">
              {/* Edgar Smith */}
              <div className="text-center">
                <div className="relative mb-12">
                  <div className="w-80 h-80 mx-auto rounded-full overflow-hidden shadow-2xl border-8 border-gray-200 hover:border-beyond-black transition-all duration-500 hover:scale-105 group">
                    <img
                      src="./edgarpic.jpeg"
                      alt="Edgar Smith"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  {/* Upload button only visible to admin users */}
                  {isLoggedIn && userRole === 'admin' && (
                    <>
                      <button
                        onClick={() => document.getElementById('edgar-photo-upload')?.click()}
                        className="absolute bottom-4 right-4 bg-beyond-black text-white p-3 rounded-full shadow-lg hover:bg-beyond-charcoal transition-colors"
                        disabled={isUploadingPhoto}
                      >
                        {isUploadingPhoto ? (
                          <RefreshCw className="w-5 h-5 animate-spin" />
                        ) : (
                          <Upload className="w-5 h-5" />
                        )}
                      </button>
                      <input
                        id="edgar-photo-upload"
                        type="file"
                        accept="image/*"
                        onChange={(e) => handlePhotoUpload('edgar', e.target.files?.[0])}
                        className="hidden"
                      />
                    </>
                  )}
                </div>

                <h3 className="text-4xl font-bold mb-6 text-beyond-black" style={{ fontFamily: 'Georgia, serif' }}>Edgar Smith</h3>
                <p className="text-2xl text-gray-600 font-light mb-10 tracking-wide" style={{ fontFamily: 'Georgia, serif' }}>Managing Partner</p>

                <div className="bg-white border-2 border-gray-200 shadow-elegant p-6 md:p-10 mb-6 md:mb-10 min-h-[400px] md:h-[480px] flex flex-col justify-between hover:shadow-2xl hover:border-gray-300 transition-all duration-500">
                  <div className="text-beyond-gray-700 font-light leading-relaxed text-base md:text-lg mb-6 md:mb-8" style={{ fontFamily: 'Georgia, serif', lineHeight: '1.8' }}>
                    <p className="mb-4">
                      Edgar Smith is the founder of Beyond26 Investment Advisors. He was formerly the first Chief Investment Officer for the Directors Guild of America (DGA) Plans.
                    </p>
                    <p className="mb-4">
                      Prior to the DGA, he was a Managing Director at USC's Investment Office for over a decade. Before USC, he was a senior investment professional at Archstone Partnerships and Paamco.
                    </p>
                    <p>
                      He began his career at JPMorgan in the Hedge Fund Counterparty Risk and Equity Derivatives desks. He holds a B.A. in Economics and History from Yale University and an MBA from Columbia University.
                    </p>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center justify-center space-x-3">
                      <Mail className="w-5 h-5 md:w-6 md:h-6 text-beyond-gray-500" />
                      <a href="mailto:esmith@beyond26advisors.com" className="text-beyond-gray-700 hover:text-beyond-black transition-colors text-base md:text-lg" style={{ fontFamily: 'Georgia, serif' }}>
                        esmith@beyond26advisors.com
                      </a>
                    </div>
                  </div>
                </div>

              </div>

              {/* Alex Smith */}
              <div className="text-center">
                <div className="relative mb-12">
                  <div className="w-80 h-80 mx-auto rounded-full overflow-hidden shadow-2xl border-8 border-gray-200 hover:border-beyond-black transition-all duration-500 hover:scale-105 group">
                    <img
                      src="./AlexPic.jpeg"
                      alt="Alex Smith"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  {/* Upload button only visible to admin users */}
                  {isLoggedIn && userRole === 'admin' && (
                    <>
                      <button
                        onClick={() => document.getElementById('alex-photo-upload')?.click()}
                        className="absolute bottom-4 right-4 bg-beyond-black text-white p-3 rounded-full shadow-lg hover:bg-beyond-charcoal transition-colors"
                        disabled={isUploadingPhoto}
                      >
                        {isUploadingPhoto ? (
                          <RefreshCw className="w-5 h-5 animate-spin" />
                        ) : (
                          <Upload className="w-5 h-5" />
                        )}
                      </button>
                      <input
                        id="alex-photo-upload"
                        type="file"
                        accept="image/*"
                        onChange={(e) => handlePhotoUpload('alex', e.target.files?.[0])}
                        className="hidden"
                      />
                    </>
                  )}
                </div>

                <h3 className="text-4xl font-bold mb-6 text-beyond-black" style={{ fontFamily: 'Georgia, serif' }}>Alex Smith</h3>
                <p className="text-2xl text-gray-600 font-light mb-10 tracking-wide" style={{ fontFamily: 'Georgia, serif' }}>Partner</p>

                <div className="bg-white border-2 border-gray-200 shadow-elegant p-6 md:p-10 mb-6 md:mb-10 min-h-[400px] md:h-[480px] flex flex-col justify-between hover:shadow-2xl hover:border-gray-300 transition-all duration-500">
                  <div className="text-beyond-gray-700 font-light leading-relaxed text-base md:text-lg mb-6 md:mb-8" style={{ fontFamily: 'Georgia, serif', lineHeight: '1.8' }}>
                    <p className="mb-4">
                      Alex Smith is a Partner at Beyond26 Investment Advisors. He was formerly an Investor at The Sobrato Organization (Single Family Office & Foundation).
                    </p>
                    <p className="mb-4">
                      Prior to Sobrato, Alex was on the investment team at the USC Investment Office. Across both institutions, he covered all asset classes with a lean on alternative investing.
                    </p>
                    <p>
                      He holds a B.S. in Business Administration/Finance and a minor in Entrepreneurship from the USC Marshall School of Business.
                    </p>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center justify-center space-x-3">
                      <Mail className="w-5 h-5 md:w-6 md:h-6 text-beyond-gray-500" />
                      <a href="mailto:asmith@beyond26advisors.com" className="text-beyond-gray-700 hover:text-beyond-black transition-colors text-base md:text-lg" style={{ fontFamily: 'Georgia, serif' }}>
                        asmith@beyond26advisors.com
                      </a>
                    </div>
                  </div>
                </div>

              </div>
            </div>
        </div>
      </section>
    </div>
  ));

  // Multi-Select Calendar Component
  const MultiSelectCalendar = React.memo(({ selectedTimes, setSelectedTimes, selectedAdvisors, onSuccess }) => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [schedulingForm, setSchedulingForm] = useState({
      name: '',
      email: '',
      phone: '',
      firm: '',
      notes: ''
    });

    // Generate time slots (8am - 4pm PT, 30-min intervals)
    const generateTimeSlots = () => {
      const slots = [];
      for (let hour = 8; hour <= 15; hour++) {
        for (let minute of [0, 30]) {
          if (hour === 15 && minute === 30) continue; // Stop at 4:00pm
          const time = new Date();
          time.setHours(hour, minute, 0, 0);
          slots.push(time.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }));
        }
      }
      return slots;
    };

    const timeSlots = generateTimeSlots();

    // Generate calendar days (45 days in advance, Mon-Fri only)
    const generateCalendarDays = () => {
      const days = [];
      const today = new Date();
      let daysAdded = 0;
      let currentDay = new Date(currentDate);
      currentDay.setDate(1);

      // Get days in current month
      const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();

      for (let i = 1; i <= daysInMonth && daysAdded < 45; i++) {
        const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), i);
        const dayOfWeek = date.getDay();

        // Only include Mon-Fri (1-5) and future dates
        if (dayOfWeek >= 1 && dayOfWeek <= 5 && date >= today) {
          days.push(date);
          daysAdded++;
        }
      }

      return days;
    };

    const calendarDays = generateCalendarDays();

    // Toggle time selection
    const toggleTimeSlot = (date, timeSlot) => {
      const dateTimeString = `${date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} at ${timeSlot}`;

      const isSelected = selectedTimes.includes(dateTimeString);

      if (isSelected) {
        setSelectedTimes(selectedTimes.filter(t => t !== dateTimeString));
      } else if (selectedTimes.length < 3) {
        setSelectedTimes([...selectedTimes, dateTimeString]);
      }
    };

    // Check if time slot is selected
    const isTimeSlotSelected = (date, timeSlot) => {
      const dateTimeString = `${date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} at ${timeSlot}`;
      return selectedTimes.includes(dateTimeString);
    };

    // Navigate months
    const previousMonth = () => {
      const newDate = new Date(currentDate);
      newDate.setMonth(newDate.getMonth() - 1);
      setCurrentDate(newDate);
    };

    const nextMonth = () => {
      const newDate = new Date(currentDate);
      newDate.setMonth(newDate.getMonth() + 1);
      // Don't go beyond 45 days
      const maxDate = new Date();
      maxDate.setDate(maxDate.getDate() + 45);
      if (newDate <= maxDate) {
        setCurrentDate(newDate);
      }
    };

    // Submit meeting request
    const handleSubmit = async () => {
      if (schedulingForm.name && schedulingForm.email && schedulingForm.firm && selectedTimes.length > 0 && selectedAdvisors.length > 0) {
        try {
          const API_BASE = 'https://beyond26-api.onrender.com/api';
          await fetch(`${API_BASE}/meeting-request`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              advisors: selectedAdvisors,
              name: schedulingForm.name,
              email: schedulingForm.email,
              phone: schedulingForm.phone,
              firm: schedulingForm.firm,
              notes: schedulingForm.notes,
              selectedTimes: selectedTimes
            })
          });

          // Reset form
          setSchedulingForm({ name: '', email: '', phone: '', firm: '', notes: '' });
          setSelectedTimes([]);

          // Show success message
          if (onSuccess) {
            onSuccess();
          }
        } catch (error) {
          console.error('Meeting request error:', error);
          alert('Failed to submit meeting request. Please try again or contact us directly.');
        }
      } else {
        alert('Please fill in your name, email, firm, select at least one advisor, and select at least one time slot.');
      }
    };

    return (
      <div className="space-y-8 calendar-slide-in">
        {/* Calendar Header */}
        <div className="flex items-center justify-between mb-6 bg-gradient-to-r from-gray-50 via-white to-gray-50 p-4 rounded-xl shadow-sm">
          <button
            onClick={previousMonth}
            className="p-3 hover:bg-gradient-to-br hover:from-gray-900 hover:to-gray-800 hover:text-white rounded-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-30 disabled:hover:scale-100 disabled:hover:bg-transparent disabled:hover:text-current"
            disabled={currentDate.getMonth() === new Date().getMonth()}
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <h4 className="text-2xl font-bold text-beyond-black" style={{ fontFamily: 'Georgia, serif' }}>
            {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </h4>
          <button
            onClick={nextMonth}
            className="p-3 hover:bg-gradient-to-br hover:from-gray-900 hover:to-gray-800 hover:text-white rounded-xl transition-all duration-300 transform hover:scale-105"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>

        {/* Selected Times Display */}
        {selectedTimes.length > 0 && (
          <div className="bg-gradient-to-br from-gray-50 via-white to-gray-100 p-6 rounded-xl shadow-md border border-gray-200 calendar-slide-in">
            <p className="text-sm font-semibold text-gray-700 mb-3" style={{ fontFamily: 'Georgia, serif' }}>
              Selected Times ({selectedTimes.length}/3):
            </p>
            <div className="flex flex-wrap gap-3">
              {selectedTimes.map((time, idx) => (
                <span
                  key={idx}
                  className="bg-gradient-to-br from-beyond-black via-gray-900 to-gray-800 text-white px-4 py-2 rounded-xl text-sm font-medium shadow-lg transform hover:scale-105 transition-all duration-300 time-slot-selected"
                >
                  <Clock className="w-3 h-3 inline mr-2" />
                  {time}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Calendar Days with Time Slots */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-h-96 overflow-y-auto pr-2" style={{ scrollbarWidth: 'thin' }}>
          {calendarDays.map((date, idx) => {
            const isToday = date.toDateString() === new Date().toDateString();
            return (
              <div
                key={idx}
                className={`border-2 rounded-2xl p-5 bg-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] ${
                  isToday ? 'border-beyond-black bg-gradient-to-br from-gray-50 to-white' : 'border-gray-200'
                }`}
                style={{ animation: `slideInUp 0.3s ease-out ${idx * 0.05}s both` }}
              >
                <div
                  className={`font-bold text-lg mb-4 pb-3 border-b-2 ${
                    isToday ? 'text-beyond-black border-beyond-black' : 'text-gray-800 border-gray-200'
                  }`}
                  style={{ fontFamily: 'Georgia, serif' }}
                >
                  {date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  {isToday && (
                    <span className="ml-2 text-xs bg-beyond-black text-white px-2 py-1 rounded-full">Today</span>
                  )}
                </div>
                <div className="space-y-2">
                  {timeSlots.map((timeSlot, timeIdx) => {
                    const isSelected = isTimeSlotSelected(date, timeSlot);
                    const isDisabled = !isSelected && selectedTimes.length >= 3;

                    return (
                      <button
                        key={timeIdx}
                        onClick={() => !isDisabled && toggleTimeSlot(date, timeSlot)}
                        disabled={isDisabled}
                        className={`w-full px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 transform ${
                          isSelected
                            ? 'bg-gradient-to-br from-beyond-black via-gray-900 to-gray-800 text-white shadow-lg scale-105 hover:scale-110'
                            : isDisabled
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-50'
                            : 'bg-gradient-to-br from-gray-50 to-gray-100 hover:from-gray-200 hover:to-gray-300 text-gray-700 hover:shadow-md hover:scale-105'
                        }`}
                      >
                        <Clock className={`w-3.5 h-3.5 inline mr-2 ${isSelected ? 'animate-pulse' : ''}`} />
                        {timeSlot}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Scheduling Form */}
        <div className="border-t pt-8 mt-8">
          <h4 className="text-xl font-bold mb-6 text-beyond-black">Your Information</h4>
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-lg font-medium mb-3 text-beyond-black">Name *</label>
                <input
                  type="text"
                  value={schedulingForm.name}
                  onChange={(e) => setSchedulingForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl text-lg focus:border-beyond-black focus:ring-2 focus:ring-beyond-black/20 focus:outline-none transition-colors"
                  required
                />
              </div>
              <div>
                <label className="block text-lg font-medium mb-3 text-beyond-black">Email *</label>
                <input
                  type="email"
                  value={schedulingForm.email}
                  onChange={(e) => setSchedulingForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl text-lg focus:border-beyond-black focus:ring-2 focus:ring-beyond-black/20 focus:outline-none transition-colors"
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-lg font-medium mb-3 text-beyond-black">Firm *</label>
                <input
                  type="text"
                  value={schedulingForm.firm}
                  onChange={(e) => setSchedulingForm(prev => ({ ...prev, firm: e.target.value }))}
                  className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl text-lg focus:border-beyond-black focus:ring-2 focus:ring-beyond-black/20 focus:outline-none transition-colors"
                  required
                />
              </div>
              <div>
                <label className="block text-lg font-medium mb-3 text-beyond-black">Phone</label>
                <input
                  type="tel"
                  value={schedulingForm.phone}
                  onChange={(e) => setSchedulingForm(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl text-lg focus:border-beyond-black focus:ring-2 focus:ring-beyond-black/20 focus:outline-none transition-colors"
                />
              </div>
            </div>
            <div>
              <label className="block text-lg font-medium mb-3 text-beyond-black">Additional Notes</label>
              <textarea
                value={schedulingForm.notes}
                onChange={(e) => setSchedulingForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={4}
                className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl text-lg focus:border-beyond-black focus:ring-2 focus:ring-beyond-black/20 focus:outline-none transition-colors resize-none"
              />
            </div>
            <button
              onClick={handleSubmit}
              className="w-full bg-gradient-to-br from-beyond-black via-gray-900 to-gray-800 text-white py-4 rounded-xl text-lg font-semibold hover:shadow-2xl transform hover:scale-[1.02] transition-all duration-300"
            >
              Submit Meeting Request
            </button>
          </div>
        </div>
      </div>
    );
  });

  // Isolated Contact Form Component with own state
  const ContactFormFields = React.memo(({ onSuccess }) => {
    const [formData, setFormData] = useState({
      name: '',
      firm: '',
      email: '',
      phone: '',
      comments: ''
    });

    const handleSubmit = async () => {
      if (formData.name && formData.email && formData.comments) {
        try {
          await api.submitContact(formData);
          setFormData({ name: '', firm: '', email: '', phone: '', comments: '' });

          // Show success message
          if (onSuccess) {
            onSuccess();
          }
        } catch (error) {
          console.error('Contact submission error:', error);
          alert('Failed to send message. Please try again or contact us directly.');
        }
      } else {
        alert('Please fill in all required fields (Name, Email, Comments)');
      }
    };

    return (
      <div className="bg-white p-12 rounded-2xl shadow-2xl border border-gray-100">
        <div className="mb-8">
          <h3 className="text-3xl font-display font-bold mb-6 text-beyond-black">
            Send a <span className="italic font-thin">Message</span>
          </h3>
          <div className="w-16 h-0.5 bg-gradient-to-r from-beyond-black to-transparent mb-8"></div>
        </div>

        <div className="space-y-8">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-lg font-medium mb-3 text-beyond-black">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl text-lg focus:border-beyond-black focus:ring-2 focus:ring-beyond-black/20 focus:outline-none transition-colors"
                required
              />
            </div>
            <div>
              <label className="block text-lg font-medium mb-3 text-beyond-black">Firm</label>
              <input
                type="text"
                value={formData.firm}
                onChange={(e) => setFormData(prev => ({ ...prev, firm: e.target.value }))}
                className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl text-lg focus:border-beyond-black focus:ring-2 focus:ring-beyond-black/20 focus:outline-none transition-colors"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-lg font-medium mb-3 text-beyond-black">Email *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl text-lg focus:border-beyond-black focus:ring-2 focus:ring-beyond-black/20 focus:outline-none transition-colors"
                required
              />
            </div>
            <div>
              <label className="block text-lg font-medium mb-3 text-beyond-black">Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl text-lg focus:border-beyond-black focus:ring-2 focus:ring-beyond-black/20 focus:outline-none transition-colors"
              />
            </div>
          </div>

          <div>
            <label className="block text-lg font-medium mb-3 text-beyond-black">Comments *</label>
            <textarea
              rows={8}
              value={formData.comments}
              onChange={(e) => setFormData(prev => ({ ...prev, comments: e.target.value }))}
              className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl resize-none text-lg focus:border-beyond-black focus:ring-2 focus:ring-beyond-black/20 focus:outline-none transition-colors"
              placeholder="Please describe your specific needs and how we can help..."
              required
            ></textarea>
          </div>

          <button
            type="button"
            onClick={handleSubmit}
            className="w-full bg-beyond-black text-white py-6 px-8 rounded-full font-medium text-lg hover:bg-beyond-charcoal transition-all duration-300 flex items-center justify-center group transform hover:scale-[1.02] shadow-lg hover:shadow-2xl"
          >
            <Send className="w-6 h-6 mr-4 group-hover:scale-110 transition-transform" />
            <span>Send Message</span>
          </button>
        </div>
      </div>
    );
  });

  // Isolated Scheduling Form Component with own state
  const SchedulingFormFields = React.memo(({ schedulingForm, setSchedulingForm }) => {
    return (
      <div className="space-y-4 pt-4 border-t border-gray-200">
        <h3 className="text-xl font-bold text-gray-900">Your Information</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
            <input
              type="text"
              value={schedulingForm.name}
              onChange={(e) => setSchedulingForm(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-beyond-black focus:ring-2 focus:ring-beyond-black/20 outline-none"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
            <input
              type="email"
              value={schedulingForm.email}
              onChange={(e) => setSchedulingForm(prev => ({ ...prev, email: e.target.value }))}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-beyond-black focus:ring-2 focus:ring-beyond-black/20 outline-none"
              required
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Phone (optional)</label>
          <input
            type="tel"
            value={schedulingForm.phone}
            onChange={(e) => setSchedulingForm(prev => ({ ...prev, phone: e.target.value }))}
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-beyond-black focus:ring-2 focus:ring-beyond-black/20 outline-none"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Meeting Notes (optional)</label>
          <textarea
            value={schedulingForm.notes}
            onChange={(e) => setSchedulingForm(prev => ({ ...prev, notes: e.target.value }))}
            rows={3}
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-beyond-black focus:ring-2 focus:ring-beyond-black/20 outline-none"
            placeholder="What would you like to discuss?"
          />
        </div>
      </div>
    );
  });

  // Contact Page Component
  const ContactPage = () => (
    <div className="min-h-screen bg-white">
      {/* Enhanced Hero Section */}
      <section className="pt-32 pb-28 bg-gradient-to-b from-black via-gray-900 to-black text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-gray-800/20 via-black to-black"></div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/3 rounded-full blur-3xl animate-pulse delay-500"></div>
        <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20 text-center relative z-10">
          <h1 className="text-6xl md:text-7xl font-display font-bold mb-8 tracking-wide">
            Contact <span className="italic font-thin">Us</span>
          </h1>
          <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-white to-transparent mx-auto mb-8"></div>
          <p className="text-xl md:text-2xl font-light max-w-4xl mx-auto leading-relaxed">
            Ready to enhance your investment strategy? Let's discuss how our bespoke solutions can help achieve your objectives.
          </p>
                              </div>
      </section>

      {/* Full Width Contact Section */}
      <section className="py-24 bg-white">
        <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-6 text-beyond-black tracking-wide">
                Get In <span className="italic font-thin">Touch</span>
              </h2>
              <div className="w-24 h-0.5 bg-gradient-to-r from-beyond-black to-transparent mx-auto mb-6"></div>
              <p className="text-xl text-gray-600 font-light max-w-2xl mx-auto" style={{ fontFamily: 'Georgia, serif' }}>
                Choose how you'd like to connect with us.
              </p>
            </div>

            {/* Toggle Buttons */}
            <div className="grid md:grid-cols-2 gap-6 mb-12" id="contact-options">
              {/* Schedule Meeting Toggle */}
              <button
                onClick={() => {
                  setContactView('schedule');
                  setTimeout(() => {
                    scheduleFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 100);
                }}
                className={`p-8 rounded-2xl shadow-elegant transition-all duration-500 transform hover:scale-[1.02] text-center group cursor-pointer ${
                  contactView === 'schedule'
                    ? 'bg-gradient-to-br from-beyond-black via-gray-900 to-gray-800 text-white'
                    : 'bg-gradient-to-br from-gray-100 via-white to-gray-200 text-beyond-black border border-gray-300'
                }`}
              >
                <Calendar className={`w-8 h-8 mx-auto mb-4 group-hover:scale-110 transition-transform ${
                  contactView === 'schedule' ? 'text-white' : 'text-beyond-black'
                }`} />
                <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Georgia, serif' }}>Schedule a Meeting</h3>
                <p className={`font-light ${contactView === 'schedule' ? 'text-gray-300' : 'text-gray-600'}`} style={{ fontFamily: 'Georgia, serif' }}>
                  Request meeting times
                </p>
              </button>

              {/* Send Message Toggle */}
              <button
                onClick={() => {
                  setContactView('message');
                  setTimeout(() => {
                    contactFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 100);
                }}
                className={`p-8 rounded-2xl shadow-elegant transition-all duration-500 transform hover:scale-[1.02] text-center group cursor-pointer ${
                  contactView === 'message'
                    ? 'bg-gradient-to-br from-beyond-black via-gray-900 to-gray-800 text-white'
                    : 'bg-gradient-to-br from-gray-100 via-white to-gray-200 text-beyond-black border border-gray-300'
                }`}
              >
                <Mail className={`w-8 h-8 mx-auto mb-4 group-hover:scale-110 transition-transform ${
                  contactView === 'message' ? 'text-white' : 'text-beyond-black'
                }`} />
                <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Georgia, serif' }}>Send a Message</h3>
                <p className={`font-light ${contactView === 'message' ? 'text-gray-300' : 'text-gray-600'}`} style={{ fontFamily: 'Georgia, serif' }}>
                  Email us directly
                </p>
              </button>
            </div>

            {/* Contact Form or Calendar - Based on Selection */}
            <div className="mt-12">
              {contactView === 'message' ? (
                <div ref={contactFormRef} id="message-form">
                  <ContactFormFields onSuccess={handleSuccessMessage} />
                </div>
              ) : (
                <div ref={scheduleFormRef} id="schedule-form" className="bg-white p-8 rounded-2xl shadow-2xl border border-gray-100">
                  <h3 className="text-3xl font-bold mb-6 text-beyond-black text-center">
                    Request a <span className="italic font-thin">Meeting</span>
                  </h3>
                  <p className="text-center text-gray-600 mb-8" style={{ fontFamily: 'Georgia, serif' }}>
                    Select up to 3 times that work for you. We'll confirm the best option.
                  </p>

                  {/* Advisor Multi-Select */}
                  <div className="mb-8">
                    <label className="block text-lg font-medium text-gray-700 mb-4 text-center">
                      Who would you like to meet with? *
                    </label>
                    <div className="grid grid-cols-2 gap-4 max-w-2xl mx-auto">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedAdvisors(prev =>
                            prev.includes('alex')
                              ? prev.filter(a => a !== 'alex')
                              : [...prev, 'alex']
                          );
                        }}
                        className={`p-6 border-2 rounded-xl transition-all duration-300 ${
                          selectedAdvisors.includes('alex')
                            ? 'border-beyond-black bg-gray-50 shadow-lg ring-2 ring-beyond-black ring-opacity-50'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <div className="text-center">
                          <p className="font-bold text-lg">Alex Smith</p>
                          <p className="text-sm text-gray-600">Partner</p>
                          {selectedAdvisors.includes('alex') && (
                            <CheckCircle className="w-6 h-6 mx-auto mt-2 text-beyond-black" />
                          )}
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedAdvisors(prev =>
                            prev.includes('edgar')
                              ? prev.filter(a => a !== 'edgar')
                              : [...prev, 'edgar']
                          );
                        }}
                        className={`p-6 border-2 rounded-xl transition-all duration-300 ${
                          selectedAdvisors.includes('edgar')
                            ? 'border-beyond-black bg-gray-50 shadow-lg ring-2 ring-beyond-black ring-opacity-50'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <div className="text-center">
                          <p className="font-bold text-lg">Edgar Smith</p>
                          <p className="text-sm text-gray-600">Managing Partner</p>
                          {selectedAdvisors.includes('edgar') && (
                            <CheckCircle className="w-6 h-6 mx-auto mt-2 text-beyond-black" />
                          )}
                        </div>
                      </button>
                    </div>
                  </div>

                  {/* Multi-Select Calendar */}
                  <MultiSelectCalendar
                    selectedTimes={selectedTimes}
                    setSelectedTimes={setSelectedTimes}
                    selectedAdvisors={selectedAdvisors}
                    onSuccess={handleSuccessMessage}
                  />
                </div>
              )}
            </div>

          {/* Success Message */}
          {showSuccessMessage && (
            <div className="fixed inset-0 flex items-center justify-center z-[1000] success-overlay">
              <div className="success-card p-8 rounded-3xl w-[min(90vw,420px)] text-center animate-bounce-in animate-fade-out-delayed">
                <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500 success-bounce" />
                <h3 className="text-2xl font-bold mb-2 text-beyond-black" style={{ fontFamily: 'Georgia, serif' }}>
                  Thank you for reaching out!
                </h3>
                <p className="text-gray-600" style={{ fontFamily: 'Georgia, serif' }}>
                  You will hear from us shortly.
                </p>
                <p className="text-sm text-green-600 mt-4 flex items-center justify-center gap-2 pulse-animation" style={{ fontFamily: 'Georgia, serif' }}>
                  <span>We'll follow up via email</span>
                </p>
              </div>
            </div>
          )}
          </div>
        </div>
      </section>
      </div>
    );

  // Dashboard Component
  const Dashboard = () => {
    const sidebarItems = {
      admin: [
        { id: 'overview', label: 'Executive Dashboard', icon: Layout, badge: null },
        { id: 'analytics', label: 'Advanced Analytics', icon: BarChart3, badge: null },
        { id: 'projects', label: 'Project Portfolio', icon: Briefcase, badge: projects.filter(p => p.status === 'Active').length },
        { id: 'messages', label: 'Communications', icon: MessageSquare, badge: messages.filter(m => !m.read).length },
        { id: 'calendar', label: 'Calendar Management', icon: Calendar, badge: null },
        { id: 'crm', label: 'Client Relations', icon: Users, badge: crmContacts.length },
        { id: 'documents', label: 'Document Center', icon: Folder, badge: uploadedFiles.length },
        { id: 'ai-assistant', label: 'AI Advisory Suite', icon: Database, badge: null },
        { id: 'reports', label: 'Reporting Suite', icon: FileText, badge: null },
        { id: 'compliance', label: 'Compliance Monitor', icon: Shield, badge: null },
        { id: 'deployment', label: 'System Deployment', icon: Globe, badge: null }
      ],
      employee: [
        { id: 'overview', label: 'My Dashboard', icon: Layout, badge: null },
        { id: 'tasks', label: 'Task Management', icon: CheckSquare, badge: (tasks || []).filter(t => t.status === 'pending').length },
        { id: 'projects', label: 'Active Projects', icon: Briefcase, badge: projects.filter(p => p.team?.includes(currentUser?.name || '')).length },
        { id: 'messages', label: 'Communications', icon: MessageSquare, badge: messages.filter(m => !m.read).length },
        { id: 'calendar', label: 'Schedule', icon: Calendar, badge: null },
        { id: 'crm', label: 'Client Relations', icon: Users, badge: crmContacts.length },
        { id: 'documents', label: 'Resources', icon: Folder, badge: uploadedFiles.length },
        { id: 'ai-assistant', label: 'AI Tools', icon: Database, badge: null },
        { id: 'portfolio', label: 'Portfolio Analysis', icon: PieChart, badge: null }
      ],
      client: [
        { id: 'overview', label: 'Client Portal', icon: Layout, badge: null },
        { id: 'projects', label: 'My Projects', icon: Briefcase, badge: projects.filter(p => p.client === currentUser?.company).length },
        { id: 'performance', label: 'Performance Review', icon: TrendingUp, badge: null },
        { id: 'messages', label: 'Communications', icon: MessageSquare, badge: messages.filter(m => !m.read).length },
        { id: 'calendar', label: 'Meetings', icon: Calendar, badge: null },
        { id: 'reports', label: 'Reports & Analysis', icon: FileText, badge: null },
        { id: 'documents', label: 'Document Library', icon: Folder, badge: uploadedFiles.filter(f => f.access === 'client' || !f.access).length }
      ]
    };

    const currentSidebarItems = sidebarItems[userRole] || sidebarItems.client;

    return (
      <div className="flex h-screen bg-gray-50">
        {/* Sidebar */}
        <div className="w-64 bg-beyond-dark text-white flex flex-col">
          <div className="p-6 border-b border-beyond-charcoal">
            <h2 className="text-xl font-display font-bold">Beyond26</h2>
            <p className="text-sm text-gray-400 mt-1">Investment Advisory Portal</p>
          </div>
          
          <div className="p-4">
            <div className="flex items-center space-x-3 bg-beyond-charcoal rounded-lg p-3">
              <div className="w-10 h-10 bg-beyond-gray-600 rounded-lg flex items-center justify-center text-white font-medium">
                {currentUser?.name?.split(' ').map(n => n[0]).join('')}
            </div>
            <div>
                <div className="font-medium">{currentUser?.name}</div>
                <div className="text-sm text-gray-400">
                  {currentUser?.role === 'client' ? currentUser?.type : currentUser?.title}
            </div>
                <div className="text-xs text-gray-500 capitalize">{currentUser?.role}</div>
              </div>
            </div>
            </div>
            
          <nav className="flex-1 px-4 pb-4">
            <div className="space-y-1">
                            {currentSidebarItems.map(item => (
              <button 
                  key={item.id}
                  onClick={() => setDashboardView(item.id)}
                  className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors ${
                    dashboardView === item.id
                      ? 'bg-white text-beyond-black'
                      : 'text-gray-300 hover:bg-beyond-charcoal hover:text-white'
                  }`}
                >
                  <item.icon size={20} />
                  <span>{item.label}</span>
                  {item.badge && item.badge > 0 && (
                    <span className="bg-red-500 text-white text-xs rounded-full px-2 py-1 ml-auto">
                      {item.badge}
                    </span>
                  )}
              </button>
              ))}
            </div>
          </nav>
        </div>

        {/* Main Content - Full Width */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto bg-gray-50">
            <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20 py-8">
              {dashboardView === 'overview' && <DashboardOverview />}
              {dashboardView === 'projects' && <ProjectsView />}
              {dashboardView === 'tasks' && <TasksView />}
              {dashboardView === 'messages' && <MessagesView />}
              {dashboardView === 'calendar' && <CalendarView />}
              {dashboardView === 'contracts' && <ContractsView />}
              {dashboardView === 'documents' && <DocumentsView />}
              {dashboardView === 'ai-assistant' && <AIAssistantView />}
              {dashboardView === 'crm' && (userRole === 'admin' || userRole === 'employee') && <CrmView />}
              {dashboardView === 'portfolio' && <PortfolioView />}
              {dashboardView === 'team' && userRole === 'admin' && <TeamView />}
              {dashboardView === 'analytics' && userRole === 'admin' && <AnalyticsView />}
              {dashboardView === 'reports' && <ReportsView />}
              {dashboardView === 'compliance' && userRole === 'admin' && <ComplianceView />}
              {dashboardView === 'deployment' && userRole === 'admin' && <DeploymentView />}
              {dashboardView === 'performance' && userRole === 'client' && <PerformanceView />}
              {dashboardView === 'settings' && <SettingsView />}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Enhanced Interactive Dashboard Overview Component
  const DashboardOverview = () => {
    const [selectedWidget, setSelectedWidget] = useState(null);
    
    // Memoize expensive calculations
    const projectStats = useMemo(() => ({
      active: projects.filter(p => p.status === 'Active' || p.status === 'In Progress').length,
      completed: projects.filter(p => p.status === 'Completed').length,
      total: projects.length
    }), [projects]);
    
    const stats = [
        { 
        id: 'projects',
        title: 'Active Projects',
        value: projectStats.active,
          icon: Briefcase,
        color: 'from-blue-600 to-blue-700',
        description: 'Currently active engagements',
        history: projects,
        actions: ['Edit Project', 'View Details', 'Update Status', 'Generate Report']
      },
      {
        id: 'completed',
        title: 'Completed Projects',
          value: projectStats.completed, 
          icon: CheckCircle,
        color: 'from-green-600 to-green-700',
        description: 'Successfully delivered projects',
        history: projects.filter(p => p.status === 'Completed'),
        actions: ['View Archive', 'Client Feedback', 'Export Data']
      },
      {
        id: 'messages',
        title: 'Unread Messages',
          value: messages.filter(m => !m.read).length, 
          icon: MessageSquare,
        color: 'from-purple-600 to-purple-700',
        description: 'Messages requiring attention',
        history: messages,
        actions: ['Reply via Email', 'Mark Read', 'Forward', 'Archive']
      }
    ];

    if (userRole === 'employee' || userRole === 'admin') {
      stats.push({
        id: 'ai',
        title: 'AI Queries Today',
        value: aiHistory.filter(h => new Date(h.timestamp).toDateString() === new Date().toDateString()).length,
        icon: Database,
        color: 'from-indigo-600 to-indigo-700',
        description: 'AI assistant interactions',
        history: aiHistory,
        actions: ['View History', 'Export Analytics', 'Trend Analysis']
      });
    }

    return (
      <div className="space-y-8">
        {/* Enhanced Welcome Section */}
        <div className="bg-gradient-to-br from-beyond-black via-beyond-dark to-beyond-charcoal text-white rounded-2xl p-12 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/3 rounded-full blur-3xl"></div>
          <div className="relative z-10">
            <h1 className="text-4xl lg:text-5xl font-display font-bold mb-4 tracking-wide">
              Welcome back, <em className="italic">{currentUser?.name}</em>
            </h1>
            <p className="text-gray-300 text-xl mb-6 max-w-3xl">
            {userRole === 'admin' && "Complete system overview and management dashboard for Beyond26 operations"}
            {userRole === 'employee' && "Your project portfolio and team collaboration center"}
            {userRole === 'client' && "Project status updates and secure communication portal"}
          </p>
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-sm text-gray-300">System Status: Online</span>
              </div>
              <div className="flex items-center space-x-2">
                <Clock className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-300">Last Login: {new Date().toLocaleDateString()}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Mail className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-300">Email Client: {emailClient === 'outlook' ? 'Outlook' : 'Gmail'}</span>
                <button
                  onClick={() => setEmailClient(emailClient === 'outlook' ? 'gmail' : 'outlook')}
                  className="text-xs bg-white/10 px-2 py-1 rounded hover:bg-white/20 transition-colors"
                >
                  Switch
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Interactive Stats Grid */}
        <div className="grid lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
          {stats.map((stat) => (
            <div 
              key={stat.id}
              className="group card-elegant p-6 cursor-pointer hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1"
              onClick={() => setSelectedWidget(selectedWidget === stat.id ? null : stat.id)}
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                  <stat.icon className="text-white" size={24} />
                </div>
                <div className="flex items-center space-x-2">
                  {selectedWidget === stat.id && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowHistory({...showHistory, [stat.id]: !showHistory[stat.id]});
                      }}
                      className="p-1 hover:bg-gray-100 rounded transition-colors"
                      title="View History"
                    >
                      <Eye className="w-4 h-4 text-gray-600" />
                    </button>
                  )}
                  <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${
                    selectedWidget === stat.id ? 'rotate-180' : ''
                  }`} />
                </div>
              </div>
              
              <div className="text-3xl font-bold text-beyond-black mb-2 group-hover:text-beyond-charcoal transition-colors">
                {stat.value}
              </div>
              
              <h3 className="font-medium text-beyond-black mb-1 group-hover:text-beyond-charcoal transition-colors">
                {stat.title}
              </h3>
              
              <p className="text-sm text-beyond-gray-600">
                {stat.description}
              </p>

              {/* Expanded Actions */}
              {selectedWidget === stat.id && (
                <div className="space-y-2 pt-4 mt-4 border-t border-gray-200">
                  {stat.actions.map((action, index) => (
                    <button
                      key={index}
                      className="w-full text-left p-2 text-sm hover:bg-gray-50 rounded transition-colors flex items-center group/action"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (action.includes('Email')) {
                          setShowEmailModal(true);
                        } else {
                          addNotification(`${action} functionality activated`, 'info');
                        }
                      }}
                    >
                      <ArrowRight className="w-3 h-3 mr-2 text-gray-400 group-hover/action:text-beyond-black transition-colors" />
                      {action}
                    </button>
                  ))}
                </div>
              )}

              {/* History View */}
              {showHistory[stat.id] && (
                <div className="mt-4 pt-4 border-t border-gray-200 max-h-48 overflow-y-auto">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-gray-700">Recent History</h4>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // Export functionality
                        const data = JSON.stringify(stat.history, null, 2);
                        const blob = new Blob([data], { type: 'application/json' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `${stat.id}-history.json`;
                        a.click();
                        addNotification('History exported successfully', 'success');
                      }}
                      className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
                    >
                      <Download className="w-3 h-3 mr-1" />
                      Export
                    </button>
                  </div>
                  <div className="space-y-2">
                    {stat.history.slice(0, 5).map((item, index) => (
                      <div key={index} className="text-xs text-gray-600 p-2 bg-gray-50 rounded hover:bg-gray-100 transition-colors">
                        <div className="font-medium">{item.title || item.subject || item.name || 'Recent activity'}</div>
                        <div className="text-gray-500 mt-1 flex items-center justify-between">
                          <span>{new Date(item.timestamp || item.date || Date.now()).toLocaleDateString()}</span>
                          <div className="flex space-x-1">
                            <button className="p-1 hover:bg-white rounded" title="Edit">
                              <Edit className="w-3 h-3" />
                            </button>
                            <button className="p-1 hover:bg-white rounded" title="Delete">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Recent Activity Section */}
        <div className="grid lg:grid-cols-2 gap-8">
          <div className="card-elegant p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-beyond-black">Recent Projects</h3>
              <button 
                onClick={() => setDashboardView('projects')}
                className="text-sm text-blue-600 hover:text-blue-800 transition-colors"
              >
                View All
              </button>
            </div>
            <div className="space-y-4">
              {projects.slice(0, 3).map(project => (
                <div key={project.id} className="group flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                  <div className="flex-1">
                    <div className="font-medium text-beyond-black group-hover:text-beyond-charcoal transition-colors">{project.title}</div>
                    <div className="text-sm text-beyond-gray-600">{project.client}</div>
                    </div>
                  <div className="flex items-center space-x-2">
                    <div className={`px-2 py-1 rounded text-xs font-medium ${
                      project.status === 'Active' ? 'bg-green-100 text-green-800' :
                      project.status === 'Completed' ? 'bg-blue-100 text-blue-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {project.status}
                  </div>
                    <button
                      className="opacity-0 group-hover:opacity-100 p-1 hover:bg-white rounded transition-all"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingProject(project.id);
                      }}
                    >
                      <Edit className="w-3 h-3 text-gray-600" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="card-elegant p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-beyond-black">Upcoming Events</h3>
              <button 
                onClick={() => setDashboardView('calendar')}
                className="text-sm text-blue-600 hover:text-blue-800 transition-colors"
              >
                View All
              </button>
            </div>
            <div className="space-y-4">
              {calendarEvents.slice(0, 3).map(event => (
                <div key={event.id} className="group flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                  <Calendar className="text-beyond-gray-600 flex-shrink-0" size={16} />
                  <div className="flex-1">
                    <div className="font-medium text-beyond-black group-hover:text-beyond-charcoal transition-colors">{event.title}</div>
                    <div className="text-sm text-beyond-gray-600">{event.date} at {event.time}</div>
                  </div>
                  <button
                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-white rounded transition-all"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Send calendar invite via email
                      const subject = `Calendar Invite: ${event.title}`;
                      const body = `Event: ${event.title}\nDate: ${event.date}\nTime: ${event.time}\nLocation: ${event.location || 'TBD'}\n\nDescription: ${(event as any).description || 'No description provided'}`;
                      sendEmail('', subject, body);
                    }}
                  >
                    <Send className="w-3 h-3 text-gray-600" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Advanced Analytics Dashboard Component
  const AnalyticsView = () => {
    const [analyticsTimeframe, setAnalyticsTimeframe] = useState('YTD');
    const [selectedMetric, setSelectedMetric] = useState('performance');
    const aiRecommendations = generateAIRecommendations();
    const marketSentiment = getMarketSentiment();

    return (
      <div className="space-y-8">
        {/* Real-time Market Dashboard */}
        <div className="bg-gradient-to-br from-beyond-black via-beyond-dark to-beyond-charcoal text-white rounded-2xl p-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-3xl font-display font-bold mb-2">Advanced Analytics</h1>
                <p className="text-gray-300">Real-time market data and portfolio insights</p>
          </div>
              <div className="flex items-center space-x-4">
                <div className={`px-3 py-1 rounded-full text-sm ${marketSentiment.color === 'green' ? 'bg-green-500' : marketSentiment.color === 'yellow' ? 'bg-yellow-500' : 'bg-red-500'}`}>
                  Market: {marketSentiment.sentiment.toUpperCase()}
              </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-sm">Live Data</span>
              </div>
            </div>
          </div>
          
            {/* Real-time Market Indices */}
            <div className="grid md:grid-cols-4 gap-6">
              {Object.entries(marketData).map(([key, data]) => (
                <div key={key} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 hover:bg-white/20 transition-colors">
                  <h3 className="text-sm text-gray-300 uppercase tracking-wide">{key.replace('sp500', 'S&P 500').replace('nasdaq', 'NASDAQ').replace('vix', 'VIX').replace('bonds', '10Y Treasury')}</h3>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xl font-bold">{data.value.toFixed(2)}</span>
                    <span className={`text-sm ${data.change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {data.change >= 0 ? '+' : ''}{data.change.toFixed(2)}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
            </div>
            
        {/* Portfolio Performance Overview */}
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="card-elegant p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-display font-bold text-beyond-black">Portfolio Performance</h3>
                <div className="flex space-x-2">
                  {['1M', '3M', 'YTD', '1Y', '3Y'].map(period => (
              <button 
                      key={period}
                      onClick={() => setAnalyticsTimeframe(period)}
                      className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                        analyticsTimeframe === period 
                          ? 'bg-beyond-black text-white' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {period}
              </button>
                  ))}
            </div>
        </div>
        
              {/* Performance Chart Simulation */}
              <div className="h-64 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 relative overflow-hidden">
                <div className="absolute inset-0 flex items-end justify-around p-6">
                  {Array.from({ length: 12 }, (_, i) => (
                    <div
                      key={i}
                      className="bg-gradient-to-t from-blue-600 to-blue-400 rounded-t"
                  style={{ 
                        height: `${Math.random() * 80 + 20}%`,
                        width: '20px',
                        animationDelay: `${i * 100}ms`
                      }}
                    />
                  ))}
              </div>
                <div className="relative z-10 text-center pt-20">
                  <div className="text-3xl font-bold text-beyond-black">+{analyticsData.avgClientROI}%</div>
                  <div className="text-gray-600">Average Portfolio Return ({analyticsTimeframe})</div>
            </div>
              </div>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="space-y-6">
            <div className="card-elegant p-6">
              <h4 className="font-medium text-beyond-black mb-4">Assets Under Management</h4>
              <div className="text-3xl font-bold text-beyond-black">${(analyticsData.totalAUM / 1000000).toFixed(0)}M</div>
              <div className="text-sm text-green-600 mt-1">+{analyticsData.monthlyGrowth}% this month</div>
            </div>

            <div className="card-elegant p-6">
              <h4 className="font-medium text-beyond-black mb-4">Active Clients</h4>
              <div className="text-3xl font-bold text-beyond-black">{analyticsData.activeClients}</div>
              <div className="text-sm text-gray-600 mt-1">Across all service lines</div>
            </div>

            <div className="card-elegant p-6">
              <h4 className="font-medium text-beyond-black mb-4">Risk Score</h4>
              <div className="flex items-center">
                <div className="text-3xl font-bold text-beyond-black mr-3">{analyticsData.riskScore.toFixed(1)}</div>
                <div className="text-sm text-yellow-600">Moderate</div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                <div 
                  className="bg-gradient-to-r from-green-500 to-yellow-500 h-2 rounded-full transition-all duration-1000"
                  style={{ width: `${(analyticsData.riskScore / 5) * 100}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* AI Recommendations */}
        {aiRecommendations.length > 0 && (
          <div className="card-elegant p-8">
            <div className="flex items-center mb-6">
              <Database className="w-6 h-6 text-blue-600 mr-3" />
              <h3 className="text-2xl font-display font-bold text-beyond-black">AI-Powered Recommendations</h3>
            </div>
            <div className="space-y-4">
              {aiRecommendations.map((rec, index) => (
                <div key={index} className={`border-l-4 pl-4 py-3 ${
                  rec.priority === 'high' ? 'border-red-500 bg-red-50' :
                  rec.priority === 'medium' ? 'border-yellow-500 bg-yellow-50' :
                  'border-blue-500 bg-blue-50'
                }`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-beyond-black">{rec.portfolio}</h4>
                      <p className="text-gray-700 mt-1">{rec.message}</p>
                    </div>
                    <div className="flex space-x-2">
                      <button className="btn-primary text-sm px-4 py-2">Review</button>
                      <button className="btn-secondary text-sm px-4 py-2">Dismiss</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
        </div>
      )}

        {/* System Health Monitor */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Object.entries(systemHealth).filter(([key]) => key !== 'lastUpdate').map(([service, status]) => (
            <div key={service} className="card-elegant p-6 text-center">
              <div className={`w-12 h-12 mx-auto mb-4 rounded-full flex items-center justify-center ${
                status === 'healthy' ? 'bg-green-100' : 'bg-red-100'
              }`}>
                <div className={`w-6 h-6 rounded-full ${
                  status === 'healthy' ? 'bg-green-500' : 'bg-red-500'
                } animate-pulse`} />
              </div>
              <h4 className="font-medium text-beyond-black capitalize">{service}</h4>
              <p className={`text-sm mt-1 ${status === 'healthy' ? 'text-green-600' : 'text-red-600'}`}>
                {status === 'healthy' ? 'Operational' : 'Issues Detected'}
              </p>
            </div>
          ))}
        </div>
    </div>
  );
};

  // Enhanced Tasks View Component
  const TasksView = () => {
    const [taskFilter, setTaskFilter] = useState('all');
    const [newTask, setNewTask] = useState({ title: '', description: '', priority: 'medium', dueDate: '', assignee: currentUser?.name });

    // Show loading state
    if (tasksLoading) {
      return (
        <div className="space-y-8">
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <RefreshCw className="animate-spin mx-auto mb-4 text-beyond-black" size={48} />
              <p className="text-gray-600">Loading tasks...</p>
            </div>
          </div>
        </div>
      );
    }

    const filteredTasks = tasks.filter(task => {
      if (taskFilter === 'all') return true;
      if (taskFilter === 'pending') return task.status === 'pending';
      if (taskFilter === 'in-progress') return task.status === 'in-progress';
      if (taskFilter === 'completed') return task.status === 'completed';
      if (taskFilter === 'overdue') return new Date(task.dueDate) < new Date() && task.status !== 'completed';
      return true;
    });

    const addTask = async () => {
      if (newTask.title.trim()) {
        try {
          const taskData = {
            title: newTask.title,
            description: newTask.description || '',
            priority: newTask.priority,
            status: 'pending',
            dueDate: newTask.dueDate || null,
            assigneeId: currentUser?.id || null,
            projectId: null,
            tags: []
          };

          const response = await api.createTask(taskData);
          setTasks(prev => [response.task, ...prev]);
          setNewTask({ title: '', description: '', priority: 'medium', dueDate: '', assignee: currentUser?.name });
          addNotification('Task created successfully', 'success');
        } catch (error) {
          console.error('Create task error:', error);
          addNotification('Failed to create task', 'error');
        }
      }
    };

    const updateTaskStatus = async (taskId, status) => {
      try {
        await api.updateTask(taskId, {
          status,
          completedAt: status === 'completed' ? new Date().toISOString() : null
        });
        setTasks(prev => prev.map(task =>
          task.id === taskId
            ? { ...task, status, completedAt: status === 'completed' ? new Date().toISOString() : null }
            : task
        ));
        addNotification(`Task marked as ${status}`, 'success');
      } catch (error) {
        console.error('Update task error:', error);
        addNotification('Failed to update task', 'error');
      }
    };

    const deleteTask = async (taskId) => {
      try {
        await api.deleteTask(taskId);
        setTasks(prev => prev.filter(task => task.id !== taskId));
        addNotification('Task deleted successfully', 'success');
      } catch (error) {
        console.error('Delete task error:', error);
        addNotification('Failed to delete task', 'error');
      }
    };

    return (
      <div className="space-y-8">
        {/* Header Section */}
        <div className="bg-gradient-to-br from-beyond-black via-beyond-dark to-beyond-charcoal text-white rounded-2xl p-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full blur-3xl"></div>
          <div className="relative z-10">
            <h1 className="text-3xl font-display font-bold mb-4">Task Management</h1>
            <p className="text-gray-300 text-lg">Organize and track your daily tasks and project deliverables</p>
            <div className="flex items-center mt-6 space-x-6">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
                <span className="text-sm text-gray-300">{tasks.filter(t => t.status === 'in-progress').length} In Progress</span>
        </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                <span className="text-sm text-gray-300">{tasks.filter(t => t.status === 'pending').length} Pending</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                <span className="text-sm text-gray-300">{tasks.filter(t => t.status === 'completed').length} Completed</span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Add Task */}
        <div className="card-elegant p-6">
          <h3 className="text-xl font-display font-bold mb-4 text-beyond-black">Quick Add Task</h3>
          <div className="grid lg:grid-cols-4 gap-4">
                    <input 
                      type="text" 
              placeholder="Task title *"
              value={newTask.title}
              onChange={(e) => setNewTask(prev => ({ ...prev, title: e.target.value }))}
              className="focus-ring px-4 py-3 border border-gray-300 rounded-lg"
            />
                      <input 
              type="date"
              value={newTask.dueDate}
              onChange={(e) => setNewTask(prev => ({ ...prev, dueDate: e.target.value }))}
              className="focus-ring px-4 py-3 border border-gray-300 rounded-lg"
            />
                      <select 
              value={newTask.priority}
              onChange={(e) => setNewTask(prev => ({ ...prev, priority: e.target.value }))}
              className="focus-ring px-4 py-3 border border-gray-300 rounded-lg"
            >
              <option value="low">Low Priority</option>
              <option value="medium">Medium Priority</option>
              <option value="high">High Priority</option>
                      </select>
            <button
              onClick={addTask}
              className="bg-beyond-black text-white px-6 py-3 rounded-lg hover:bg-beyond-charcoal transition-colors flex items-center justify-center space-x-2"
            >
              <Plus size={16} />
              <span>Add Task</span>
            </button>
                    </div>
          {newTask.title && (
            <div className="mt-4">
                    <textarea 
                placeholder="Task description (optional)"
                value={newTask.description}
                onChange={(e) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
                className="focus-ring w-full px-4 py-3 border border-gray-300 rounded-lg resize-none"
                rows={2}
              />
                    </div>
                  )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4">
          {[
            { id: 'all', label: 'All Tasks', count: tasks.length },
            { id: 'pending', label: 'Pending', count: tasks.filter(t => t.status === 'pending').length },
            { id: 'in-progress', label: 'In Progress', count: tasks.filter(t => t.status === 'in-progress').length },
            { id: 'completed', label: 'Completed', count: tasks.filter(t => t.status === 'completed').length },
            { id: 'overdue', label: 'Overdue', count: tasks.filter(t => new Date(t.dueDate) < new Date() && t.status !== 'completed').length }
          ].map(filter => (
            <button
              key={filter.id}
              onClick={() => setTaskFilter(filter.id)}
              className={`px-4 py-2 rounded-lg transition-all flex items-center space-x-2 ${
                taskFilter === filter.id
                  ? 'bg-beyond-black text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
              }`}
            >
              <span>{filter.label}</span>
              <span className={`text-xs px-2 py-1 rounded-full ${
                taskFilter === filter.id ? 'bg-white/20' : 'bg-gray-200'
              }`}>
                {filter.count}
              </span>
            </button>
          ))}
              </div>
              
        {/* Tasks Grid */}
        <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredTasks.map(task => (
            <div key={task.id} className="card-elegant p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h4 className="font-medium text-beyond-black mb-2">{task.title}</h4>
                  <p className="text-sm text-gray-600 leading-relaxed">{task.description}</p>
                </div>
                <div className="flex items-center space-x-2 ml-4">
                <button 
                    onClick={() => {
                      const subject = `Task Update: ${task.title}`;
                      const body = `Task: ${task.title}\nDescription: ${task.description}\nStatus: ${task.status}\nDue Date: ${task.dueDate}\nPriority: ${task.priority}`;
                      sendEmail('', subject, body);
                    }}
                    className="p-1 hover:bg-gray-100 rounded"
                    title="Email task details"
                  >
                    <Mail className="w-4 h-4 text-gray-600" />
                </button>
                <button 
                    onClick={() => deleteTask(task.id)}
                    className="p-1 hover:bg-gray-100 rounded"
                    title="Delete task"
                  >
                    <Trash2 className="w-4 h-4 text-gray-600" />
                </button>
              </div>
            </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Due Date:</span>
                  <span className={`font-medium ${
                    new Date(task.dueDate) < new Date() && task.status !== 'completed' 
                      ? 'text-red-600' 
                      : 'text-gray-700'
                  }`}>
                    {new Date(task.dueDate).toLocaleDateString()}
                    </span>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Priority:</span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    task.priority === 'high' ? 'bg-red-100 text-red-800' :
                    task.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Status:</span>
                  <select
                    value={task.status}
                    onChange={(e) => updateTaskStatus(task.id, e.target.value)}
                    className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-beyond-black"
                  >
                    <option value="pending">Pending</option>
                    <option value="in-progress">In Progress</option>
                    <option value="completed">Completed</option>
                  </select>
              </div>

                {task.project && (
                  <div className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">
                    Project: {task.project}
                  </div>
                )}
                </div>
                      </div>
                    ))}
                  </div>

        {filteredTasks.length === 0 && (
          <div className="text-center py-12">
            <CheckSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-600 mb-2">No tasks found</h3>
            <p className="text-gray-500">Try adjusting your filters or create a new task</p>
                </div>
        )}
              </div>
    );
  };

  // Projects View Component
  const ProjectsView = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');

    // Show loading state
    if (projectsLoading) {
      return (
        <div className="p-8">
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <RefreshCw className="animate-spin mx-auto mb-4 text-beyond-black" size={48} />
              <p className="text-gray-600">Loading projects...</p>
            </div>
          </div>
        </div>
      );
    }

    const filteredProjects = useMemo(() => {
      return projects.filter(project => {
        const matchesSearch = project.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                              project.client?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                              project.type?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesStatus = statusFilter === 'All' || project.status === statusFilter;
        return matchesSearch && matchesStatus;
      });
    }, [projects, searchQuery, statusFilter]);
    
    return (
      <div className="p-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-display font-bold text-beyond-black">Projects</h1>
                        {(userRole === 'admin' || userRole === 'employee') && (
                                <button 
              onClick={() => setShowProjectModal(true)}
              className="btn-primary flex items-center space-x-2"
            >
              <Plus size={20} />
              <span>New Project</span>
                                </button>
          )}
        </div>

        {/* Search and Filter Bar */}
        <div className="flex space-x-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search projects by title, client, or type..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-beyond-black focus:border-transparent"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-beyond-black focus:border-transparent"
          >
            <option value="All">All Status</option>
            <option value="Active">Active</option>
            <option value="Planning">Planning</option>
            <option value="On Hold">On Hold</option>
            <option value="Completed">Completed</option>
          </select>
        </div>

      <div className="grid gap-6">
        {filteredProjects.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <Search size={48} className="mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-500 mb-2">No projects found</h3>
            <p className="text-gray-400">Try adjusting your search criteria or create a new project.</p>
          </div>
        ) : (
          filteredProjects.map(project => (
          <div key={project.id} className="card-elegant p-6">
            <div className="flex justify-between items-start mb-4">
                              <div>
                <h3 className="text-xl font-medium text-beyond-black mb-1">{project.title}</h3>
                <p className="text-beyond-gray-600">{project.client}</p>
                                </div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                project.status === 'Active' ? 'bg-green-100 text-green-800' :
                project.status === 'Completed' ? 'bg-blue-100 text-blue-800' :
                'bg-yellow-100 text-yellow-800'
              }`}>
                {project.status}
                              </span>
                            </div>

            <div className="mb-4">
              <div className="flex justify-between text-sm text-beyond-gray-600 mb-2">
                <span>Progress</span>
                <span>{project.progress}%</span>
                          </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-beyond-black h-2 rounded-full transition-all duration-300"
                  style={{ width: `${project.progress}%` }}
                ></div>
                    </div>
                  </div>

            <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                <span className="text-beyond-gray-600">Type: </span>
                <span className="text-beyond-black font-medium">{project.type}</span>
              </div>
                              <div>
                <span className="text-beyond-gray-600">Deadline: </span>
                <span className="text-beyond-black font-medium">{project.deadline}</span>
                              </div>
              <div>
                <span className="text-beyond-gray-600">Last Update: </span>
                <span className="text-beyond-black font-medium">{project.lastUpdate}</span>
            </div>
          </div>
        </div>
          ))
        )}
      </div>
    </div>
    );
  };

  // Messages View Component
  const MessagesView = () => {
    // Show loading state
    if (messagesLoading) {
      return (
        <div className="p-8">
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <RefreshCw className="animate-spin mx-auto mb-4 text-beyond-black" size={48} />
              <p className="text-gray-600">Loading messages...</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="p-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-display font-bold text-beyond-black">Messages</h1>
          <button
            onClick={() => setShowComposeModal(true)}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus size={20} />
            <span>Compose</span>
          </button>
        </div>

        <div className="space-y-4">
          {messages.map(message => (
          <div key={message.id} className={`card-elegant p-6 ${!message.read ? 'border-l-4 border-l-beyond-black' : ''}`}>
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <h3 className="font-medium text-beyond-black mb-1">{message.subject}</h3>
                <p className="text-sm text-beyond-gray-600">From: {message.senderName}</p>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleReplyToMessage(message)}
                  className="p-2 hover:bg-gray-100 rounded transition-colors flex items-center space-x-1"
                  title="Reply to message"
                >
                  <Reply size={16} className="text-gray-600" />
                  <span className="text-sm text-gray-600">Reply</span>
                </button>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  message.priority === 'high' ? 'bg-red-100 text-red-800' :
                  message.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-green-100 text-green-800'
                }`}>
                  {message.priority}
                </span>
              </div>
            </div>
            <p className="text-beyond-gray-700">{message.body}</p>
          </div>
          ))}
        </div>
      </div>
    );
  };

  // Calendar View Component
  const CalendarView = () => (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-display font-bold text-beyond-black">Calendar</h1>
        <button
          onClick={() => setShowCalendarModal(true)}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus size={20} />
          <span>New Event</span>
        </button>
      </div>

      <div className="grid gap-6">
        {calendarEvents.map(event => (
          <div key={event.id} className="card-elegant p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-medium text-beyond-black mb-1">{event.title}</h3>
                <div className="text-beyond-gray-600">
                  <p>{event.date} at {event.time}</p>
                  <p>{event.location}</p>
              </div>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                event.type === 'Meeting' ? 'bg-blue-100 text-blue-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {event.type}
                              </span>
                            </div>
            <div>
              <span className="text-beyond-gray-600">Attendees: </span>
              <span className="text-beyond-black">{event.attendees.join(', ')}</span>
                          </div>
                            </div>
        ))}
                        </div>
                      </div>
  );

  // Contracts View Component
  const ContractsView = () => {
    // Show loading state
    if (contractsLoading) {
      return (
        <div className="p-8">
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <RefreshCw className="animate-spin mx-auto mb-4 text-beyond-black" size={48} />
              <p className="text-gray-600">Loading contracts...</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="p-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-display font-bold text-beyond-black">Contracts</h1>
        </div>

        <div className="grid gap-6">
          {contracts.map(contract => (
          <div key={contract.id} className="card-elegant p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-medium text-beyond-black mb-1">{contract.title}</h3>
                <p className="text-beyond-gray-600">Value: {contract.value}</p>
                      </div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                contract.status === 'Active' ? 'bg-green-100 text-green-800' :
                'bg-yellow-100 text-yellow-800'
              }`}>
                {contract.status}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
    );
  };

  // Documents View Component
  const DocumentsView = () => {
    const [docCategory, setDocCategory] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');

    // Client documents - will be loaded from backend
    const [clientDocuments, setClientDocuments] = useState([]);
    const [documentsLoading, setDocumentsLoading] = useState(false);

    const filteredDocuments = clientDocuments.filter(doc => {
      const matchesCategory = docCategory === 'all' || doc.category === docCategory;
      const matchesSearch = doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           doc.description.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });

    const getFileIcon = (type) => {
      switch (type) {
        case 'pdf': return <FileText className="text-red-500" size={24} />;
        case 'docx': return <File className="text-blue-500" size={24} />;
        case 'pptx': return <Image className="text-orange-500" size={24} />;
        case 'zip': return <Archive className="text-purple-500" size={24} />;
        default: return <File className="text-gray-500" size={24} />;
      }
    };

    return (
      <div className="space-y-8">
        {/* Header */}
        <div className="bg-gradient-to-br from-beyond-black via-beyond-charcoal to-beyond-dark text-white rounded-2xl p-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl"></div>
          <div className="relative z-10">
            <h1 className="text-3xl font-display font-bold mb-4">Document Library</h1>
            <p className="text-gray-300 text-lg">Access your investment documents, reports, and files</p>
            <div className="flex items-center mt-6 space-x-6">
              <div className="flex items-center space-x-2">
                <Folder className="text-blue-400" size={20} />
                <span className="text-sm">{clientDocuments.length} Documents</span>
              </div>
              <div className="flex items-center space-x-2">
                <Shield className="text-green-400" size={20} />
                <span className="text-sm">{clientDocuments.filter(d => d.confidential).length} Confidential</span>
              </div>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-beyond-black focus:border-transparent"
            />
          </div>
          <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
            {[
              { id: 'all', label: 'All' },
              { id: 'reports', label: 'Reports' },
              { id: 'presentations', label: 'Presentations' },
              { id: 'due-diligence', label: 'Due Diligence' },
              { id: 'tax', label: 'Tax Docs' },
              { id: 'compliance', label: 'Compliance' }
            ].map(category => (
              <button
                key={category.id}
                onClick={() => setDocCategory(category.id)}
                className={`px-4 py-2 rounded-lg transition-all ${
                  docCategory === category.id
                    ? 'bg-white text-beyond-black shadow-sm'
                    : 'text-gray-600 hover:text-beyond-black'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>

        {/* Documents Grid */}
        <div className="grid gap-4">
          {filteredDocuments.map(document => (
            <div key={document.id} className="card-elegant p-6 card-hover">
              <div className="flex justify-between items-start">
                <div className="flex items-start space-x-4 flex-1">
                  <div className="flex-shrink-0">
                    {getFileIcon(document.type)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h3 className="text-lg font-semibold text-beyond-black">{document.name}</h3>
                      {document.confidential && (
                        <Lock className="text-red-500" size={16} />
                      )}
                    </div>
                    <p className="text-gray-600 mt-1">{document.description}</p>
                    <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                      <span>{new Date(document.date).toLocaleDateString()}</span>
                      <span>{document.size}</span>
                      <span className="capitalize bg-gray-100 px-2 py-1 rounded text-xs">
                        {document.category.replace('-', ' ')}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button className="p-2 hover:bg-gray-100 rounded transition-colors" title="Download">
                    <Download size={16} className="text-gray-600" />
                  </button>
                  <button className="p-2 hover:bg-gray-100 rounded transition-colors" title="Share">
                    <ExternalLink size={16} className="text-gray-600" />
                  </button>
                  <button className="p-2 hover:bg-gray-100 rounded transition-colors" title="View">
                    <Eye size={16} className="text-gray-600" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Document Request Section */}
        <div className="card-elegant p-6">
          <h3 className="text-xl font-bold mb-4 text-beyond-black">Request Documents</h3>
          <p className="text-gray-600 mb-4">Need a specific document or report? Let us know what you're looking for.</p>
          <div className="flex space-x-4">
            <input
              type="text"
              placeholder="Describe the document you need..."
              className="flex-1 form-input"
            />
            <button className="btn-primary">Send Request</button>
          </div>
        </div>
      </div>
    );
  };

  // AI Assistant View Component (Employee/Admin only)
  const AIAssistantView = () => {
    const [aiMode, setAiMode] = useState('chat');
    const [aiContext, setAiContext] = useState('general');
    const [aiHistory, setAiHistory] = useState([]);
    const [selectedProjects, setSelectedProjects] = useState([]);
    const [selectedContacts, setSelectedContacts] = useState([]);

    // Enhanced AI capabilities with context awareness
    const contextOptions = [
      { id: 'general', label: 'General Advisory', icon: Bot },
      { id: 'portfolio', label: 'Portfolio Analysis', icon: PieChart },
      { id: 'market', label: 'Market Research', icon: TrendingUp },
      { id: 'due-diligence', label: 'Due Diligence', icon: Shield },
      { id: 'compliance', label: 'Compliance Check', icon: CheckSquare },
      { id: 'reporting', label: 'Report Generation', icon: FileText }
    ];

    const aiModes = [
      { id: 'chat', label: 'Chat Assistant', icon: MessageSquare },
      { id: 'analysis', label: 'Data Analysis', icon: BarChart3 },
      { id: 'document', label: 'Document Creator', icon: Edit },
      { id: 'strategy', label: 'Strategy Advisor', icon: Target }
    ];

    const enhancedPrompts = {
      portfolio: [
        'Analyze portfolio diversification and suggest rebalancing strategies',
        'Evaluate current asset allocation against market conditions',
        'Identify potential risk factors in current holdings',
        'Compare portfolio performance to relevant benchmarks'
      ],
      market: [
        'Research emerging market trends in private equity',
        'Analyze macroeconomic factors affecting real estate investments',
        'Evaluate cryptocurrency and digital asset opportunities',
        'Assess ESG investment landscape and opportunities'
      ],
      'due-diligence': [
        'Create comprehensive due diligence framework for tech startups',
        'Analyze regulatory risks for healthcare investments',
        'Evaluate management team capabilities and track record',
        'Assess competitive landscape and market positioning'
      ]
    };

    const handleEnhancedAiQuery = async () => {
      if (!aiQuery.trim()) return;
      
      setAiLoading(true);
      
      // Enhanced context building
      const contextData = {
        mode: aiMode,
        context: aiContext,
        projects: selectedProjects.length > 0 ? selectedProjects : projects.slice(0, 3),
        contacts: selectedContacts.length > 0 ? selectedContacts : [],
        portfolio: portfolios[0] || null,
        messages: messages.slice(0, 5),
        user: currentUser
      };

      // Simulate enhanced AI processing with context
      setTimeout(() => {
        let response = '';

        if (aiMode === 'analysis') {
          response = generateAnalysisResponse(aiQuery, contextData);
        } else if (aiMode === 'document') {
          response = generateDocumentResponse(aiQuery, contextData);
        } else if (aiMode === 'strategy') {
          response = generateStrategyResponse(aiQuery, contextData);
        } else {
          response = generateChatResponse(aiQuery, contextData);
        }

        setAiResponse(response);
        
        // Add to history
        const historyEntry = {
          id: Date.now(),
          query: aiQuery,
          response,
          mode: aiMode,
          context: aiContext,
          timestamp: new Date().toISOString(),
          contextData: JSON.stringify(contextData, null, 2)
        };
        
        setAiHistory(prev => [historyEntry, ...prev.slice(0, 9)]);
        setAiQuery('');
        setAiLoading(false);
      }, 2000);
    };

    const generateAnalysisResponse = (query, context) => {
      return `**AI Data Analysis Report**

Based on your query: "${query}"

**Key Findings:**
• Portfolio Value: $${context.portfolio?.totalValue?.toLocaleString() || 'N/A'}
• Active Projects: ${context.projects.length} projects under management
• Recent Activity: ${context.messages.length} recent interactions analyzed

**Recommendations:**
1. Continue monitoring market conditions for optimal rebalancing opportunities
2. Consider diversification strategies based on current economic indicators
3. Implement enhanced risk management protocols

**Next Steps:**
• Schedule quarterly portfolio review
• Update risk assessment models
• Consider ESG integration opportunities

*Analysis generated on ${new Date().toLocaleDateString()} by Beyond26 AI Advisory Suite*`;
    };

    const generateDocumentResponse = (query, context) => {
      return `**Document Generated by AI**

**Title:** ${query.includes('report') ? 'Investment Analysis Report' : 
                 query.includes('contract') ? 'Investment Agreement' : 
                 'Strategic Document'}

**Executive Summary:**
This document has been generated based on your current portfolio data and project information. It incorporates analysis from ${context.projects.length} active projects and current market conditions.

**Key Sections:**
1. **Current Portfolio Status**
   - Total AUM: $${context.portfolio?.totalValue?.toLocaleString() || 'TBD'}
   - Performance Metrics: Available in detailed analysis
   
2. **Project Overview**
${context.projects.map(p => `   - ${p.title}: ${p.status}`).join('\n')}

3. **Recommendations**
   - Diversification strategies
   - Risk mitigation approaches
   - Growth opportunities

**Document prepared by Beyond26 AI on ${new Date().toLocaleDateString()}**

*This document is confidential and prepared specifically for Beyond26 stakeholders.*`;
    };

    const generateStrategyResponse = (query, context) => {
      return `**Strategic Advisory Response**

**Strategic Context:** ${query}

**Current Position Analysis:**
• Portfolio Composition: Diversified across ${context.portfolio ? '5+ asset classes' : 'multiple sectors'}
• Active Engagements: ${context.projects.length} strategic initiatives
• Market Positioning: Strong presence in target sectors

**Strategic Recommendations:**

**Short-term (3-6 months):**
1. Optimize current portfolio allocation based on market volatility
2. Enhance due diligence processes for new opportunities
3. Strengthen client relationship management systems

**Medium-term (6-18 months):**
1. Explore emerging technology investments (AI, quantum computing)
2. Expand ESG-focused investment strategies
3. Develop strategic partnerships in key markets

**Long-term (18+ months):**
1. Position for next market cycle opportunities
2. Build institutional-grade infrastructure
3. Establish thought leadership in alternative investments

**Implementation Priorities:**
• High: Market positioning and risk management
• Medium: Technology integration and process optimization
• Low: Exploratory research and development

*Strategy developed by Beyond26 AI Advisory Suite - ${new Date().toLocaleDateString()}*`;
    };

    const generateChatResponse = (query, context) => {
      if (query.toLowerCase().includes('portfolio')) {
        return `Based on your current portfolio data, I can see you have strong diversification across multiple asset classes. Your ${context.projects.length} active projects show healthy deal flow. The current market conditions suggest maintaining your strategic allocation while being prepared for tactical adjustments. Would you like me to dive deeper into any specific aspect of your portfolio performance?`;
      }
      
      if (query.toLowerCase().includes('market')) {
        return `Current market analysis indicates mixed signals across asset classes. Private equity remains robust with selective opportunities, while public markets show increased volatility. Your portfolio positioning appears well-suited for current conditions. I recommend maintaining a defensive posture while staying alert for opportunistic investments. What specific market segment interests you most?`;
      }
      
      return `I understand you're asking about "${query}". Based on your current Beyond26 portfolio and ${context.projects.length} active projects, I can provide tailored investment insights. Your diversified approach across private equity, real estate, and alternative investments positions you well for various market conditions. Would you like me to focus on any particular aspect of this topic?`;
    };

    return (
      <div className="space-y-8">
        {/* Header */}
        <div className="bg-gradient-to-br from-beyond-black via-beyond-charcoal to-beyond-dark text-white rounded-2xl p-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl"></div>
          <div className="relative z-10">
            <h1 className="text-3xl font-display font-bold mb-4">AI Investment Assistant</h1>
            <p className="text-gray-300 text-lg">Advanced AI-powered investment analysis and advisory services</p>
            <div className="flex items-center mt-6 space-x-6">
              <div className="flex items-center space-x-2">
                <Brain className="text-purple-400" size={20} />
                <span className="text-sm">Advanced Analytics</span>
              </div>
              <div className="flex items-center space-x-2">
                <Database className="text-blue-400" size={20} />
                <span className="text-sm">Portfolio Integration</span>
              </div>
              <div className="flex items-center space-x-2">
                <Zap className="text-yellow-400" size={20} />
                <span className="text-sm">Real-time Insights</span>
              </div>
            </div>
          </div>
        </div>

        {/* AI Controls */}
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="form-label">AI Mode</label>
              <div className="grid grid-cols-2 gap-2">
                {aiModes.map(mode => (
                  <button
                    key={mode.id}
                    onClick={() => setAiMode(mode.id)}
                    className={`flex items-center space-x-2 p-3 rounded-lg transition-all ${
                      aiMode === mode.id 
                        ? 'bg-beyond-black text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <mode.icon size={16} />
                    <span className="text-sm">{mode.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="form-label">Context Focus</label>
              <div className="grid grid-cols-2 gap-2">
                {contextOptions.map(option => (
                  <button
                    key={option.id}
                    onClick={() => setAiContext(option.id)}
                    className={`flex items-center space-x-2 p-3 rounded-lg transition-all ${
                      aiContext === option.id 
                        ? 'bg-beyond-black text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <option.icon size={16} />
                    <span className="text-sm">{option.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* AI Query Interface */}
        <div className="card-elegant p-6">
          <h3 className="text-xl font-bold mb-4 text-beyond-black">AI Query Interface</h3>
          <div className="space-y-4">
            <div className="flex space-x-4">
              <input
                type="text"
                placeholder={`Ask your AI ${aiMode} assistant about investment strategies, analysis, or advice...`}
                value={aiQuery}
                onChange={(e) => setAiQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleEnhancedAiQuery()}
                className="flex-1 form-input"
              />
              <button 
                onClick={handleEnhancedAiQuery}
                disabled={aiLoading || !aiQuery.trim()}
                className="btn-primary flex items-center space-x-2 disabled:opacity-50"
              >
                {aiLoading ? <RefreshCw className="animate-spin" size={20} /> : <Send size={20} />}
                <span>{aiLoading ? 'Processing...' : 'Ask AI'}</span>
              </button>
            </div>

            {/* Contextual Prompt Suggestions */}
            {enhancedPrompts[aiContext] && (
              <div className="space-y-2">
                <p className="text-sm text-gray-600">Quick prompts for {aiContext}:</p>
                <div className="grid md:grid-cols-2 gap-2">
                  {enhancedPrompts[aiContext].map((prompt, index) => (
                    <button
                      key={index}
                      onClick={() => setAiQuery(prompt)}
                      className="text-left p-2 text-sm text-gray-700 hover:bg-gray-50 rounded transition-colors border border-gray-200"
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* AI Response */}
        {aiResponse && (
          <div className="card-elegant p-6">
            <h3 className="text-xl font-bold mb-4 text-beyond-black flex items-center space-x-2">
              <Brain className="text-purple-600" size={24} />
              <span>AI Response ({aiMode} mode)</span>
            </h3>
            <div className="prose prose-gray max-w-none">
              <pre className="whitespace-pre-wrap text-gray-700 font-sans leading-relaxed">{aiResponse}</pre>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between text-sm text-gray-500">
                <span>Context: {aiContext} | Mode: {aiMode}</span>
                <button className="flex items-center space-x-1 hover:text-gray-700">
                  <Copy size={14} />
                  <span>Copy Response</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* AI History */}
        {aiHistory.length > 0 && (
          <div className="card-elegant p-6">
            <h3 className="text-xl font-bold mb-4 text-beyond-black">Recent AI Interactions</h3>
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {aiHistory.slice(0, 5).map(interaction => (
                <div key={interaction.id} className="border-l-4 border-purple-500 pl-4 py-2">
                  <div className="font-medium text-gray-900">{interaction.query}</div>
                  <div className="text-sm text-gray-600 mt-1 line-clamp-2">
                    {interaction.response.substring(0, 150)}...
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-gray-500">
                      {new Date(interaction.timestamp).toLocaleString()} • {interaction.mode} mode
                    </span>
                    <button 
                      onClick={() => setAiQuery(interaction.query)}
                      className="text-xs text-purple-600 hover:text-purple-800"
                    >
                      Rerun Query
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* AI Capabilities and Features */}
        <div className="grid lg:grid-cols-2 gap-8">
          <div className="card-elegant p-6">
            <h3 className="text-xl font-bold mb-4 text-beyond-black flex items-center space-x-2">
              <Lightbulb className="text-yellow-500" size={20} />
              <span>Enhanced AI Capabilities</span>
            </h3>
            <div className="space-y-3 text-sm">
              {[
                'Context-aware portfolio analysis with real-time data integration',
                'Multi-modal investment strategy development and optimization',
                'Advanced due diligence frameworks with regulatory compliance',
                'Predictive market analysis using alternative data sources',
                'ESG integration and impact measurement capabilities',
                'Real estate and infrastructure opportunity identification',
                'Private equity and venture capital deal flow analysis',
                'Risk modeling with Monte Carlo simulations',
                'Regulatory compliance monitoring and reporting',
                'Performance attribution and benchmark analysis'
              ].map((capability, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-gray-700">{capability}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="card-elegant p-6">
            <h3 className="text-xl font-bold mb-4 text-beyond-black flex items-center space-x-2">
              <Settings className="text-gray-600" size={20} />
              <span>AI Configuration</span>
            </h3>
            <div className="space-y-4">
              <div>
                <label className="form-label">AI Response Style</label>
                <select className="form-select">
                  <option>Professional & Detailed</option>
                  <option>Concise & Executive</option>
                  <option>Technical & Analytical</option>
                  <option>Conversational & Accessible</option>
                </select>
              </div>
              <div>
                <label className="form-label">Default Context</label>
                <select 
                  className="form-select"
                  value={aiContext}
                  onChange={(e) => setAiContext(e.target.value)}
                >
                  {contextOptions.map(option => (
                    <option key={option.id} value={option.id}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <input type="checkbox" id="save-history" className="rounded" />
                <label htmlFor="save-history" className="text-sm text-gray-700">
                  Save interaction history
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <input type="checkbox" id="auto-context" className="rounded" defaultChecked />
                <label htmlFor="auto-context" className="text-sm text-gray-700">
                  Automatically include portfolio context
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Team View Component (Admin only)
  const TeamView = () => (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-display font-bold text-beyond-black">Team Management</h1>
        </div>

      <div className="grid gap-6">
        {teamMembers.map(member => (
          <div key={member.id} className="card-elegant p-6">
            <div className="flex justify-between items-start">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-beyond-gray-600 rounded-full flex items-center justify-center text-white font-medium">
                  {member.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div>
                  <h3 className="text-lg font-medium text-beyond-black">{member.name}</h3>
                  <p className="text-beyond-gray-600">{member.email}</p>
                  <p className="text-sm text-beyond-gray-600">
                    {member.company ? member.company : member.title}
                </p>
              </div>
              </div>
              
              <div className="text-right">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  member.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {member.status}
                </span>
                <p className="text-sm text-beyond-gray-600 mt-2 capitalize">
                  {member.role} {member.type ? `(${member.type})` : ''}
                </p>
            </div>
          </div>
      </div>
              ))}
            </div>
            </div>
  );

  // CRM View Component (Employee/Admin only)
  const CrmView = () => {
    const [crmView, setCrmView] = useState('contacts');
    const [searchQuery, setSearchQuery] = useState('');
    const [newContact, setNewContact] = useState({
      firstName: '', lastName: '', email: '', phone: '', company: '', title: '', 
      industry: '', source: '', notes: '', tags: []
    });
    const [showNewContactForm, setShowNewContactForm] = useState(false);
    const [editingContact, setEditingContact] = useState(null);
    const [emailAccounts, setEmailAccounts] = useState([
      { id: 1, provider: 'gmail', email: 'alex@beyond26.com', connected: true, lastSync: '2025-01-08T10:30:00Z' },
      { id: 2, provider: 'outlook', email: 'alex.smith@beyond26advisors.com', connected: false, lastSync: null },
      { id: 3, provider: 'godaddy', email: 'asmith@beyond26.com', connected: true, lastSync: '2025-01-08T09:15:00Z' }
    ]);

    // CRM data states
    const [localContacts, setLocalContacts] = useState([]);
    const [crmContactsLoading, setCrmContactsLoading] = useState(false);

    const [interactions, setInteractions] = useState([]);
    const [interactionsLoading, setInteractionsLoading] = useState(false);

    const [opportunities, setOpportunities] = useState([]);
    const [opportunitiesLoading, setOpportunitiesLoading] = useState(false);

    // Load CRM data from backend on mount
    useEffect(() => {
      const loadCrmData = async () => {
        if (userRole !== 'admin' && userRole !== 'employee') return;

        try {
          setCrmContactsLoading(true);
          const contactsResponse = await api.getCrmContacts();
          setLocalContacts(contactsResponse.contacts || []);
        } catch (error) {
          console.error('Error loading CRM contacts:', error);
        } finally {
          setCrmContactsLoading(false);
        }

        try {
          setInteractionsLoading(true);
          const interactionsResponse = await api.getCrmInteractions();
          setInteractions(interactionsResponse.interactions || []);
        } catch (error) {
          console.error('Error loading CRM interactions:', error);
        } finally {
          setInteractionsLoading(false);
        }

        try {
          setOpportunitiesLoading(true);
          const opportunitiesResponse = await api.getCrmOpportunities();
          setOpportunities(opportunitiesResponse.opportunities || []);
        } catch (error) {
          console.error('Error loading CRM opportunities:', error);
        } finally {
          setOpportunitiesLoading(false);
        }
      };

      loadCrmData();
    }, [userRole]);

    const addContact = async () => {
      if (newContact.firstName && newContact.lastName && newContact.email) {
        try {
          const response = await api.createCrmContact({
            firstName: newContact.firstName,
            lastName: newContact.lastName,
            email: newContact.email,
            phone: newContact.phone,
            company: newContact.company,
            title: newContact.title,
            industry: newContact.industry,
            source: newContact.source,
            notes: newContact.notes,
            tags: newContact.tags,
            status: 'prospect'
          });

          setLocalContacts(prev => [response.contact, ...prev]);
          setCrmContacts(prev => [response.contact, ...prev]);
          setNewContact({
            firstName: '', lastName: '', email: '', phone: '', company: '', title: '',
            industry: '', source: '', notes: '', tags: []
          });
          setShowNewContactForm(false);
          addNotification('Contact added successfully', 'success');
        } catch (error) {
          console.error('Create contact error:', error);
          addNotification('Failed to add contact', 'error');
        }
      }
    };

    const connectEmailAccount = async (accountId) => {
      // Simulate OAuth flow
      addNotification('Redirecting to email provider for authentication...', 'info');
      setTimeout(() => {
        setEmailAccounts(prev => prev.map(acc => 
          acc.id === accountId 
            ? { ...acc, connected: true, lastSync: new Date().toISOString() }
            : acc
        ));
        addNotification('Email account connected successfully!', 'success');
      }, 2000);
    };

    const syncEmailAccount = async (accountId) => {
      addNotification('Syncing email account...', 'info');
      setTimeout(() => {
        setEmailAccounts(prev => prev.map(acc => 
          acc.id === accountId 
            ? { ...acc, lastSync: new Date().toISOString() }
            : acc
        ));
        addNotification('Email sync completed!', 'success');
      }, 1500);
    };

    const filteredContacts = localContacts.filter(contact =>
      contact.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.lastName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.company.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.email.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
      <div className="space-y-8">
        {/* Header */}
        <div className="bg-gradient-to-br from-beyond-black via-beyond-charcoal to-beyond-dark text-white rounded-2xl p-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl"></div>
          <div className="relative z-10">
            <h1 className="text-3xl font-display font-bold mb-4">Client Relations Management</h1>
            <p className="text-gray-300 text-lg">Manage contacts, track interactions, and nurture client relationships</p>
            <div className="flex items-center mt-6 space-x-6">
              <div className="flex items-center space-x-2">
                <Users className="text-blue-400" size={20} />
                <span className="text-sm">{localContacts.length} Total Contacts</span>
              </div>
              <div className="flex items-center space-x-2">
                <Mail className="text-green-400" size={20} />
                <span className="text-sm">{emailAccounts.filter(a => a.connected).length} Connected Accounts</span>
              </div>
            </div>
          </div>
        </div>

        {/* CRM Navigation */}
        <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
          {[
            { id: 'contacts', label: 'Contacts', icon: Users },
            { id: 'interactions', label: 'Interactions', icon: MessageSquare },
            { id: 'opportunities', label: 'Opportunities', icon: Target },
            { id: 'email-setup', label: 'Email Integration', icon: Settings }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setCrmView(tab.id)}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-lg transition-all ${
                crmView === tab.id
                  ? 'bg-white text-beyond-black shadow-sm'
                  : 'text-gray-600 hover:text-beyond-black'
              }`}
            >
              <tab.icon size={18} />
              <span className="font-medium">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Contacts View */}
        {crmView === 'contacts' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search contacts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-beyond-black focus:border-transparent"
                />
              </div>
              <button
                onClick={() => setShowNewContactForm(true)}
                className="btn-primary flex items-center space-x-2"
              >
                <Plus size={20} />
                <span>Add Contact</span>
              </button>
            </div>

            {showNewContactForm && (
              <div className="card-elegant p-6">
                <h3 className="text-xl font-bold mb-4 text-beyond-black">Add New Contact</h3>
                <div className="grid lg:grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="form-label">First Name *</label>
                    <input
                      type="text"
                      value={newContact.firstName}
                      onChange={(e) => setNewContact(prev => ({ ...prev, firstName: e.target.value }))}
                      className="form-input"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Last Name *</label>
                    <input
                      type="text"
                      value={newContact.lastName}
                      onChange={(e) => setNewContact(prev => ({ ...prev, lastName: e.target.value }))}
                      className="form-input"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      value={newContact.email}
                      onChange={(e) => setNewContact(prev => ({ ...prev, email: e.target.value }))}
                      className="form-input"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input
                      type="tel"
                      value={newContact.phone}
                      onChange={(e) => setNewContact(prev => ({ ...prev, phone: e.target.value }))}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Company</label>
                    <input
                      type="text"
                      value={newContact.company}
                      onChange={(e) => setNewContact(prev => ({ ...prev, company: e.target.value }))}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Title</label>
                    <input
                      type="text"
                      value={newContact.title}
                      onChange={(e) => setNewContact(prev => ({ ...prev, title: e.target.value }))}
                      className="form-input"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-4 mt-6">
                  <button
                    onClick={() => setShowNewContactForm(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={addContact}
                    className="btn-primary"
                  >
                    Add Contact
                  </button>
                </div>
              </div>
            )}

            <div className="grid gap-4">
              {filteredContacts.map(contact => (
                <div key={contact.id} className="card-elegant p-6 card-hover">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-beyond-black">
                        {contact.firstName} {contact.lastName}
                      </h3>
                      <p className="text-beyond-gray-600">{contact.title} at {contact.company}</p>
                      <div className="mt-2 space-y-1">
                        <p className="text-sm text-gray-600">{contact.email}</p>
                        {contact.phone && <p className="text-sm text-gray-600">{contact.phone}</p>}
                      </div>
                      <div className="flex flex-wrap gap-2 mt-3">
                        {contact.tags.map((tag, index) => (
                          <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button className="p-2 hover:bg-gray-100 rounded transition-colors">
                        <Mail size={16} className="text-gray-600" />
                      </button>
                      <button className="p-2 hover:bg-gray-100 rounded transition-colors">
                        <Phone size={16} className="text-gray-600" />
                      </button>
                      <button className="p-2 hover:bg-gray-100 rounded transition-colors">
                        <Edit size={16} className="text-gray-600" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Email Integration Setup */}
        {crmView === 'email-setup' && (
          <div className="space-y-6">
            <div className="card-elegant p-6">
              <h3 className="text-xl font-bold mb-4 text-beyond-black">Email Account Integration</h3>
              <p className="text-gray-600 mb-6">Connect your email accounts to automatically sync interactions and enable direct email communication.</p>
              
              <div className="space-y-4">
                {emailAccounts.map(account => (
                  <div key={account.id} className="border border-gray-200 rounded-lg p-4 flex justify-between items-center">
                    <div className="flex items-center space-x-4">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                        account.provider === 'gmail' ? 'bg-red-100' :
                        account.provider === 'outlook' ? 'bg-blue-100' : 'bg-orange-100'
                      }`}>
                        <Mail size={24} className={
                          account.provider === 'gmail' ? 'text-red-600' :
                          account.provider === 'outlook' ? 'text-blue-600' : 'text-orange-600'
                        } />
                      </div>
                      <div>
                        <h4 className="font-medium text-beyond-black capitalize">{account.provider}</h4>
                        <p className="text-sm text-gray-600">{account.email}</p>
                        {account.lastSync && (
                          <p className="text-xs text-gray-500">
                            Last sync: {new Date(account.lastSync).toLocaleString()}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        account.connected 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {account.connected ? 'Connected' : 'Disconnected'}
                      </span>
                      {account.connected ? (
                        <button
                          onClick={() => syncEmailAccount(account.id)}
                          className="btn-secondary text-sm"
                        >
                          <RefreshCw size={16} />
                          Sync
                        </button>
                      ) : (
                        <button
                          onClick={() => connectEmailAccount(account.id)}
                          className="btn-primary text-sm"
                        >
                          Connect
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="card-elegant p-6">
              <h3 className="text-xl font-bold mb-4 text-beyond-black">Email Integration Features</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="font-medium text-beyond-black">Automated Tracking</h4>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-center space-x-2">
                      <CheckCircle size={16} className="text-green-500" />
                      <span>Automatic email interaction logging</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle size={16} className="text-green-500" />
                      <span>Contact association and tagging</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle size={16} className="text-green-500" />
                      <span>Email thread management</span>
                    </li>
                  </ul>
                </div>
                <div className="space-y-4">
                  <h4 className="font-medium text-beyond-black">Direct Integration</h4>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-center space-x-2">
                      <CheckCircle size={16} className="text-green-500" />
                      <span>Send emails directly from CRM</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle size={16} className="text-green-500" />
                      <span>Template and signature management</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle size={16} className="text-green-500" />
                      <span>Calendar integration for meetings</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Other CRM views can be implemented similarly */}
        {crmView === 'interactions' && (
          <div className="card-elegant p-6">
            <h3 className="text-xl font-bold mb-4 text-beyond-black">Recent Interactions</h3>
            <div className="space-y-4">
              {interactions.map(interaction => (
                <div key={interaction.id} className="border-l-4 border-blue-500 pl-4 py-2">
                  <h4 className="font-medium text-beyond-black">{interaction.subject}</h4>
                  <p className="text-sm text-gray-600">{interaction.content}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {interaction.type} • {new Date(interaction.date).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {crmView === 'opportunities' && (
          <div className="card-elegant p-6">
            <h3 className="text-xl font-bold mb-4 text-beyond-black">Sales Opportunities</h3>
            <div className="space-y-4">
              {opportunities.map(opp => (
                <div key={opp.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-medium text-beyond-black">{opp.title}</h4>
                      <p className="text-sm text-gray-600">${opp.value.toLocaleString()}</p>
                      <p className="text-xs text-gray-500">Expected close: {new Date(opp.expectedClose).toLocaleDateString()}</p>
                    </div>
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs">
                      {opp.probability}% probability
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Portfolio View Component
  const PortfolioView = () => (
    <div className="p-8">
      <div className="bg-gradient-to-br from-beyond-black via-beyond-charcoal to-beyond-dark text-white rounded-2xl p-8 mb-8">
        <h1 className="text-3xl font-display font-bold mb-4">Portfolio Analysis</h1>
        <p className="text-gray-300 text-lg">Comprehensive portfolio performance and analytics</p>
      </div>
      <div className="card-elegant p-6">
        <div className="text-center py-12">
          <PieChart className="mx-auto text-beyond-gray-400 mb-4" size={48} />
          <h3 className="text-lg font-medium text-beyond-black mb-2">Portfolio Analytics</h3>
          <p className="text-beyond-gray-600">Advanced portfolio analysis and performance metrics</p>
        </div>
      </div>
    </div>
  );

  // Reports View Component
  const ReportsView = () => (
    <div className="p-8">
      <div className="bg-gradient-to-br from-beyond-black via-beyond-charcoal to-beyond-dark text-white rounded-2xl p-8 mb-8">
        <h1 className="text-3xl font-display font-bold mb-4">Reports & Analytics</h1>
        <p className="text-gray-300 text-lg">Generate comprehensive reports and insights</p>
      </div>
      <div className="card-elegant p-6">
        <div className="text-center py-12">
          <FileText className="mx-auto text-beyond-gray-400 mb-4" size={48} />
          <h3 className="text-lg font-medium text-beyond-black mb-2">Report Generation</h3>
          <p className="text-beyond-gray-600">Create detailed investment and performance reports</p>
        </div>
      </div>
    </div>
  );

  // Compliance View Component
  const ComplianceView = () => (
    <div className="p-8">
      <div className="bg-gradient-to-br from-beyond-black via-beyond-charcoal to-beyond-dark text-white rounded-2xl p-8 mb-8">
        <h1 className="text-3xl font-display font-bold mb-4">Compliance Monitor</h1>
        <p className="text-gray-300 text-lg">Regulatory compliance and risk management</p>
      </div>
      <div className="card-elegant p-6">
        <div className="text-center py-12">
          <Shield className="mx-auto text-beyond-gray-400 mb-4" size={48} />
          <h3 className="text-lg font-medium text-beyond-black mb-2">Compliance Dashboard</h3>
          <p className="text-beyond-gray-600">Monitor regulatory requirements and compliance status</p>
        </div>
      </div>
    </div>
  );

  // Deployment View Component (for admin)
  const DeploymentView = () => {
    const [deploymentConfig, setDeploymentConfig] = useState({
      domain: '',
      sslEnabled: true,
      environment: 'production',
      backupEnabled: true
    });
    const [deploymentStatus, setDeploymentStatus] = useState('idle');
    const [deploymentLogs, setDeploymentLogs] = useState([]);

    const handleDomainMigration = async () => {
      if (!deploymentConfig.domain) {
        addNotification('Please enter a domain name', 'error');
        return;
      }
      
      setDeploymentStatus('deploying');
      setDeploymentLogs(['Starting domain migration...', `Configuring domain: ${deploymentConfig.domain}`]);
      
      // Simulate deployment process
      setTimeout(() => {
        setDeploymentLogs(prev => [...prev, 'DNS configuration updated', 'SSL certificate generated', 'Nginx configuration updated']);
        setTimeout(() => {
          setDeploymentStatus('success');
          setDeploymentLogs(prev => [...prev, 'Deployment completed successfully!']);
          addNotification(`Successfully deployed to ${deploymentConfig.domain}`, 'success');
        }, 2000);
      }, 1500);
    };

    return (
      <div className="p-8">
        <div className="bg-gradient-to-br from-beyond-black via-beyond-charcoal to-beyond-dark text-white rounded-2xl p-8 mb-8">
          <h1 className="text-3xl font-display font-bold mb-4">System Deployment</h1>
          <p className="text-gray-300 text-lg">Production deployment and domain migration tools</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Domain Migration */}
          <div className="card-elegant p-6">
            <div className="flex items-center mb-4">
              <Globe className="text-beyond-black mr-3" size={24} />
              <h2 className="text-xl font-semibold">Domain Migration</h2>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-beyond-black mb-2">Domain Name</label>
                <input
                  type="text"
                  value={deploymentConfig.domain}
                  onChange={(e) => setDeploymentConfig(prev => ({ ...prev, domain: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-beyond-black focus:border-transparent"
                  placeholder="yourdomain.com"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="sslEnabled"
                    checked={deploymentConfig.sslEnabled}
                    onChange={(e) => setDeploymentConfig(prev => ({ ...prev, sslEnabled: e.target.checked }))}
                    className="mr-2"
                  />
                  <label htmlFor="sslEnabled" className="text-sm">Enable SSL</label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="backupEnabled"
                    checked={deploymentConfig.backupEnabled}
                    onChange={(e) => setDeploymentConfig(prev => ({ ...prev, backupEnabled: e.target.checked }))}
                    className="mr-2"
                  />
                  <label htmlFor="backupEnabled" className="text-sm">Auto Backup</label>
                </div>
              </div>
              
              <button
                onClick={handleDomainMigration}
                disabled={deploymentStatus === 'deploying'}
                className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                  deploymentStatus === 'deploying' 
                    ? 'bg-gray-400 text-white cursor-not-allowed' 
                    : 'bg-beyond-black text-white hover:bg-beyond-charcoal'
                }`}
              >
                {deploymentStatus === 'deploying' ? (
                  <div className="flex items-center justify-center">
                    <RefreshCw className="animate-spin mr-2" size={16} />
                    Migrating...
                  </div>
                ) : 'Start Migration'}
              </button>
            </div>
          </div>

          {/* Deployment Status */}
          <div className="card-elegant p-6">
            <div className="flex items-center mb-4">
              <Monitor className="text-beyond-black mr-3" size={24} />
              <h2 className="text-xl font-semibold">Deployment Status</h2>
            </div>
            
            <div className="space-y-4">
              <div className={`p-3 rounded-lg ${
                deploymentStatus === 'success' ? 'bg-green-100 text-green-800' :
                deploymentStatus === 'deploying' ? 'bg-blue-100 text-blue-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                <div className="flex items-center">
                  {deploymentStatus === 'success' && <CheckCircle size={16} className="mr-2" />}
                  {deploymentStatus === 'deploying' && <RefreshCw size={16} className="mr-2 animate-spin" />}
                  {deploymentStatus === 'idle' && <Clock size={16} className="mr-2" />}
                  <span className="font-medium">
                    {deploymentStatus === 'success' ? 'Deployment Successful' :
                     deploymentStatus === 'deploying' ? 'Deploying...' : 
                     'Ready for Deployment'}
                  </span>
                </div>
              </div>
              
              {deploymentLogs.length > 0 && (
                <div className="bg-gray-900 text-green-400 p-4 rounded-lg max-h-48 overflow-y-auto font-mono text-sm">
                  {deploymentLogs.map((log, index) => (
                    <div key={index} className="mb-1">
                      <span className="text-gray-500">[{new Date().toLocaleTimeString()}]</span> {log}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
          <div className="card-elegant p-4 text-center">
            <Download className="mx-auto text-beyond-black mb-2" size={24} />
            <h3 className="font-medium mb-2">Deployment Guide</h3>
            <p className="text-sm text-gray-600 mb-3">Complete production deployment instructions</p>
            <button
              onClick={() => window.open('/DEPLOYMENT.md', '_blank')}
              className="text-beyond-black hover:text-beyond-charcoal font-medium"
            >
              View Guide
            </button>
          </div>
          
          <div className="card-elegant p-4 text-center">
            <Database className="mx-auto text-beyond-black mb-2" size={24} />
            <h3 className="font-medium mb-2">Database Backup</h3>
            <p className="text-sm text-gray-600 mb-3">Create production database backup</p>
            <button
              onClick={() => addNotification('Database backup initiated', 'success')}
              className="text-beyond-black hover:text-beyond-charcoal font-medium"
            >
              Backup Now
            </button>
          </div>
          
          <div className="card-elegant p-4 text-center">
            <Settings className="mx-auto text-beyond-black mb-2" size={24} />
            <h3 className="font-medium mb-2">System Health</h3>
            <p className="text-sm text-gray-600 mb-3">Check application and server status</p>
            <button
              onClick={() => window.open('/api/health', '_blank')}
              className="text-beyond-black hover:text-beyond-charcoal font-medium"
            >
              Health Check
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Performance View Component (for clients)
  const PerformanceView = () => {
    const [performanceTimeframe, setPerformanceTimeframe] = useState('YTD');
    const [selectedMetric, setSelectedMetric] = useState('returns');

    // Portfolio performance data - will be loaded from backend
    const [performanceData, setPerformanceData] = useState({
      portfolio: { totalValue: 0, netReturns: {}, benchmark: {} },
      allocations: [],
      metrics: {},
      recentTransactions: []
    });
    const [performanceLoading, setPerformanceLoading] = useState(false);

    return (
      <div className="space-y-8">
        {/* Header */}
        <div className="bg-gradient-to-br from-beyond-black via-beyond-charcoal to-beyond-dark text-white rounded-2xl p-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl"></div>
          <div className="relative z-10">
            <h1 className="text-3xl font-display font-bold mb-4">Portfolio Performance</h1>
            <p className="text-gray-300 text-lg mb-6">Your investment performance and portfolio analytics</p>
            <div className="grid md:grid-cols-3 gap-6 mt-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-400">
                  ${performanceData.portfolio.totalValue.toLocaleString()}
                </div>
                <div className="text-sm text-gray-300">Total Portfolio Value</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-400">
                  +{performanceData.portfolio.netReturns[performanceTimeframe]}%
                </div>
                <div className="text-sm text-gray-300">Net Returns ({performanceTimeframe})</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-400">
                  +{(performanceData.portfolio.netReturns[performanceTimeframe] - performanceData.portfolio.benchmark[performanceTimeframe]).toFixed(1)}%
                </div>
                <div className="text-sm text-gray-300">vs Benchmark</div>
              </div>
            </div>
          </div>
        </div>

        {/* Performance Controls */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
            {['YTD', '1Y', '3Y', '5Y'].map(period => (
              <button
                key={period}
                onClick={() => setPerformanceTimeframe(period)}
                className={`px-4 py-2 rounded-lg transition-all ${
                  performanceTimeframe === period
                    ? 'bg-white text-beyond-black shadow-sm'
                    : 'text-gray-600 hover:text-beyond-black'
                }`}
              >
                {period}
              </button>
            ))}
          </div>
          <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
            {[
              { id: 'returns', label: 'Returns' },
              { id: 'allocation', label: 'Allocation' },
              { id: 'metrics', label: 'Risk Metrics' }
            ].map(metric => (
              <button
                key={metric.id}
                onClick={() => setSelectedMetric(metric.id)}
                className={`px-4 py-2 rounded-lg transition-all ${
                  selectedMetric === metric.id
                    ? 'bg-white text-beyond-black shadow-sm'
                    : 'text-gray-600 hover:text-beyond-black'
                }`}
              >
                {metric.label}
              </button>
            ))}
          </div>
        </div>

        {/* Performance Content */}
        {selectedMetric === 'returns' && (
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Performance Chart Placeholder */}
            <div className="card-elegant p-6">
              <h3 className="text-xl font-bold mb-4 text-beyond-black">Performance History</h3>
              <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <LineChart className="mx-auto text-gray-400 mb-2" size={48} />
                  <p className="text-gray-600">Performance chart will be displayed here</p>
                </div>
              </div>
            </div>

            {/* Returns Summary */}
            <div className="card-elegant p-6">
              <h3 className="text-xl font-bold mb-4 text-beyond-black">Returns Summary</h3>
              <div className="space-y-4">
                {Object.entries(performanceData.portfolio.netReturns).map(([period, returns]) => (
                  <div key={period} className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="font-medium text-gray-700">{period}</span>
                    <div className="text-right">
                      <div className="font-semibold text-green-600">+{returns}%</div>
                      <div className="text-xs text-gray-500">
                        vs {performanceData.portfolio.benchmark[period]}% benchmark
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {selectedMetric === 'allocation' && (
          <div className="grid lg:grid-cols-3 gap-6">
            {performanceData.allocations.map(allocation => (
              <div key={allocation.asset} className="card-elegant p-6">
                <div className="flex justify-between items-start mb-4">
                  <h4 className="font-semibold text-beyond-black">{allocation.asset}</h4>
                  <span className="text-2xl font-bold text-beyond-black">{allocation.allocation}%</span>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Value:</span>
                    <span className="font-medium">${allocation.value.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Returns:</span>
                    <span className={`font-medium ${allocation.returns > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {allocation.returns > 0 ? '+' : ''}{allocation.returns}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
                    <div 
                      className="bg-beyond-black h-2 rounded-full transition-all duration-500"
                      style={{ width: `${allocation.allocation * 2}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {selectedMetric === 'metrics' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Object.entries(performanceData.metrics).map(([metric, value]) => (
              <div key={metric} className="card-elegant p-6 text-center">
                <div className="text-2xl font-bold text-beyond-black mb-2">
                  {typeof value === 'number' ? (value > 0 ? '+' : '') + value.toFixed(2) : value}
                  {metric === 'maxDrawdown' || metric === 'volatility' ? '%' : ''}
                </div>
                <div className="text-sm text-gray-600 capitalize">
                  {metric.replace(/([A-Z])/g, ' $1').trim()}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Recent Activity */}
        <div className="card-elegant p-6">
          <h3 className="text-xl font-bold mb-4 text-beyond-black">Recent Investment Activity</h3>
          <div className="space-y-4">
            {performanceData.recentTransactions.map((transaction, index) => (
              <div key={index} className="flex justify-between items-center py-3 border-b border-gray-100">
                <div>
                  <div className="font-medium text-beyond-black">{transaction.fund}</div>
                  <div className="text-sm text-gray-600">{new Date(transaction.date).toLocaleDateString()}</div>
                </div>
                <div className="text-right">
                  <div className={`font-semibold ${
                    transaction.type === 'Investment' ? 'text-blue-600' : 'text-green-600'
                  }`}>
                    {transaction.type}
                  </div>
                  <div className="text-sm text-gray-700">
                    ${Math.abs(transaction.amount).toLocaleString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Download Reports Section */}
        <div className="card-elegant p-6">
          <h3 className="text-xl font-bold mb-4 text-beyond-black">Performance Reports</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <button className="flex items-center justify-center space-x-2 p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors">
              <Download size={20} className="text-gray-600" />
              <span className="text-gray-800">Quarterly Report</span>
            </button>
            <button className="flex items-center justify-center space-x-2 p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors">
              <Download size={20} className="text-gray-600" />
              <span className="text-gray-800">Tax Documents</span>
            </button>
            <button className="flex items-center justify-center space-x-2 p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors">
              <Download size={20} className="text-gray-600" />
              <span className="text-gray-800">Investment Summary</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Settings View Component
  const SettingsView = () => {
    const [settingsTab, setSettingsTab] = useState('profile');
    const [apiKeys, setApiKeys] = useState([
      { id: 1, service: 'OpenAI', key: 'sk-proj-****abcd', connected: true, lastUsed: '2025-01-08' },
      { id: 2, service: 'Bloomberg API', key: 'bapi-****efgh', connected: true, lastUsed: '2025-01-07' },
      { id: 3, service: 'Yahoo Finance', key: 'yf-****ijkl', connected: false, lastUsed: null },
      { id: 4, service: 'Alpha Vantage', key: '', connected: false, lastUsed: null }
    ]);

    const [userProfile, setUserProfile] = useState({
      firstName: currentUser?.name?.split(' ')[0] || '',
      lastName: currentUser?.name?.split(' ')[1] || '',
      email: currentUser?.email || '',
      phone: '',
      timezone: 'America/New_York',
      notifications: {
        email: true,
        desktop: true,
        mobile: false
      }
    });

    const [systemSettings, setSystemSettings] = useState({
      autoLogout: 60,
      sessionTimeout: 30,
      twoFactorAuth: false,
      dataRetention: 365,
      backupFrequency: 'daily'
    });

    const settingsTabs = [
      { id: 'profile', label: 'Profile & Account', icon: Users },
      { id: 'integrations', label: 'API & Integrations', icon: Database },
      { id: 'notifications', label: 'Notifications', icon: Bell },
      { id: 'security', label: 'Security', icon: Shield },
      ...(userRole === 'admin' ? [{ id: 'system', label: 'System Preferences', icon: Settings }] : [])
    ];

    const updateApiKey = (serviceId, newKey) => {
      setApiKeys(prev => prev.map(api => 
        api.id === serviceId 
          ? { ...api, key: newKey, connected: newKey.length > 0 }
          : api
      ));
      addNotification(`${apiKeys.find(a => a.id === serviceId)?.service} API key updated`, 'success');
    };

    const testConnection = async (serviceId) => {
      const service = apiKeys.find(a => a.id === serviceId);
      addNotification(`Testing connection to ${service?.service}...`, 'info');
      
      // Simulate API test
      setTimeout(() => {
        setApiKeys(prev => prev.map(api => 
          api.id === serviceId 
            ? { ...api, connected: true, lastUsed: new Date().toISOString().split('T')[0] }
            : api
        ));
        addNotification(`${service?.service} connection successful!`, 'success');
      }, 2000);
    };

    return (
      <div className="space-y-8">
        {/* Header */}
        <div className="bg-gradient-to-br from-beyond-black via-beyond-charcoal to-beyond-dark text-white rounded-2xl p-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl"></div>
          <div className="relative z-10">
            <h1 className="text-3xl font-display font-bold mb-4">Settings & Configuration</h1>
            <p className="text-gray-300 text-lg">Manage your account, integrations, and system preferences</p>
            <div className="flex items-center mt-6 space-x-6">
              <div className="flex items-center space-x-2">
                <Key className="text-blue-400" size={20} />
                <span className="text-sm">{apiKeys.filter(k => k.connected).length} API Connections</span>
              </div>
              <div className="flex items-center space-x-2">
                <Shield className="text-green-400" size={20} />
                <span className="text-sm">Security Active</span>
              </div>
            </div>
          </div>
        </div>

        {/* Settings Navigation */}
        <div className="flex space-x-1 bg-gray-100 rounded-lg p-1 overflow-x-auto">
          {settingsTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setSettingsTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-3 rounded-lg transition-all whitespace-nowrap ${
                settingsTab === tab.id
                  ? 'bg-white text-beyond-black shadow-sm'
                  : 'text-gray-600 hover:text-beyond-black'
              }`}
            >
              <tab.icon size={16} />
              <span className="font-medium">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Profile & Account Settings */}
        {settingsTab === 'profile' && (
          <div className="space-y-6">
            <div className="card-elegant p-6">
              <h3 className="text-xl font-bold mb-4 text-beyond-black">Profile Information</h3>
              <div className="grid lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="form-group">
                    <label className="form-label">First Name</label>
                    <input
                      type="text"
                      value={userProfile.firstName}
                      onChange={(e) => setUserProfile(prev => ({ ...prev, firstName: e.target.value }))}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Last Name</label>
                    <input
                      type="text"
                      value={userProfile.lastName}
                      onChange={(e) => setUserProfile(prev => ({ ...prev, lastName: e.target.value }))}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email Address</label>
                    <input
                      type="email"
                      value={userProfile.email}
                      onChange={(e) => setUserProfile(prev => ({ ...prev, email: e.target.value }))}
                      className="form-input"
                    />
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="form-group">
                    <label className="form-label">Phone Number</label>
                    <input
                      type="tel"
                      value={userProfile.phone}
                      onChange={(e) => setUserProfile(prev => ({ ...prev, phone: e.target.value }))}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Timezone</label>
                    <select
                      value={userProfile.timezone}
                      onChange={(e) => setUserProfile(prev => ({ ...prev, timezone: e.target.value }))}
                      className="form-select"
                    >
                      <option value="America/New_York">Eastern Time</option>
                      <option value="America/Chicago">Central Time</option>
                      <option value="America/Denver">Mountain Time</option>
                      <option value="America/Los_Angeles">Pacific Time</option>
                      <option value="UTC">UTC</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Role</label>
                    <input
                      type="text"
                      value={currentUser?.role || 'User'}
                      disabled
                      className="form-input bg-gray-50"
                    />
                  </div>
                </div>
              </div>
              <div className="flex justify-end mt-6">
                <button className="btn-primary">Update Profile</button>
              </div>
            </div>

            <div className="card-elegant p-6">
              <h3 className="text-xl font-bold mb-4 text-beyond-black">Account Actions</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <button className="flex items-center justify-center space-x-2 p-4 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors">
                  <Key size={20} className="text-blue-600" />
                  <span className="text-blue-800">Change Password</span>
                </button>
                <button className="flex items-center justify-center space-x-2 p-4 bg-green-50 hover:bg-green-100 rounded-lg transition-colors">
                  <Download size={20} className="text-green-600" />
                  <span className="text-green-800">Export Data</span>
                </button>
                <button className="flex items-center justify-center space-x-2 p-4 bg-red-50 hover:bg-red-100 rounded-lg transition-colors">
                  <Trash2 size={20} className="text-red-600" />
                  <span className="text-red-800">Delete Account</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* API & Integrations */}
        {settingsTab === 'integrations' && (
          <div className="space-y-6">
            <div className="card-elegant p-6">
              <h3 className="text-xl font-bold mb-4 text-beyond-black">API Keys & Integrations</h3>
              <div className="space-y-4">
                {apiKeys.map(apiKey => (
                  <div key={apiKey.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          apiKey.connected ? 'bg-green-100' : 'bg-gray-100'
                        }`}>
                          <Database size={20} className={
                            apiKey.connected ? 'text-green-600' : 'text-gray-600'
                          } />
                        </div>
                        <div>
                          <h4 className="font-semibold text-beyond-black">{apiKey.service}</h4>
                          <p className="text-sm text-gray-600">
                            {apiKey.connected ? `Last used: ${apiKey.lastUsed}` : 'Not connected'}
                          </p>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        apiKey.connected 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {apiKey.connected ? 'Connected' : 'Disconnected'}
                      </span>
                    </div>
                    <div className="flex space-x-4">
                      <input
                        type="password"
                        placeholder="Enter API key..."
                        value={apiKey.key}
                        onChange={(e) => updateApiKey(apiKey.id, e.target.value)}
                        className="flex-1 form-input"
                      />
                      <button
                        onClick={() => testConnection(apiKey.id)}
                        disabled={!apiKey.key}
                        className="btn-secondary disabled:opacity-50"
                      >
                        Test Connection
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="card-elegant p-6">
              <h3 className="text-xl font-bold mb-4 text-beyond-black">Third-Party Integrations</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 border border-gray-200 rounded-lg">
                  <h4 className="font-semibold text-beyond-black mb-2">Slack Notifications</h4>
                  <p className="text-sm text-gray-600 mb-3">Get portal notifications in Slack</p>
                  <button className="btn-primary text-sm">Connect Slack</button>
                </div>
                <div className="p-4 border border-gray-200 rounded-lg">
                  <h4 className="font-semibold text-beyond-black mb-2">Calendar Integration</h4>
                  <p className="text-sm text-gray-600 mb-3">Sync meetings with Google/Outlook</p>
                  <button className="btn-primary text-sm">Connect Calendar</button>
                </div>
                <div className="p-4 border border-gray-200 rounded-lg">
                  <h4 className="font-semibold text-beyond-black mb-2">DocuSign Integration</h4>
                  <p className="text-sm text-gray-600 mb-3">Electronic document signing</p>
                  <button className="btn-primary text-sm">Connect DocuSign</button>
                </div>
                <div className="p-4 border border-gray-200 rounded-lg">
                  <h4 className="font-semibold text-beyond-black mb-2">Salesforce CRM</h4>
                  <p className="text-sm text-gray-600 mb-3">Sync with existing CRM data</p>
                  <button className="btn-primary text-sm">Connect Salesforce</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Security Settings */}
        {settingsTab === 'security' && (
          <div className="space-y-6">
            <div className="card-elegant p-6">
              <h3 className="text-xl font-bold mb-4 text-beyond-black">Security Preferences</h3>
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-beyond-black">Two-Factor Authentication</h4>
                    <p className="text-sm text-gray-600">Add an extra layer of security to your account</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={systemSettings.twoFactorAuth}
                      onChange={(e) => setSystemSettings(prev => ({ ...prev, twoFactorAuth: e.target.checked }))}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="form-label">Auto Logout (minutes)</label>
                    <select
                      value={systemSettings.autoLogout}
                      onChange={(e) => setSystemSettings(prev => ({ ...prev, autoLogout: parseInt(e.target.value) }))}
                      className="form-select"
                    >
                      <option value={15}>15 minutes</option>
                      <option value={30}>30 minutes</option>
                      <option value={60}>1 hour</option>
                      <option value={120}>2 hours</option>
                      <option value={480}>8 hours</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="form-label">Session Timeout (minutes)</label>
                    <select
                      value={systemSettings.sessionTimeout}
                      onChange={(e) => setSystemSettings(prev => ({ ...prev, sessionTimeout: parseInt(e.target.value) }))}
                      className="form-select"
                    >
                      <option value={15}>15 minutes</option>
                      <option value={30}>30 minutes</option>
                      <option value={60}>1 hour</option>
                      <option value={120}>2 hours</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="card-elegant p-6">
              <h3 className="text-xl font-bold mb-4 text-beyond-black">Login History</h3>
              <div className="space-y-3">
                {[
                  { date: '2025-01-08', location: 'New York, NY', device: 'Chrome on Windows', status: 'Current' },
                  { date: '2025-01-07', location: 'New York, NY', device: 'Safari on iPhone', status: 'Success' },
                  { date: '2025-01-06', location: 'New York, NY', device: 'Chrome on Windows', status: 'Success' }
                ].map((login, index) => (
                  <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100">
                    <div>
                      <div className="font-medium text-beyond-black">{login.device}</div>
                      <div className="text-sm text-gray-600">{login.location} • {login.date}</div>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      login.status === 'Current' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                    }`}>
                      {login.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* System Preferences - Admin Only */}
        {settingsTab === 'system' && userRole === 'admin' && (
          <div className="space-y-6">
            <div className="card-elegant p-6">
              <h3 className="text-xl font-bold mb-4 text-beyond-black">System Configuration</h3>
              <div className="space-y-6">
                <div>
                  <label className="form-label">Data Retention Period (days)</label>
                  <select
                    value={systemSettings.dataRetention}
                    onChange={(e) => setSystemSettings(prev => ({ ...prev, dataRetention: parseInt(e.target.value) }))}
                    className="form-select"
                  >
                    <option value={90}>90 days</option>
                    <option value={180}>180 days</option>
                    <option value={365}>1 year</option>
                    <option value={1095}>3 years</option>
                    <option value={1825}>5 years</option>
                  </select>
                </div>
                
                <div>
                  <label className="form-label">Backup Frequency</label>
                  <select
                    value={systemSettings.backupFrequency}
                    onChange={(e) => setSystemSettings(prev => ({ ...prev, backupFrequency: e.target.value }))}
                    className="form-select"
                  >
                    <option value="hourly">Hourly</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="card-elegant p-6">
              <h3 className="text-xl font-bold mb-4 text-beyond-black">Advanced Options</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <button className="flex items-center justify-center space-x-2 p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors">
                  <RefreshCw size={20} className="text-gray-600" />
                  <span className="text-gray-800">Clear Cache</span>
                </button>
                <button className="flex items-center justify-center space-x-2 p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors">
                  <Database size={20} className="text-gray-600" />
                  <span className="text-gray-800">Export Database</span>
                </button>
                <button className="flex items-center justify-center space-x-2 p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors">
                  <Upload size={20} className="text-gray-600" />
                  <span className="text-gray-800">Import Data</span>
                </button>
                <button className="flex items-center justify-center space-x-2 p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors">
                  <Shield size={20} className="text-gray-600" />
                  <span className="text-gray-800">Security Audit</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Notifications */}
        {settingsTab === 'notifications' && (
          <div className="card-elegant p-6">
            <h3 className="text-xl font-bold mb-4 text-beyond-black">Notification Preferences</h3>
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-beyond-black">Email Notifications</h4>
                  <p className="text-sm text-gray-600">Receive notifications via email</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={userProfile.notifications.email}
                    onChange={(e) => setUserProfile(prev => ({
                      ...prev,
                      notifications: { ...prev.notifications, email: e.target.checked }
                    }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-beyond-black">Desktop Notifications</h4>
                  <p className="text-sm text-gray-600">Show desktop push notifications</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={userProfile.notifications.desktop}
                    onChange={(e) => setUserProfile(prev => ({
                      ...prev,
                      notifications: { ...prev.notifications, desktop: e.target.checked }
                    }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Note: Advanced AnalyticsView component is defined earlier in the file

  // Login Modal Component
  const LoginModal = React.memo(() => {
    const [isLoggingIn, setIsLoggingIn] = useState(false);

    const handleSubmit = useCallback(async (e) => {
      e.preventDefault();
      if (isLoggingIn) return;
      
      setIsLoggingIn(true);
      try {
        await handleLogin();
      } finally {
        setIsLoggingIn(false);
      }
    }, [isLoggingIn, handleLogin]);

    const handleKeyPress = useCallback((e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit(e);
      }
    }, [handleSubmit]);

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4 animate-slide-up">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-display font-bold text-beyond-black">
              {loginPortalType === 'employee' ? 'Employee Portal Login' : 'Client Portal Login'}
            </h2>
            <button 
              onClick={() => setShowLogin(false)}
              className="text-beyond-gray-600 hover:text-beyond-black"
            >
              <X size={24} />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-beyond-black mb-2">Email</label>
              <input
                type="email"
                value={loginForm.email}
                onChange={handleEmailChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-beyond-black focus:ring-2 focus:ring-beyond-black/20 focus:outline-none transition-colors"
                placeholder="admin@b26.com"
                required
                autoComplete="email"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-beyond-black mb-2">Password</label>
              <input 
                type="password"
                value={loginForm.password}
                onChange={handlePasswordChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-beyond-black focus:ring-2 focus:ring-beyond-black/20 focus:outline-none transition-colors"
                placeholder="Password123!"
                required
                autoComplete="current-password"
              />
            </div>
            
            <button 
              type="submit"
              disabled={isLoggingIn || !loginForm.email || !loginForm.password}
              className="w-full bg-beyond-black text-white py-4 px-8 rounded-full font-medium text-lg hover:bg-beyond-charcoal transition-all duration-300 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoggingIn ? 'Logging in...' : 'Access Portal'}
            </button>
          </form>
      
          <div className="mt-6 text-center text-sm text-beyond-gray-600">
            <strong>Demo credentials:</strong><br/>
            Email: admin@b26.com<br/>
            Password: Password123!
          </div>
        </div>
      </div>
    );
  });

  // Footer Component
  const Footer = () => (
    <footer className="bg-gradient-to-b from-black via-gray-900 to-black text-white py-16 border-t border-gray-800">
      <div className="max-w-[90%] mx-auto px-8">
        <div className="grid lg:grid-cols-2 gap-16 mb-12">
          {/* Company Info - Left Side */}
          <div className="text-left">
            <h3 className="text-4xl font-bold mb-4 tracking-[0.15em]" style={{ fontFamily: 'Georgia, serif', textShadow: '0 2px 12px rgba(255,255,255,0.1)' }}>Beyond<span className="italic font-thin">26</span></h3>
            <p className="text-gray-400 font-light leading-relaxed text-base max-w-md" style={{ fontFamily: 'Georgia, serif' }}>
              Strategic investment advisory for institutional investors and asset managers
            </p>
          </div>

          {/* Contact Information - Right Side */}
          <div className="text-left">
            <h4 className="font-bold mb-6 text-2xl tracking-wide" style={{ fontFamily: 'Georgia, serif' }}>Contact</h4>
            <div className="space-y-5">
              <div>
                <p className="font-medium text-white text-lg mb-1" style={{ fontFamily: 'Georgia, serif' }}>Edgar Smith</p>
                <p className="text-gray-400 font-light text-sm mb-1" style={{ fontFamily: 'Georgia, serif' }}>Managing Partner</p>
                <a href="mailto:esmith@beyond26advisors.com" className="text-gray-300 font-light text-sm hover:text-white transition-colors" style={{ fontFamily: 'Georgia, serif' }}>esmith@beyond26advisors.com</a>
              </div>
              <div>
                <p className="font-medium text-white text-lg mb-1" style={{ fontFamily: 'Georgia, serif' }}>Alex Smith</p>
                <p className="text-gray-400 font-light text-sm mb-1" style={{ fontFamily: 'Georgia, serif' }}>Partner</p>
                <a href="mailto:asmith@beyond26advisors.com" className="text-gray-300 font-light text-sm hover:text-white transition-colors" style={{ fontFamily: 'Georgia, serif' }}>asmith@beyond26advisors.com</a>
              </div>
            </div>
          </div>
        </div>
          
          <div className="border-t border-beyond-gray-700 mt-16 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-beyond-gray-400 text-lg font-light mb-4 md:mb-0">
                &copy; 2025 Beyond26 Investment Advisors. All rights reserved.
              </p>
              <div className="flex space-x-8">
                <a href="#" className="text-beyond-gray-400 hover:text-white transition-colors">Privacy Policy</a>
                <a href="#" className="text-beyond-gray-400 hover:text-white transition-colors">Terms of Service</a>
                <a href="#" className="text-beyond-gray-400 hover:text-white transition-colors">Disclaimer</a>
              </div>
            </div>
          </div>
        </div>
    </footer>
  );

  // Project Modal Component
  const ProjectModal = () => {
    const [projectForm, setProjectForm] = useState({
      title: '',
      client: '',
      status: 'Active',
      deadline: '',
      type: 'Due Diligence',
      team: [],
      progress: 0
    });
    
    const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const validateForm = () => {
      const errors: {[key: string]: string} = {};
      if (!projectForm.title.trim()) errors.title = 'Project title is required';
      if (!projectForm.client.trim()) errors.client = 'Client name is required';
      if (projectForm.deadline && new Date(projectForm.deadline) < new Date()) {
        errors.deadline = 'Deadline cannot be in the past';
      }
      setFormErrors(errors);
      return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!validateForm()) {
        addNotification('Please fix the form errors before submitting', 'error');
        return;
      }
      
      setIsSubmitting(true);
      try {
        await createProject({
          ...projectForm,
          tasks: [],
          lastUpdate: new Date().toISOString()
        });
        setShowProjectModal(false);
        setProjectForm({
          title: '',
          client: '',
          status: 'Active',
          deadline: '',
          type: 'Due Diligence',
          team: [],
          progress: 0
        });
        setFormErrors({});
      } catch (error) {
        // Error already handled in createProject function
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-beyond-black">Create New Project</h2>
            <button
              onClick={() => setShowProjectModal(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Project Title
              </label>
              <input
                type="text"
                value={projectForm.title}
                onChange={(e) => {
                  setProjectForm(prev => ({ ...prev, title: e.target.value }));
                  if (formErrors.title) setFormErrors(prev => ({ ...prev, title: '' }));
                }}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-beyond-black focus:border-transparent ${
                  formErrors.title ? 'border-red-500' : 'border-gray-300'
                }`}
                required
              />
              {formErrors.title && (
                <p className="mt-1 text-sm text-red-600">{formErrors.title}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client
              </label>
              <input
                type="text"
                value={projectForm.client}
                onChange={(e) => {
                  setProjectForm(prev => ({ ...prev, client: e.target.value }));
                  if (formErrors.client) setFormErrors(prev => ({ ...prev, client: '' }));
                }}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-beyond-black focus:border-transparent ${
                  formErrors.client ? 'border-red-500' : 'border-gray-300'
                }`}
                required
              />
              {formErrors.client && (
                <p className="mt-1 text-sm text-red-600">{formErrors.client}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type
                </label>
                <select
                  value={projectForm.type}
                  onChange={(e) => setProjectForm(prev => ({ ...prev, type: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-beyond-black focus:border-transparent"
                >
                  <option value="Due Diligence">Due Diligence</option>
                  <option value="Asset Allocation">Asset Allocation</option>
                  <option value="Strategic Advisory">Strategic Advisory</option>
                  <option value="Investment Analysis">Investment Analysis</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  value={projectForm.status}
                  onChange={(e) => setProjectForm(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-beyond-black focus:border-transparent"
                >
                  <option value="Active">Active</option>
                  <option value="Planning">Planning</option>
                  <option value="On Hold">On Hold</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Deadline
              </label>
              <input
                type="date"
                value={projectForm.deadline}
                onChange={(e) => {
                  setProjectForm(prev => ({ ...prev, deadline: e.target.value }));
                  if (formErrors.deadline) setFormErrors(prev => ({ ...prev, deadline: '' }));
                }}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-beyond-black focus:border-transparent ${
                  formErrors.deadline ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {formErrors.deadline && (
                <p className="mt-1 text-sm text-red-600">{formErrors.deadline}</p>
              )}
            </div>

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => setShowProjectModal(false)}
                className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`px-6 py-3 rounded-lg text-white transition-colors ${
                  isSubmitting 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-beyond-black hover:bg-beyond-charcoal'
                }`}
              >
                {isSubmitting ? 'Creating...' : 'Create Project'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Message Compose Modal Component
  const ComposeModal = () => {
    const [messageForm, setMessageForm] = useState({
      subject: '',
      body: '',
      recipient: ''
    });

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        await sendMessage(messageForm);
        setShowComposeModal(false);
        setMessageForm({ subject: '', body: '', recipient: '' });
      } catch (error) {
        // Error already handled in sendMessage function
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-beyond-black">Compose Message</h2>
            <button
              onClick={() => setShowComposeModal(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Recipient
              </label>
              <input
                type="email"
                value={messageForm.recipient}
                onChange={(e) => setMessageForm(prev => ({ ...prev, recipient: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-beyond-black focus:border-transparent"
                placeholder="recipient@example.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject
              </label>
              <input
                type="text"
                value={messageForm.subject}
                onChange={(e) => setMessageForm(prev => ({ ...prev, subject: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-beyond-black focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message
              </label>
              <textarea
                value={messageForm.body}
                onChange={(e) => setMessageForm(prev => ({ ...prev, body: e.target.value }))}
                rows={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-beyond-black focus:border-transparent"
                required
              />
            </div>

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => setShowComposeModal(false)}
                className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-3 bg-beyond-black text-white rounded-lg hover:bg-beyond-charcoal transition-colors"
              >
                Send Message
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Calendar Scheduling Modal
  // Simple Meeting Request Modal - Isolated component with own state
  const MeetingRequestModal = React.memo(() => {
    const [meetingForm, setMeetingForm] = useState({
      name: '',
      email: '',
      phone: '',
      suggestedTime1: '',
      suggestedTime2: '',
      suggestedTime3: '',
      notes: ''
    });

    const handleSubmit = async () => {
      if (!selectedAdvisor || !meetingForm.name || !meetingForm.email || !meetingForm.suggestedTime1) {
        alert('Please fill in all required fields (Name, Email, and at least one suggested time)');
        return;
      }

      const advisorEmail = selectedAdvisor === 'edgar' ? 'esmith@beyond26advisors.com' : 'asmith@beyond26advisors.com';
      const advisorName = selectedAdvisor === 'edgar' ? 'Edgar Smith' : 'Alex Smith';

      const meetingData = {
        ...meetingForm,
        advisor: advisorName,
        advisorEmail: advisorEmail
      };

      try {
        await api.submitContact({
          name: meetingForm.name,
          email: meetingForm.email,
          phone: meetingForm.phone,
          comments: `Meeting Request with ${advisorName}\n\nSuggested Times:\n1. ${meetingForm.suggestedTime1}\n2. ${meetingForm.suggestedTime2 || 'N/A'}\n3. ${meetingForm.suggestedTime3 || 'N/A'}\n\nNotes:\n${meetingForm.notes || 'No additional notes'}`
        });

        setShowCalendarModal(false);
        setMeetingForm({ name: '', email: '', phone: '', suggestedTime1: '', suggestedTime2: '', suggestedTime3: '', notes: '' });
        setSelectedAdvisor('edgar');
        alert('Meeting request sent successfully! We\'ll review your suggested times and send you a calendar invite.');
      } catch (error) {
        console.error('Meeting request error:', error);
        alert('Failed to send meeting request. Please try again or contact us directly.');
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-3xl font-bold text-beyond-black" style={{ fontFamily: 'Georgia, serif' }}>
              Request a <span className="italic font-thin">Meeting</span>
            </h2>
            <button
              onClick={() => setShowCalendarModal(false)}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <X size={28} />
            </button>
          </div>

          <div className="space-y-6">
            {/* Select Advisor */}
            <div>
              <label className="block text-lg font-medium text-gray-700 mb-4">Select Advisor</label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setSelectedAdvisor('edgar')}
                  className={`p-6 border-2 rounded-xl transition-all duration-300 ${
                    selectedAdvisor === 'edgar'
                      ? 'border-beyond-black bg-gray-50 shadow-lg'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="text-center">
                    <h3 className="font-bold text-xl mb-1">Edgar Smith</h3>
                    <p className="text-gray-600 text-sm">Managing Partner</p>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedAdvisor('alex')}
                  className={`p-6 border-2 rounded-xl transition-all duration-300 ${
                    selectedAdvisor === 'alex'
                      ? 'border-beyond-black bg-gray-50 shadow-lg'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="text-center">
                    <h3 className="font-bold text-xl mb-1">Alex Smith</h3>
                    <p className="text-gray-600 text-sm">Partner</p>
                  </div>
                </button>
              </div>
            </div>

            {/* Your Information */}
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Your Information</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                    <input
                      type="text"
                      value={meetingForm.name}
                      onChange={(e) => setMeetingForm(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-beyond-black focus:ring-2 focus:ring-beyond-black/20 outline-none transition-colors"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                    <input
                      type="email"
                      value={meetingForm.email}
                      onChange={(e) => setMeetingForm(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-beyond-black focus:ring-2 focus:ring-beyond-black/20 outline-none transition-colors"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone (optional)</label>
                  <input
                    type="tel"
                    value={meetingForm.phone}
                    onChange={(e) => setMeetingForm(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-beyond-black focus:ring-2 focus:ring-beyond-black/20 outline-none transition-colors"
                  />
                </div>
              </div>
            </div>

            {/* Suggested Times */}
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Suggest 3 Meeting Times</h3>
              <p className="text-sm text-gray-600 mb-4">Please provide 3 date/time options that work for you. We'll send you a calendar invite for one of these times.</p>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Option 1 * (e.g., "Monday, Oct 14 at 2:00 PM PST")</label>
                  <input
                    type="text"
                    value={meetingForm.suggestedTime1}
                    onChange={(e) => setMeetingForm(prev => ({ ...prev, suggestedTime1: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-beyond-black focus:ring-2 focus:ring-beyond-black/20 outline-none transition-colors"
                    placeholder="e.g., Monday, Oct 14 at 2:00 PM PST"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Option 2 (optional)</label>
                  <input
                    type="text"
                    value={meetingForm.suggestedTime2}
                    onChange={(e) => setMeetingForm(prev => ({ ...prev, suggestedTime2: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-beyond-black focus:ring-2 focus:ring-beyond-black/20 outline-none transition-colors"
                    placeholder="e.g., Tuesday, Oct 15 at 10:00 AM PST"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Option 3 (optional)</label>
                  <input
                    type="text"
                    value={meetingForm.suggestedTime3}
                    onChange={(e) => setMeetingForm(prev => ({ ...prev, suggestedTime3: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-beyond-black focus:ring-2 focus:ring-beyond-black/20 outline-none transition-colors"
                    placeholder="e.g., Wednesday, Oct 16 at 3:00 PM PST"
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Additional Notes (optional)</label>
              <textarea
                rows={3}
                value={meetingForm.notes}
                onChange={(e) => setMeetingForm(prev => ({ ...prev, notes: e.target.value }))}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-beyond-black focus:ring-2 focus:ring-beyond-black/20 outline-none resize-none transition-colors"
                placeholder="Any specific topics or questions you'd like to discuss..."
              />
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
              <button
                onClick={() => setShowCalendarModal(false)}
                className="px-6 py-3 border-2 border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="px-8 py-3 bg-beyond-black text-white rounded-lg hover:bg-beyond-charcoal transition-colors font-medium"
              >
                Submit Request
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  });

  const CalendarSchedulingModal = () => {
    const generateTimeSlots = () => {
      const slots = [];
      for (let hour = 8; hour < 16; hour++) {
        const period = hour < 12 ? 'AM' : 'PM';
        const displayHour = hour > 12 ? hour - 12 : hour;
        slots.push({ value: `${hour}:00`, display: `${displayHour}:00 ${period}` });
        slots.push({ value: `${hour}:30`, display: `${displayHour}:30 ${period}` });
      }
      return slots;
    };

    const generateAvailableDates = () => {
      const dates = [];
      const today = new Date();
      for (let i = 1; i <= 14; i++) {
        const date = new Date(today);
        date.setDate(today.getDate() + i);
        if (date.getDay() !== 0 && date.getDay() !== 6) { // Exclude weekends
          dates.push(date);
        }
      }
      return dates;
    };

    const handleScheduleMeeting = () => {
      if (!selectedDate || !selectedTime || !schedulingForm.name || !schedulingForm.email) {
        addNotification('Please fill in all required fields', 'error');
        return;
      }

      const advisorEmail = selectedAdvisor === 'edgar' ? 'esmith@beyond26advisors.com' : 'asmith@beyond26advisors.com';
      const advisorName = selectedAdvisor === 'edgar' ? 'Edgar Smith' : 'Alex Smith';

      const meetingDate = new Date(selectedDate);
      const [hours, minutes] = selectedTime.split(':');
      meetingDate.setHours(parseInt(hours), parseInt(minutes));

      const endTime = new Date(meetingDate);
      endTime.setMinutes(endTime.getMinutes() + 30);

      const formatDateTime = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}${month}${day}T${hours}${minutes}00`;
      };

      const subject = `Meeting with ${advisorName} - Beyond26 Advisors`;
      const body = `Meeting Request Details:\n\nDate: ${meetingDate.toLocaleDateString()}\nTime: ${selectedTime} PST (30 minutes)\n\nClient Information:\nName: ${schedulingForm.name}\nEmail: ${schedulingForm.email}\nPhone: ${schedulingForm.phone || 'Not provided'}\n\nNotes:\n${schedulingForm.notes || 'No additional notes'}\n\n---\nThis meeting request was submitted via Beyond26 Advisors website.`;

      // Create calendar event URL
      const startDateTime = formatDateTime(meetingDate);
      const endDateTime = formatDateTime(endTime);

      if (emailClient === 'outlook') {
        const outlookUrl = `https://outlook.office.com/calendar/deeplink/compose?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}&startdt=${startDateTime}&enddt=${endDateTime}&to=${encodeURIComponent(advisorEmail)}&path=/calendar/action/compose&rru=addevent`;
        window.open(outlookUrl, '_blank');
      } else {
        const googleCalUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(subject)}&details=${encodeURIComponent(body)}&dates=${startDateTime}/${endDateTime}&add=${encodeURIComponent(advisorEmail)}`;
        window.open(googleCalUrl, '_blank');
      }

      // Also send email notification
      sendEmail(advisorEmail, subject, body);

      setShowCalendarModal(false);
      setSchedulingForm({ name: '', email: '', phone: '', notes: '' });
      setSelectedDate(null);
      setSelectedTime(null);
      addNotification('Meeting request sent! A calendar invite has been created.', 'success');
    };

    const availableDates = generateAvailableDates();
    const timeSlots = generateTimeSlots();

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-3xl font-bold text-beyond-black" style={{ fontFamily: 'Georgia, serif' }}>
              Schedule a <span className="italic font-thin">Meeting</span>
            </h2>
            <button
              onClick={() => setShowCalendarModal(false)}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <X size={28} />
            </button>
          </div>

          <div className="space-y-8">
            {/* Select Advisor */}
            <div>
              <label className="block text-lg font-medium text-gray-700 mb-4">Select Advisor</label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setSelectedAdvisor('edgar')}
                  className={`p-6 border-2 rounded-xl transition-all duration-300 ${
                    selectedAdvisor === 'edgar'
                      ? 'border-beyond-black bg-gray-50 shadow-lg'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="text-center">
                    <h3 className="font-bold text-xl mb-1">Edgar Smith</h3>
                    <p className="text-gray-600 text-sm">Managing Partner</p>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedAdvisor('alex')}
                  className={`p-6 border-2 rounded-xl transition-all duration-300 ${
                    selectedAdvisor === 'alex'
                      ? 'border-beyond-black bg-gray-50 shadow-lg'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="text-center">
                    <h3 className="font-bold text-xl mb-1">Alex Smith</h3>
                    <p className="text-gray-600 text-sm">Partner</p>
                  </div>
                </button>
              </div>
            </div>

            {/* Email Client Selection */}
            <div>
              <label className="block text-lg font-medium text-gray-700 mb-4">Preferred Calendar</label>
              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => setEmailClient('outlook')}
                  className={`flex items-center space-x-3 px-8 py-4 rounded-xl font-medium transition-all duration-300 ${
                    emailClient === 'outlook'
                      ? 'bg-blue-600 text-white shadow-lg scale-105'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border-2 border-gray-300'
                  }`}
                >
                  <Calendar className="w-6 h-6" />
                  <span>Outlook</span>
                </button>
                <button
                  type="button"
                  onClick={() => setEmailClient('gmail')}
                  className={`flex items-center space-x-3 px-8 py-4 rounded-xl font-medium transition-all duration-300 ${
                    emailClient === 'gmail'
                      ? 'bg-red-600 text-white shadow-lg scale-105'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border-2 border-gray-300'
                  }`}
                >
                  <Calendar className="w-6 h-6" />
                  <span>Google Calendar</span>
                </button>
              </div>
            </div>

            {/* Select Date */}
            <div>
              <label className="block text-lg font-medium text-gray-700 mb-4">Select Date</label>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                {availableDates.map((date) => (
                  <button
                    key={date.toISOString()}
                    type="button"
                    onClick={() => setSelectedDate(date)}
                    className={`p-4 border-2 rounded-lg transition-all duration-300 ${
                      selectedDate?.toDateString() === date.toDateString()
                        ? 'border-beyond-black bg-gray-50 shadow-lg'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <div className="text-center">
                      <div className="text-sm font-medium text-gray-600">
                        {date.toLocaleDateString('en-US', { weekday: 'short' })}
                      </div>
                      <div className="text-xl font-bold text-gray-900">
                        {date.getDate()}
                      </div>
                      <div className="text-xs text-gray-500">
                        {date.toLocaleDateString('en-US', { month: 'short' })}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Select Time */}
            {selectedDate && (
              <div>
                <label className="block text-lg font-medium text-gray-700 mb-4">
                  Select Time (PST - 30 min meeting)
                </label>
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
                  {timeSlots.map((time) => (
                    <button
                      key={time.value}
                      type="button"
                      onClick={() => setSelectedTime(time.value)}
                      className={`p-3 border-2 rounded-lg font-medium transition-all duration-300 ${
                        selectedTime === time.value
                          ? 'border-beyond-black bg-gray-50 shadow-lg'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      {time.display}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Contact Information */}
            {selectedTime && (
              <SchedulingFormFields schedulingForm={schedulingForm} setSchedulingForm={setSchedulingForm} />
            )}

            {/* Submit Button */}
            {selectedTime && schedulingForm.name && schedulingForm.email && (
              <div className="flex justify-end space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCalendarModal(false)}
                  className="px-8 py-4 border-2 border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleScheduleMeeting}
                  className="px-8 py-4 bg-gradient-to-br from-black via-gray-900 to-gray-800 text-white rounded-lg hover:from-gray-900 hover:via-black hover:to-gray-900 transition-all duration-500 shadow-lg font-medium"
                >
                  Schedule Meeting
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Main Render
  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {!isLoggedIn ? (
        <>
          {activeTab === 'home' && <HomePage />}
          {activeTab === 'about' && <AboutPage />}
          {activeTab === 'contact' && <ContactPage />}
          <Footer />
        </>
      ) : (
        <Dashboard />
      )}

      {/* Login Modal */}
      {showLogin && <LoginModal />}

      {/* Project Modal */}
      {showProjectModal && <ProjectModal />}

      {/* Message Compose Modal */}
      {showComposeModal && <ComposeModal />}

      {/* Meeting Request Modal */}
      {showCalendarModal && <MeetingRequestModal />}
    </div>
  );
};

export default Beyond26System;
